
// ════════════════════════════════════════════════════════════════════════
// SURPLIQ — Complete B2B Industrial Surplus Marketplace
// Firebase + Claude AI | Production Build
// ════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from "react";

// ════════════════════════════════════════════════════════════════════════
// ██████  PASTE YOUR KEYS BELOW — ONLY THESE 2 LINES NEED CHANGING  ██████
// ════════════════════════════════════════════════════════════════════════

const CLAUDE_API_KEY = "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";   // ← CHANGE THIS (sk-ant-api03-...)

const FB = {
  apiKey:    "AIzaSyASiIyeDWuaWimhN8aRtsi8QePdBny53N4",            // ← CHANGE THIS (AIzaSy...)
  projectId: "surpliq-2a734",                    // ← already set, do not change
};

// ════════════════════════════════════════════════════════════════════════
// Do not edit below this line
// ════════════════════════════════════════════════════════════════════════
const DEMO_MODE = FB.apiKey === "YOUR_FIREBASE_API_KEY";

// ── FIREBASE REST API LAYER ──────────────────────────────────────────────
const FS_BASE  = `https://firestore.googleapis.com/v1/projects/${FB.projectId}/databases/(default)/documents`;
const AUTH_URL = `https://identitytoolkit.googleapis.com/v1/accounts`;

// Firestore value → JS
const fVal = (v) => {
  if (!v) return null;
  if (v.stringValue   !== undefined) return v.stringValue;
  if (v.integerValue  !== undefined) return Number(v.integerValue);
  if (v.doubleValue   !== undefined) return v.doubleValue;
  if (v.booleanValue  !== undefined) return v.booleanValue;
  if (v.timestampValue!== undefined) return v.timestampValue;
  if (v.nullValue     !== undefined) return null;
  if (v.arrayValue    !== undefined) return (v.arrayValue.values||[]).map(fVal);
  if (v.mapValue      !== undefined) return fDoc({ fields: v.mapValue.fields, name:"" });
  return null;
};
// Firestore doc → JS object
const fDoc = (doc) => {
  if (!doc?.fields) return null;
  const o = { _id: doc.name?.split("/").pop() || "" };
  Object.keys(doc.fields).forEach(k => { o[k] = fVal(doc.fields[k]); });
  return o;
};
// JS → Firestore value
const toFVal = (v) => {
  if (v === null || v === undefined) return { nullValue: null };
  if (typeof v === "string")  return { stringValue: v };
  if (typeof v === "boolean") return { booleanValue: v };
  if (typeof v === "number")  return Number.isInteger(v) ? { integerValue: String(v) } : { doubleValue: v };
  if (Array.isArray(v))       return { arrayValue: { values: v.map(toFVal) } };
  if (typeof v === "object")  return { mapValue: { fields: Object.fromEntries(Object.entries(v).map(([k,x])=>[k,toFVal(x)])) } };
  return { stringValue: String(v) };
};
// JS object → Firestore fields
const toFFields = (obj) => ({ fields: Object.fromEntries(Object.entries(obj).filter(([,v])=>v!==undefined).map(([k,v])=>[k,toFVal(v)])) });

let _idToken = null;
const authHeader = () => _idToken ? { "Authorization": `Bearer ${_idToken}` } : {};

// ── FIRESTORE CRUD ───────────────────────────────────────────────────────
const DB = {
  async get(col, id) {
    if (DEMO_MODE) return demoGet(col, id);
    const r = await fetch(`${FS_BASE}/${col}/${id}?key=${FB.apiKey}`, { headers: authHeader() });
    const d = await r.json();
    return r.ok ? fDoc(d) : null;
  },
  async list(col, filters = []) {
    if (DEMO_MODE) return demoList(col, filters);
    const body = { structuredQuery: { from:[{collectionId:col}] } };
    if (filters.length) body.structuredQuery.where = filters.length === 1
      ? { fieldFilter: { field:{fieldPath:filters[0][0]}, op: filters[0][1]==="=="?"EQUAL":"ARRAY_CONTAINS", value: toFVal(filters[0][2]) }}
      : { compositeFilter: { op:"AND", filters: filters.map(([f,op,v])=>({ fieldFilter:{ field:{fieldPath:f}, op:op==="=="?"EQUAL":op==="array-contains"?"ARRAY_CONTAINS":"EQUAL", value:toFVal(v) }})) }};
    const r = await fetch(`${FS_BASE}:runQuery?key=${FB.apiKey}`, { method:"POST", headers:{"Content-Type":"application/json",...authHeader()}, body:JSON.stringify(body) });
    const arr = await r.json();
    return Array.isArray(arr) ? arr.map(x=>x.document ? fDoc(x.document) : null).filter(Boolean) : [];
  },
  async set(col, id, data) {
    if (DEMO_MODE) return demoSet(col, id, data);
    const url = `${FS_BASE}/${col}/${id}?key=${FB.apiKey}`;
    const r = await fetch(url, { method:"PATCH", headers:{"Content-Type":"application/json",...authHeader()}, body:JSON.stringify(toFFields(data)) });
    const d = await r.json(); return fDoc(d);
  },
  async add(col, data) {
    if (DEMO_MODE) return demoAdd(col, data);
    const r = await fetch(`${FS_BASE}/${col}?key=${FB.apiKey}`, { method:"POST", headers:{"Content-Type":"application/json",...authHeader()}, body:JSON.stringify(toFFields(data)) });
    const d = await r.json(); return fDoc(d);
  },
  async update(col, id, data) {
    if (DEMO_MODE) return demoUpdate(col, id, data);
    const existing = await DB.get(col, id);
    return DB.set(col, id, { ...(existing||{}), ...data });
  },
  async delete(col, id) {
    if (DEMO_MODE) return demoDelete(col, id);
    await fetch(`${FS_BASE}/${col}/${id}?key=${FB.apiKey}`, { method:"DELETE", headers: authHeader() });
  },
};

// ── DEMO STORAGE (localStorage fallback) ────────────────────────────────
const lsKey = (c) => `surpliq_${c}`;
const lsGet  = (c) => { try { return JSON.parse(localStorage.getItem(lsKey(c))||"[]"); } catch { return []; } };
const lsSave = (c, arr) => localStorage.setItem(lsKey(c), JSON.stringify(arr));
const uid    = () => Math.random().toString(36).slice(2,10)+Date.now().toString(36);

function demoGet(col, id)         { return lsGet(col).find(x=>x._id===id)||null; }
function demoList(col, filters)   { let d=lsGet(col); filters.forEach(([f,,v])=>{ d=d.filter(x=>Array.isArray(x[f])?x[f].includes(v):x[f]===v); }); return d; }
function demoSet(col, id, data)   { const arr=lsGet(col); const i=arr.findIndex(x=>x._id===id); const o={...data,_id:id}; i>=0?arr[i]=o:arr.push(o); lsSave(col,arr); return o; }
function demoAdd(col, data)       { const id=uid(); return demoSet(col,id,{...data,_id:id}); }
function demoUpdate(col, id, data){ const e=demoGet(col,id)||{}; return demoSet(col,id,{...e,...data}); }
function demoDelete(col, id)      { lsSave(col,lsGet(col).filter(x=>x._id!==id)); }

// ── FIREBASE AUTH ────────────────────────────────────────────────────────
const Auth = {
  async register(email, password, profile) {
    if (DEMO_MODE) {
      const existing = demoList("users",[]).find(u=>u.email===email);
      if (existing) throw new Error("Email already registered");
      const user = demoAdd("users", { email, ...profile, createdAt: new Date().toISOString() });
      localStorage.setItem("surpliq_session", JSON.stringify(user));
      return user;
    }
    const r = await fetch(`${AUTH_URL}:signUp?key=${FB.apiKey}`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email, password, returnSecureToken:true }) });
    const d = await r.json();
    if (d.error) throw new Error(d.error.message);
    _idToken = d.idToken;
    const user = await DB.set("users", d.localId, { email, ...profile, uid:d.localId, createdAt:new Date().toISOString() });
    localStorage.setItem("surpliq_session", JSON.stringify({...user, _token:d.idToken}));
    return user;
  },
  async login(email, password) {
    if (DEMO_MODE) {
      const user = demoList("users",[]).find(u=>u.email===email);
      if (!user) throw new Error("No account found with this email");
      localStorage.setItem("surpliq_session", JSON.stringify(user));
      return user;
    }
    const r = await fetch(`${AUTH_URL}:signInWithPassword?key=${FB.apiKey}`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email, password, returnSecureToken:true }) });
    const d = await r.json();
    if (d.error) throw new Error(d.error.message);
    _idToken = d.idToken;
    const user = await DB.get("users", d.localId);
    const u = {...(user||{}), email, _token:d.idToken};
    localStorage.setItem("surpliq_session", JSON.stringify(u));
    return u;
  },
  logout() { localStorage.removeItem("surpliq_session"); _idToken = null; },
  getSession() { try { return JSON.parse(localStorage.getItem("surpliq_session")||"null"); } catch { return null; } },
};

// ── AI HELPER ────────────────────────────────────────────────────────────
async function callAI(system, user, fallback) {
  // Check key is set
  if (!CLAUDE_API_KEY || CLAUDE_API_KEY === "YOUR_CLAUDE_API_KEY") {
    console.warn("Claude API key not set — using fallback");
    return typeof fallback === "string" ? fallback : JSON.stringify(fallback);
  }
  try {
    const r = await fetch("https://api.anthropic.com/v1/messages", {
      method:"POST",
      headers:{
        "Content-Type":"application/json",
        "x-api-key": CLAUDE_API_KEY,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body:JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:2000, system, messages:[{role:"user",content:user}] })
    });
    const d = await r.json();
    // Surface API errors visibly
    if (d.error) {
      console.error("Claude API error:", d.error);
      throw new Error(d.error.message || "API error");
    }
    const text = d.content?.[0]?.text || "";
    const clean = text.replace(/```json|```/g,"").trim();
    return clean || (typeof fallback === "string" ? fallback : JSON.stringify(fallback));
  } catch(e) {
    console.error("callAI failed:", e.message);
    return typeof fallback === "string" ? fallback : JSON.stringify(fallback);
  }
}

// ── SEED DEMO DATA ───────────────────────────────────────────────────────
function seedDemoData() {
  if (lsGet("listings").length > 0) return;
  const sellerId = "demo-seller-1";
  const buyerId  = "demo-buyer-1";
  demoSet("users", sellerId, { _id:sellerId, email:"seller@hmcgroup.in", name:"Pratap Sahoo", role:"seller", company:"HMC Group", gst:"03AABHM1234B1ZX", city:"Ludhiana", state:"Punjab", phone:"+91 98760 00001", kycStatus:"approved" });
  demoSet("users", buyerId,  { _id:buyerId,  email:"buyer@narayan.com",  name:"Raj Narayan",   role:"buyer",  company:"Narayan Steel Traders", gst:"03AABNS5678C1ZY", city:"Ludhiana", state:"Punjab", phone:"+91 98760 00002", kycStatus:"approved" });
  demoSet("users", "admin-1",{ _id:"admin-1", email:"admin@surpliq.com", name:"Surpliq Admin", role:"admin", company:"Surpliq Technologies", kycStatus:"approved" });

  const listings = [
    { sellerId, title:"HR Steel Coil IS2062 — 22.7 MT Lot", category:"RM", icon:"🔩", qty:"22.7", unit:"MT", location:"Ludhiana, Punjab", price:"37500", priceUnit:"/MT", bookValue:"951500", ageMonths:"26", condition:"Used — Good", status:"active", description:"Prime quality HR Steel Coil 2.5mm and 3.0mm IS2062 Grade A/B stored under cover. Full mill test certificates available. Suitable for fabrication, re-rolling, or secondary use.", scrapFloor:"₹26,000/MT", liquidationRange:"₹34,000–40,000/MT", resaleMax:"₹47,000/MT", tags:["IS2062","HR Steel","MS Coil"], views:142, enquiryCount:4, createdAt:new Date(Date.now()-8*86400000).toISOString() },
    { sellerId, title:"Aluminium Extrusion 6063-T5 Profile", category:"RM", icon:"🏗️", qty:"3200", unit:"KG", location:"Ludhiana, Punjab", price:"70", priceUnit:"/kg", bookValue:"224000", ageMonths:"19", condition:"Like New", status:"active", description:"6063-T5 aluminium extrusion profiles in standard sections. Stored indoors, no surface damage. Original packaging intact. Ideal for fabricators.", scrapFloor:"₹58/kg", liquidationRange:"₹65–75/kg", resaleMax:"₹82/kg", tags:["Aluminium","6063-T5"], views:68, enquiryCount:2, createdAt:new Date(Date.now()-6*86400000).toISOString() },
    { sellerId, title:"ABB 7.5kW IE3 Motor + Danfoss VFD Bundle", category:"CE", icon:"⚡", qty:"4+2", unit:"NOS", location:"Ludhiana, Punjab", price:"110000", priceUnit:"", bookValue:"260000", ageMonths:"40", condition:"Used — Tested", status:"active", description:"ABB 7.5kW IE3 4-pole motors (4 nos) and Danfoss FC302 11kW VFD drives (2 nos) from decommissioned line. Tested and working, full nameplate data available.", scrapFloor:"₹80,000", liquidationRange:"₹1,00,000–1,20,000", resaleMax:"₹1,40,000", tags:["ABB Motor","VFD","Danfoss"], views:96, enquiryCount:3, createdAt:new Date(Date.now()-5*86400000).toISOString() },
    { sellerId, title:"SKF + FAG Bearing Mixed Lot — 600 pcs", category:"BOP", icon:"⚙️", qty:"600", unit:"PCS", location:"Pune, Maharashtra", price:"280", priceUnit:"/pc", bookValue:"168000", ageMonths:"21", condition:"New — Boxed", status:"active", description:"SKF 6205 ZZ (480 pcs) and FAG NU308 (120 pcs) bearings in original packaging. All boxes sealed, within shelf life. Full traceability.", scrapFloor:"₹180/pc", liquidationRange:"₹240–300/pc", resaleMax:"₹340/pc", tags:["SKF","FAG","Bearings"], views:201, enquiryCount:7, createdAt:new Date(Date.now()-4*86400000).toISOString() },
    { sellerId, title:"Shell Omala S2 G 320 Gear Oil — 24 Drums", category:"MRO", icon:"🛢️", qty:"24", unit:"DRM", location:"Ahmedabad, Gujarat", price:"2400", priceUnit:"/drm", bookValue:"57600", ageMonths:"11", condition:"Sealed — Unused", status:"active", description:"Shell Omala S2 G 320 gear oil, 20L drums sealed and within expiry. Ideal for industrial gearboxes. Stored in climate-controlled warehouse.", scrapFloor:"₹1,800/drm", liquidationRange:"₹2,200–2,600/drm", resaleMax:"₹3,000/drm", tags:["Shell","Lubricant","Gear Oil"], views:38, enquiryCount:0, createdAt:new Date(Date.now()-2*86400000).toISOString() },
  ];
  listings.forEach(l => demoAdd("listings", {...l}));

  // Seed one enquiry thread
  const listingId = lsGet("listings")[0]?._id;
  if (listingId) {
    demoAdd("enquiries", {
      listingId, buyerId, sellerId, listingTitle:"HR Steel Coil IS2062 — 22.7 MT Lot",
      buyerName:"Raj Narayan", buyerCompany:"Narayan Steel Traders", buyerCity:"Ludhiana",
      buyerRating:4.2, status:"negotiating", createdAt:new Date(Date.now()-2*3600000).toISOString(),
      thread:[
        { from:"buyer", time:new Date(Date.now()-2*3600000).toISOString(), text:"Interested in the HR steel lot. Can you share mill test certificates? Is split lot possible — need 10 MT only." },
        { from:"seller", time:new Date(Date.now()-90*60000).toISOString(), text:"MTCs available for both coils. Minimum lot is 15 MT, no split below that. Inspection welcome anytime." },
        { from:"buyer", time:new Date(Date.now()-60*60000).toISOString(), isQuote:true, quote:{ price:"₹34,000/MT", qty:"15 MT", total:"₹5,10,000", validity:"3 days", status:"pending", label:"Buyer Offer" }},
        { from:"seller", time:new Date(Date.now()-45*60000).toISOString(), text:"₹34,000 is below our floor. Best we can do is ₹36,500/MT for 15 MT — quick pickup." },
        { from:"seller", time:new Date(Date.now()-44*60000).toISOString(), isQuote:true, quote:{ price:"₹36,500/MT", qty:"15 MT", total:"₹5,47,500", validity:"24 hrs", status:"counter", label:"Seller Counter" }},
        { from:"buyer", time:new Date(Date.now()-30*60000).toISOString(), isQuote:true, quote:{ price:"₹35,800/MT", qty:"15 MT", total:"₹5,37,000", validity:"48 hrs", status:"pending", label:"Buyer Revised Offer" }},
      ]
    });
  }
}

// ── STYLES ───────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Plus+Jakarta+Sans:wght@600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --navy:#0A1628;--n2:#1E3A6E;--n3:#2C5282;
  --gold:#C8A84B;--g2:#E8C96A;
  --white:#fff;--off:#F4F6FA;--border:#DDE5F0;--muted:#64748B;
  --green:#16A34A;--gbg:rgba(22,163,74,.1);
  --red:#DC2626;--rbg:rgba(220,38,38,.08);
  --amber:#D97706;--abg:rgba(217,119,6,.09);
  --blue:#2563EB;--bbg:rgba(37,99,235,.08);
  --fh:'Plus Jakarta Sans',sans-serif;--fb:'Inter',sans-serif;
}
html{scroll-behavior:smooth;}
body{font-family:var(--fb);background:var(--off);color:var(--navy);-webkit-font-smoothing:antialiased;}
/* TOPBAR */
.bar{background:var(--navy);height:52px;display:flex;align-items:center;justify-content:space-between;padding:0 16px;position:sticky;top:0;z-index:300;box-shadow:0 2px 8px rgba(0,0,0,.2);}
.logo{font-family:var(--fh);font-size:18px;font-weight:800;color:#fff;display:flex;align-items:center;gap:9px;cursor:pointer;text-decoration:none;letter-spacing:-0.5px;}
.lm{width:28px;height:28px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.logo-surp{color:#C8A84B;}
.logo-liq{color:#ffffff;}
.demo-badge{background:rgba(200,168,75,.2);border:1px solid rgba(200,168,75,.35);color:var(--gold);font-size:10px;font-weight:700;padding:2px 8px;border-radius:6px;letter-spacing:.5px;}
/* LAYOUT */
.layout{display:flex;min-height:calc(100vh - 52px);}
.sidebar{width:210px;background:var(--white);border-right:1.5px solid var(--border);padding:12px 10px;flex-shrink:0;position:sticky;top:52px;height:calc(100vh - 52px);overflow-y:auto;}
.sb-sec{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin:14px 0 6px 8px;}
.sb-item{display:flex;align-items:center;gap:9px;padding:8px 10px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;color:var(--muted);transition:all .18s;margin-bottom:2px;}
.sb-item:hover{background:var(--off);color:var(--navy);}
.sb-item.act{background:rgba(10,22,40,.07);color:var(--navy);font-weight:700;}
.sb-badge{margin-left:auto;font-size:10px;font-weight:700;padding:1px 6px;border-radius:10px;}
.sbb-r{background:var(--red);color:#fff;}
.sbb-g{background:var(--gbg);color:var(--green);}
.sbb-a{background:var(--abg);color:var(--amber);}
.main{flex:1;padding:22px 18px;overflow:auto;}
/* TYPOGRAPHY */
.eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:5px;}
.h1{font-family:var(--fh);font-size:22px;font-weight:800;color:var(--navy);margin-bottom:4px;}
.h2{font-family:var(--fh);font-size:17px;font-weight:800;color:var(--navy);margin-bottom:4px;}
.h3{font-family:var(--fh);font-size:14px;font-weight:800;color:var(--navy);margin-bottom:3px;}
.sub{font-size:13px;color:var(--muted);line-height:1.5;}
/* CARDS */
.card{background:var(--white);border:1.5px solid var(--border);border-radius:14px;padding:18px;}
/* BUTTONS */
.btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:9px 16px;border-radius:9px;font-family:var(--fb);font-size:13px;font-weight:600;cursor:pointer;border:none;transition:all .18s;outline:none;}
.btn-gold{background:var(--gold);color:var(--navy);}
.btn-gold:hover{background:var(--g2);transform:translateY(-1px);}
.btn-navy{background:var(--navy);color:#fff;}
.btn-navy:hover{background:var(--n2);}
.btn-out{background:transparent;color:var(--navy);border:1.5px solid var(--border);}
.btn-out:hover{border-color:var(--navy);}
.btn-green{background:var(--gbg);color:var(--green);border:1px solid rgba(22,163,74,.2);}
.btn-red{background:var(--rbg);color:var(--red);border:1px solid rgba(220,38,38,.2);}
.btn-blue{background:var(--bbg);color:var(--blue);border:1px solid rgba(37,99,235,.2);}
.btn-sm{padding:6px 12px;font-size:12px;border-radius:7px;}
.btn-lg{padding:12px 24px;font-size:15px;border-radius:10px;}
.btn-full{width:100%;justify-content:center;}
.btn:disabled{opacity:.4;cursor:not-allowed;transform:none!important;}
/* FORMS */
.fgrp{margin-bottom:14px;}
.flabel{display:block;font-size:12px;font-weight:600;color:var(--navy);margin-bottom:5px;}
.flabel span{color:var(--red);}
.finput{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:9px;font-family:var(--fb);font-size:13px;color:var(--navy);outline:none;background:var(--white);transition:border-color .2s;}
.finput:focus{border-color:var(--n2);box-shadow:0 0 0 3px rgba(44,82,130,.08);}
.fselect{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:9px;font-family:var(--fb);font-size:13px;color:var(--navy);outline:none;background:var(--white);appearance:none;cursor:pointer;}
.ftextarea{width:100%;padding:10px 13px;border:1.5px solid var(--border);border-radius:9px;font-family:var(--fb);font-size:13px;outline:none;resize:vertical;min-height:80px;background:var(--white);line-height:1.6;}
.ftextarea:focus{border-color:var(--n2);}
.frow{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.ferr{font-size:12px;color:var(--red);margin-top:5px;}
/* PILLS */
.pill{padding:3px 10px;border-radius:8px;font-size:11px;font-weight:700;}
.pill-green{background:var(--gbg);color:var(--green);}
.pill-amber{background:var(--abg);color:var(--amber);}
.pill-red{background:var(--rbg);color:var(--red);}
.pill-blue{background:var(--bbg);color:var(--blue);}
.pill-navy{background:rgba(10,22,40,.08);color:var(--navy);}
/* CAT BADGES */
.cat-badge{padding:2px 8px;border-radius:5px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;}
/* AI BOX */
.ai-box{background:linear-gradient(135deg,#0A1628,#1B3A5C);border-radius:12px;padding:16px 18px;margin:12px 0;}
.ai-lbl{background:rgba(200,168,75,.2);border:1px solid rgba(200,168,75,.3);color:var(--gold);font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:2px 8px;border-radius:6px;display:inline-block;margin-bottom:8px;}
.ai-dots{display:flex;gap:5px;padding:4px 0;}
.ald{width:6px;height:6px;border-radius:50%;background:var(--gold);}
.ald:nth-child(1){animation:ald .9s infinite;}
.ald:nth-child(2){animation:ald .9s .2s infinite;}
.ald:nth-child(3){animation:ald .9s .4s infinite;}
@keyframes ald{0%,100%{opacity:.2;transform:scale(.7)}50%{opacity:1;transform:scale(1)}}
/* TABLE */
.tbl{width:100%;border-collapse:collapse;font-size:13px;}
.tbl th{background:var(--off);padding:9px 12px;text-align:left;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--muted);border-bottom:1.5px solid var(--border);}
.tbl td{padding:10px 12px;border-bottom:1px solid var(--border);vertical-align:middle;}
.tbl tr:last-child td{border-bottom:none;}
.tbl tr:hover td{background:rgba(10,22,40,.015);}
/* KPI GRID */
.kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px;}
.kpi{background:var(--white);border:1.5px solid var(--border);border-radius:12px;padding:14px 16px;}
.kpi-n{font-family:var(--fh);font-size:22px;font-weight:800;line-height:1;}
.kpi-l{font-size:11px;color:var(--muted);margin-top:3px;}
.kpi-d{font-size:11px;font-weight:600;margin-top:5px;}
/* LISTING CARD */
.lst-card{background:var(--white);border:1.5px solid var(--border);border-radius:14px;padding:16px;cursor:pointer;transition:all .2s;}
.lst-card:hover{box-shadow:0 6px 24px rgba(10,22,40,.09);transform:translateY(-2px);}
/* PRICE BANDS */
.pb-row{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;}
.pb{background:var(--off);border-radius:8px;padding:10px 8px;text-align:center;border:1px solid var(--border);}
.pb.hi{background:rgba(200,168,75,.1);border-color:rgba(200,168,75,.3);}
.pb-l{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px;}
.pb-v{font-family:var(--fh);font-size:13px;font-weight:800;}
.pb.hi .pb-v{color:var(--gold);}
/* CHAT */
.msg{max-width:85%;padding:11px 14px;border-radius:12px;font-size:13px;line-height:1.65;}
.msg.buyer{background:var(--off);border:1px solid var(--border);align-self:flex-start;border-radius:4px 12px 12px 12px;}
.msg.seller{background:var(--navy);color:rgba(255,255,255,.85);align-self:flex-end;border-radius:12px 4px 12px 12px;}
.quote-card{background:var(--white);border:1.5px solid var(--border);border-radius:12px;padding:13px;max-width:85%;}
/* ANIMATIONS */
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.fu{animation:fadeUp .3s ease forwards;}
@keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
/* RESPONSIVE */
@media(max-width:700px){
  .sidebar{width:100%;height:auto;position:static;border-right:none;border-bottom:1.5px solid var(--border);padding:6px 8px;display:flex;flex-direction:row;gap:4px;overflow-x:auto;flex-shrink:0;}
  .sb-sec{display:none;}
  .sb-item{flex-shrink:0;padding:7px 12px;border-radius:20px;font-size:11px;white-space:nowrap;}
  .sb-item.act{background:var(--navy)!important;color:#fff!important;}
  .kpi-grid{grid-template-columns:1fr 1fr;}
  .frow{grid-template-columns:1fr;}
  .pb-row{grid-template-columns:1fr 1fr;}
  .layout{flex-direction:column;}
  .main{padding:14px 12px;}
  .card{padding:14px;}
  .h1{font-size:18px;}
  .h2{font-size:15px;}
  .bar{padding:0 12px;}
  .tbl th,.tbl td{padding:7px 8px;font-size:11px;}
}
@media(max-width:480px){
  .kpi-grid{grid-template-columns:1fr 1fr;}
  .pb-row{grid-template-columns:1fr;}
  .btn-lg{padding:11px 18px;font-size:14px;}
}
`;

// ── CAT COLORS ───────────────────────────────────────────────────────────
const CC = {
  RM:  { bg:"#FEE2E2", tx:"#B91C1C", border:"#FECACA" },
  BOP: { bg:"#DBEAFE", tx:"#1D4ED8", border:"#BFDBFE" },
  MRO: { bg:"#DCFCE7", tx:"#15803D", border:"#BBF7D0" },
  CE:  { bg:"#EDE9FE", tx:"#7C3AED", border:"#DDD6FE" },
  FG:  { bg:"#FEF9C3", tx:"#A16207", border:"#FDE68A" },
};

// ── SHARED COMPONENTS ────────────────────────────────────────────────────
const AiBox = ({ title, log, loading, children }) => (
  <div className="ai-box">
    <span className="ai-lbl">AI</span>
    <div style={{ fontFamily:"var(--fh)", fontSize:13, fontWeight:800, color:"#fff", marginBottom:8 }}>{title}</div>
    {log?.map((l,i) => <div key={i} style={{ fontSize:12, color:"rgba(255,255,255,.65)", marginBottom:4 }}>{l}</div>)}
    {loading && <div className="ai-dots"><div className="ald"/><div className="ald"/><div className="ald"/></div>}
    {children}
  </div>
);

const SmartTip = ({ text }) => (
  <div style={{ padding:"9px 13px", background:"rgba(200,168,75,.08)", border:"1px solid rgba(200,168,75,.2)", borderRadius:8, fontSize:12, color:"var(--amber)", lineHeight:1.5, marginTop:8 }}>
    💡 {text}
  </div>
);

const CatBadge = ({ cat }) => {
  const c = CC[cat]||CC.MRO;
  return <span className="cat-badge" style={{ background:c.bg, color:c.tx, border:`1px solid ${c.border}` }}>{cat}</span>;
};

const SpecRow = ({ label, value }) => (
  <div style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--border)", fontSize:13 }}>
    <span style={{ color:"var(--muted)" }}>{label}</span>
    <span style={{ fontWeight:600 }}>{value}</span>
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
    <div style={{ background:"#fff", borderRadius:18, padding:24, width:"100%", maxWidth:520, maxHeight:"90vh", overflowY:"auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div className="h2">{title}</div>
        <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"var(--muted)" }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Toast = ({ msg, type }) => msg ? (
  <div style={{ position:"fixed", bottom:24, right:24, background: type==="error"?"var(--red)":type==="warn"?"var(--amber)":"var(--navy)", color:"#fff", padding:"12px 20px", borderRadius:12, fontSize:13, fontWeight:600, zIndex:999, boxShadow:"0 8px 24px rgba(0,0,0,.2)", maxWidth:320 }}>
    {type==="error"?"❌":type==="warn"?"⚠️":"✅"} {msg}
  </div>
) : null;

// ── GST VALIDATION HELPERS ───────────────────────────────────────────────
const GST_STATE_CODES = {
  "01":"Jammu & Kashmir","02":"Himachal Pradesh","03":"Punjab","04":"Chandigarh",
  "05":"Uttarakhand","06":"Haryana","07":"Delhi NCT","08":"Rajasthan",
  "09":"Uttar Pradesh","10":"Bihar","11":"Sikkim","12":"Arunachal Pradesh",
  "13":"Nagaland","14":"Manipur","15":"Mizoram","16":"Tripura",
  "17":"Meghalaya","18":"Assam","19":"West Bengal","20":"Jharkhand",
  "21":"Odisha","22":"Chhattisgarh","23":"Madhya Pradesh","24":"Gujarat",
  "25":"Daman & Diu","26":"Dadra & Nagar Haveli","27":"Maharashtra",
  "28":"Andhra Pradesh","29":"Karnataka","30":"Goa","31":"Lakshadweep",
  "32":"Kerala","33":"Tamil Nadu","34":"Puducherry","35":"Andaman & Nicobar",
  "36":"Telangana","37":"Andhra Pradesh (New)","38":"Ladakh",
};

function validateGST(gst) {
  const g = (gst || "").trim().toUpperCase();
  if (!g) return { valid: null, msg: "", state: "" };
  if (g.length < 15) return { valid: false, msg: "GST number must be 15 characters", state: "" };
  if (g.length > 15) return { valid: false, msg: "GST number cannot exceed 15 characters", state: "" };

  // Format: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
  const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!regex.test(g)) return { valid: false, msg: "Invalid format. Expected: 27AABCS1234A1ZB", state: "" };

  // State code check
  const stateCode = g.slice(0, 2);
  const stateName = GST_STATE_CODES[stateCode];
  if (!stateName) return { valid: false, msg: `Invalid state code: ${stateCode}`, state: "" };

  // Checksum validation (official GST algorithm)
  const CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const v = CHARS.indexOf(g[i]);
    const m = v * (i % 2 === 0 ? 1 : 2);
    sum += Math.floor(m / 36) + (m % 36);
  }
  const checkChar = CHARS[(36 - (sum % 36)) % 36];
  if (checkChar !== g[14]) return { valid: false, msg: "Invalid checksum — please verify the number", state: stateName };

  return { valid: true, msg: `✓ Valid GST · ${stateName}`, state: stateName };
}

// ── AUTH PAGE ─────────────────────────────────────────────────────────────
function AuthPage({ onAuth, onBack }) {
  const [mode, setMode]       = useState("login");
  const [role, setRole]       = useState("seller");
  const [form, setForm]       = useState({ email:"", password:"", name:"", company:"", gst:"", city:"", state:"", phone:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [gstStatus, setGstStatus] = useState({ valid: null, msg: "", state: "" });
  const upd = (k,v) => setForm(p=>({...p,[k]:v}));

  const handleGSTChange = (val) => {
    const upper = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
    upd("gst", upper);
    const result = validateGST(upper);
    setGstStatus(result);
    // Auto-fill state if valid
    if (result.valid && result.state) {
      // Map full state name to select option
      const stateMap = {
        "Punjab":"Punjab","Maharashtra":"Maharashtra","Gujarat":"Gujarat",
        "Tamil Nadu":"Tamil Nadu","Karnataka":"Karnataka","Delhi NCT":"Delhi NCT",
        "Haryana":"Haryana","Rajasthan":"Rajasthan","Uttar Pradesh":"Uttar Pradesh",
        "West Bengal":"West Bengal","Telangana":"Telangana","Andhra Pradesh":"Other",
        "Andhra Pradesh (New)":"Other","Kerala":"Other","Madhya Pradesh":"Other",
        "Bihar":"Other","Odisha":"Other","Jharkhand":"Other","Assam":"Other",
        "Chhattisgarh":"Other","Uttarakhand":"Other","Himachal Pradesh":"Other",
        "Jammu & Kashmir":"Other","Goa":"Other","Chandigarh":"Other",
        "Arunachal Pradesh":"Other","Manipur":"Other","Meghalaya":"Other",
        "Mizoram":"Other","Nagaland":"Other","Sikkim":"Other","Tripura":"Other",
        "Puducherry":"Other","Ladakh":"Other","Lakshadweep":"Other",
        "Andaman & Nicobar":"Other","Daman & Diu":"Other","Dadra & Nagar Haveli":"Other",
      };
      const mapped = stateMap[result.state] || "Other";
      upd("state", mapped);
    }
  };

  const quickLogin = async (r) => {
    setLoading(true); setError("");
    try {
      const emails = { seller:"seller@hmcgroup.in", buyer:"buyer@narayan.com", admin:"admin@surpliq.com" };
      const user = await Auth.login(emails[r], "demo123");
      onAuth(user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const submit = async () => {
    if (!form.email || !form.password) { setError("Email and password required"); return; }
    if (mode === "register") {
      if (!form.name || !form.company) { setError("Name and company required"); return; }
      if (form.gst && gstStatus.valid === false) { setError("Please enter a valid GST number or leave it blank"); return; }
    }
    setLoading(true); setError("");
    try {
      let user;
      if (mode === "login") {
        user = await Auth.login(form.email, form.password);
      } else {
        user = await Auth.register(form.email, form.password, { name:form.name, company:form.company, gst:form.gst, city:form.city, state:form.state, phone:form.phone, role, kycStatus:"pending" });
      }
      onAuth(user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(140deg,#070E1A,#0D1E36,#122848)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ marginBottom:32, textAlign:"center" }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"rgba(255,255,255,.35)", fontSize:12, cursor:"pointer", display:"inline-flex", alignItems:"center", gap:6, fontFamily:"var(--fb)", marginBottom:20, padding:"4px 0" }}>← Back to home</button>
        <div className="logo" onClick={onBack} style={{ justifyContent:"center", fontSize:24, marginBottom:8, cursor:"pointer" }}><svg width="34" height="34" viewBox="0 0 34 34" style={{display:"block"}}><defs><linearGradient id="slgauth" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#C8A84B"/><stop offset="100%" stopColor="#E8C96A"/></linearGradient></defs><circle cx="17.0" cy="17.0" r="11.2" fill="none" stroke="url(#slgauth)" strokeWidth="2.7"/><circle cx="32.1" cy="17.0" r="3.9" fill="rgba(255,255,255,0.9)"/><circle cx="24.3" cy="17.0" r="3.9" fill="#E8C96A"/></svg><span style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,letterSpacing:-0.5,whiteSpace:"nowrap",lineHeight:1}}><span style={{color:"#C8A84B"}}>Surp</span><span style={{color:"#ffffff"}}>liq</span></span></div>
        <div style={{ color:"rgba(255,255,255,.45)", fontSize:13 }}>India's B2B Industrial Surplus Marketplace</div>
      </div>

      {/* Quick Demo Logins */}
      <div style={{ background:"rgba(255,255,255,.05)", border:"1px solid rgba(255,255,255,.1)", borderRadius:14, padding:"14px 18px", marginBottom:20, width:"100%", maxWidth:420 }}>
        <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.35)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Quick Demo Login</div>
        <div style={{ display:"flex", gap:8 }}>
          {["seller","buyer","admin"].map(r=>(
            <button key={r} className="btn btn-sm" style={{ flex:1, background:"rgba(255,255,255,.07)", color:"rgba(255,255,255,.7)", border:"1px solid rgba(255,255,255,.12)" }} onClick={()=>quickLogin(r)} disabled={loading}>
              {r==="seller"?"📦 Seller":r==="buyer"?"🛒 Buyer":"⚙️ Admin"}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background:"#fff", borderRadius:18, padding:28, width:"100%", maxWidth:420 }}>
        <div style={{ display:"flex", gap:0, marginBottom:22, background:"var(--off)", borderRadius:10, padding:4 }}>
          {["login","register"].map(m=>(
            <button key={m} style={{ flex:1, padding:"8px", borderRadius:8, border:"none", fontFamily:"var(--fb)", fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .2s", background:mode===m?"var(--navy)":"transparent", color:mode===m?"#fff":"var(--muted)" }} onClick={()=>{setMode(m);setError("");}}>
              {m==="login"?"Login":"Register"}
            </button>
          ))}
        </div>

        {mode === "register" && (
          <div style={{ display:"flex", gap:6, marginBottom:16 }}>
            {["seller","buyer"].map(r=>(
              <button key={r} className={`btn btn-sm`} style={{ flex:1, background:role===r?"var(--navy)":"transparent", color:role===r?"#fff":"var(--muted)", border:"1.5px solid "+(role===r?"var(--navy)":"var(--border)") }} onClick={()=>setRole(r)}>
                {r==="seller"?"📦 I'm a Seller":"🛒 I'm a Buyer"}
              </button>
            ))}
          </div>
        )}

        <div className="fgrp">
          <label className="flabel">Email <span>*</span></label>
          <input className="finput" type="email" placeholder="you@company.com" value={form.email} onChange={e=>upd("email",e.target.value)} />
        </div>
        <div className="fgrp">
          <label className="flabel">Password <span>*</span></label>
          <input className="finput" type="password" placeholder="••••••••" value={form.password} onChange={e=>upd("password",e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} />
        </div>

        {mode === "register" && (
          <>
            <div className="frow">
              <div className="fgrp"><label className="flabel">Your Name <span>*</span></label><input className="finput" placeholder="Full name" value={form.name} onChange={e=>upd("name",e.target.value)} /></div>
              <div className="fgrp"><label className="flabel">Company <span>*</span></label><input className="finput" placeholder="Company name" value={form.company} onChange={e=>upd("company",e.target.value)} /></div>
            </div>
            <div className="frow">
              <div className="fgrp">
                <label className="flabel" style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span>GST Number</span>
                  {form.gst && (
                    <span style={{ fontSize:10, fontWeight:700,
                      color: gstStatus.valid === true ? "#16A34A" : gstStatus.valid === false ? "#DC2626" : "#94A3B8" }}>
                      {gstStatus.valid === true ? "✓ Valid" : gstStatus.valid === false ? "✗ Invalid" : "Checking..."}
                    </span>
                  )}
                </label>
                <div style={{ position:"relative" }}>
                  <input
                    className="finput"
                    placeholder="e.g. 27AABCS1234A1ZB"
                    value={form.gst}
                    maxLength={15}
                    style={{ paddingRight:38,
                      borderColor: form.gst ? (gstStatus.valid === true ? "#16A34A" : gstStatus.valid === false ? "#DC2626" : "var(--border)") : "var(--border)",
                      boxShadow: form.gst && gstStatus.valid === true ? "0 0 0 3px rgba(22,163,74,.08)" : form.gst && gstStatus.valid === false ? "0 0 0 3px rgba(220,38,38,.07)" : "none"
                    }}
                    onChange={e=>handleGSTChange(e.target.value)}
                  />
                  {form.gst && (
                    <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", fontSize:16 }}>
                      {gstStatus.valid === true ? "✅" : gstStatus.valid === false ? "❌" : "⏳"}
                    </span>
                  )}
                </div>
                {form.gst && gstStatus.msg && (
                  <div style={{ fontSize:11, marginTop:5, fontWeight:600,
                    color: gstStatus.valid === true ? "#16A34A" : gstStatus.valid === false ? "#DC2626" : "#94A3B8" }}>
                    {gstStatus.msg}
                  </div>
                )}
                {gstStatus.valid === true && gstStatus.state && (
                  <div style={{ fontSize:11, marginTop:3, color:"#94A3B8" }}>
                    📍 State auto-detected: <strong style={{ color:"#0A1628" }}>{gstStatus.state}</strong>
                  </div>
                )}
              </div>
              <div className="fgrp"><label className="flabel">Phone</label><input className="finput" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e=>upd("phone",e.target.value)} /></div>
            </div>
            <div className="frow">
              <div className="fgrp"><label className="flabel">City</label><input className="finput" placeholder="e.g. Ludhiana" value={form.city} onChange={e=>upd("city",e.target.value)} /></div>
              <div className="fgrp">
                <label className="flabel">State</label>
                <select className="fselect" value={form.state} onChange={e=>upd("state",e.target.value)}>
                  <option value="">Select</option>
                  {["Punjab","Maharashtra","Gujarat","Tamil Nadu","Karnataka","Delhi NCT","Haryana","Rajasthan","Uttar Pradesh","West Bengal","Telangana","Other"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </>
        )}

        {error && <div className="ferr" style={{ marginBottom:12 }}>❌ {error}</div>}
        <button className="btn btn-gold btn-full btn-lg" onClick={submit} disabled={loading}>
          {loading?"Processing...":mode==="login"?"Login to Surpliq →":"Create Account →"}
        </button>
      </div>
    </div>
  );
}


// ── PRO UPGRADE MODAL ─────────────────────────────────────────────────────
function ProUpgradeModal({ onClose, onUpgrade }) {
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div style={{ background:'#fff',borderRadius:20,width:'100%',maxWidth:460,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#0A1628,#1E3A6E)',padding:'24px 24px 20px',textAlign:'center',position:'relative' }}>
          <button onClick={onClose} style={{ position:'absolute',top:14,right:14,background:'rgba(255,255,255,.1)',border:'none',borderRadius:'50%',width:28,height:28,cursor:'pointer',fontSize:16,color:'rgba(255,255,255,.6)',display:'flex',alignItems:'center',justifyContent:'center' }}>×</button>
          <div style={{ fontSize:36,marginBottom:10 }}>🎯</div>
          <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:20,color:'#fff',marginBottom:6 }}>Unlock AI Buyer Discovery</div>
          <div style={{ fontSize:12,color:'rgba(255,255,255,.55)',lineHeight:1.6 }}>
            Our AI scans IndiaMART, TradeIndia, MRAI, and Surpliq's network to find verified buyers for your surplus — and sends them a personalised outreach with your listing.
          </div>
        </div>
        {/* Features */}
        <div style={{ padding:'20px 24px' }}>
          <div style={{ fontSize:11,fontWeight:700,color:'var(--muted)',textTransform:'uppercase',letterSpacing:1.5,marginBottom:12 }}>What you get with Pro</div>
          {[
            ['🎯','AI Buyer Discovery','Unlimited searches across IndiaMART, TradeIndia, MRAI & Surpliq network'],
            ['📣','Automated Outreach','Pre-written WhatsApp + email messages sent to matched buyers'],
            ['📊','Listing Analytics','Views, enquiry rates, buyer interest trends per listing'],
            ['🔔','Priority Notifications','Real-time alerts when high-intent buyers view your listings'],
            ['🏷️','Featured Listings','Your listings appear at the top of buyer search results'],
          ].map(([ico,title,desc])=>(
            <div key={title} style={{ display:'flex',gap:12,marginBottom:14,alignItems:'flex-start' }}>
              <span style={{ fontSize:20,flexShrink:0 }}>{ico}</span>
              <div>
                <div style={{ fontWeight:700,fontSize:13,color:'var(--navy)',marginBottom:2 }}>{title}</div>
                <div style={{ fontSize:11,color:'var(--muted)',lineHeight:1.5 }}>{desc}</div>
              </div>
              <span style={{ flexShrink:0,fontSize:14,color:'var(--green)' }}>✅</span>
            </div>
          ))}
          {/* Pricing */}
          <div style={{ background:'linear-gradient(135deg,#0A1628,#1E3A6E)',borderRadius:12,padding:'16px',textAlign:'center',margin:'16px 0' }}>
            <div style={{ fontSize:11,color:'rgba(255,255,255,.4)',marginBottom:4,textTransform:'uppercase',letterSpacing:1 }}>Pro Plan</div>
            <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:32,color:'#C8A84B',letterSpacing:-1 }}>₹499<span style={{ fontSize:14,color:'rgba(255,255,255,.4)',fontWeight:400 }}>/month</span></div>
            <div style={{ fontSize:11,color:'rgba(255,255,255,.4)',marginTop:4 }}>Cancel anytime · GST invoice provided</div>
          </div>
          <button className="btn btn-gold btn-full" style={{ fontSize:15,padding:'14px',marginBottom:10 }} onClick={onUpgrade}>
            🚀 Upgrade to Pro — ₹499/month
          </button>
          <div style={{ fontSize:11,color:'var(--muted)',textAlign:'center',lineHeight:1.6 }}>
            Payment via Razorpay (UPI, Cards, NetBanking).<br/>
            For enterprise pricing, contact <a href="mailto:support@surpliq.com" style={{ color:'var(--gold)' }}>support@surpliq.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── SELLER DASHBOARD ─────────────────────────────────────────────────────
function SellerDash({ user, onTab, tab }) {
  const ww = useWindowWidth(); const mob = ww < 640;
  const [listings,  setListings]  = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [aiInsight, setAiInsight] = useState('');
  const [shareFromDash, setShareFromDash] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [l, e] = await Promise.all([
        DB.list('listings',  [['sellerId','==',user._id]]),
        DB.list('enquiries', [['sellerId','==',user._id]]),
      ]);
      setListings(l); setEnquiries(e); setLoading(false);
    })();
  }, [user._id]);

  // ── Computed metrics ───────────────────────────────────────────────
  const active       = listings.filter(l => l.status === 'active');
  const sold         = listings.filter(l => l.status === 'sold');
  const totalBook    = listings.reduce((s,l) => s + Number(l.bookValue||0), 0);
  const totalAsking  = active.reduce((s,l) => s + Number(l.price||0), 0);
  const openEnq      = enquiries.filter(e => e.status !== 'closed' && e.status !== 'declined');
  const newEnq       = enquiries.filter(e => e.status === 'new');
  const deals        = enquiries.filter(e => e.status === 'accepted');
  const totalViews   = listings.reduce((s,l) => s + (l.views||0), 0);
  const totalInquiryCount = listings.reduce((s,l) => s + (l.enquiryCount||0), 0);

  // Recovery rate: asking vs book value
  const recoveryRate = totalBook > 0 ? Math.round((totalAsking / totalBook) * 100) : 0;

  // Category breakdown
  const catCounts = listings.reduce((acc,l) => {
    acc[l.category] = (acc[l.category]||0) + 1; return acc;
  }, {});

  // Listing performance sorted by views
  const topListings = [...active].sort((a,b) => (b.views||0)-(a.views||0)).slice(0,5);

  // Recent enquiries
  const recentEnq = [...enquiries]
    .sort((a,b) => new Date(b.updatedAt||0)-new Date(a.updatedAt||0))
    .slice(0,4);

  const getAiInsight = async () => {
    setAiLoading(true);
    const insight = await callAI(
      `You are an industrial surplus sales advisor. Analyse this seller's portfolio and give 2–3 specific, actionable insights to improve recovery rate and deal closure speed. Be concrete and India-specific.`,
      `Seller: ${user.company}. Active listings: ${active.length}. Book value: ₹${(totalBook/100000).toFixed(1)}L. Asking value: ₹${(totalAsking/100000).toFixed(1)}L. Recovery rate: ${recoveryRate}%. Open enquiries: ${openEnq.length}. Deals closed: ${deals.length}. Total views: ${totalViews}. Categories: ${JSON.stringify(catCounts)}. Avg age in storage: ${Math.round(listings.reduce((s,l)=>s+Number(l.ageMonths||0),0)/Math.max(listings.length,1))} months.`,
      '1. Reduce asking price on items >18 months old by 8-10% to accelerate deals. 2. Add photos to listings — visual listings get 3x more enquiries. 3. Follow up on open enquiries within 2 hours for best conversion.'
    );
    setAiInsight(insight); setAiLoading(false);
  };

  if (loading) return (
    <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
      <div className="ai-dots" style={{ justifyContent:'center', marginBottom:8 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>
      Loading dashboard…
    </div>
  );

  return (
    <div className="fu">
      {shareFromDash && <ShareModal listing={shareFromDash} onClose={()=>setShareFromDash(null)}/>}
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div className="eye">Seller Dashboard</div>
        <div className="h1">Welcome back, {user.name?.split(' ')[0]} 👋</div>
        <div className="sub">{user.company} · KYC {user.kycStatus==='approved'?'✅ Verified':'⏳ Pending'}</div>
      </div>

      {/* Quick actions */}
      <div style={{ display:'flex', gap:10, marginBottom:22, flexWrap:'wrap' }}>
        <button className="btn btn-gold" onClick={()=>onTab('create')}>+ New Listing</button>
        {newEnq.length > 0 && (
          <button className="btn btn-navy" onClick={()=>onTab('enquiries')}>
            💬 {newEnq.length} New Enquir{newEnq.length===1?'y':'ies'}
          </button>
        )}
        <button className="btn btn-out" onClick={()=>onTab('listings')}>My Listings →</button>
      </div>

      {/* KPI cards — row 1 */}
      <div style={{ display:'grid', gridTemplateColumns: mob?'1fr 1fr':'repeat(4,1fr)', gap:10, marginBottom:12 }}>
        {[
          { n:`₹${(totalBook/100000).toFixed(1)}L`,   l:'Book Value',      d:'Total inventory listed',      c:'var(--navy)', ico:'📒' },
          { n:`₹${(totalAsking/100000).toFixed(1)}L`, l:'Asking Value',    d:`${active.length} active lots`, c:'var(--blue)', ico:'🏷️' },
          { n:`${recoveryRate}%`,                     l:'Recovery Rate',   d:'Asking ÷ Book value',         c: recoveryRate>=70?'var(--green)':recoveryRate>=50?'var(--amber)':'var(--red)', ico:'📈' },
          { n:deals.length,                           l:'Deals Closed',    d:'Lifetime',                    c:'var(--green)', ico:'🤝' },
        ].map((k,i) => (
          <div key={i} style={{ background:'#fff', border:'1.5px solid var(--border)', borderRadius:14, padding:'16px 16px', position:'relative', overflow:'hidden' }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{k.ico}</div>
            <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:22, color:k.c, letterSpacing:-1, lineHeight:1 }}>{k.n}</div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--navy)', marginTop:4 }}>{k.l}</div>
            <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{k.d}</div>
          </div>
        ))}
      </div>

      {/* KPI cards — row 2 */}
      <div style={{ display:'grid', gridTemplateColumns: mob?'1fr 1fr':'repeat(4,1fr)', gap:10, marginBottom:20 }}>
        {[
          { n:active.length,        l:'Active Listings',  d:'Live to buyers',        c:'var(--navy)',  ico:'📦' },
          { n:openEnq.length,       l:'Open Enquiries',   d: openEnq.length>0?'Needs attention':'All clear', c:openEnq.length>0?'var(--amber)':'var(--green)', ico:'💬' },
          { n:totalViews,           l:'Total Views',      d:'Across all listings',   c:'var(--blue)',  ico:'👁️' },
          { n:totalInquiryCount,    l:'Total Enquiries',  d:'All-time received',     c:'var(--navy)',  ico:'📨' },
        ].map((k,i) => (
          <div key={i} style={{ background:'#fff', border:'1.5px solid var(--border)', borderRadius:14, padding:'16px 16px', cursor: k.l==='Open Enquiries'&&openEnq.length>0?'pointer':'default' }}
            onClick={k.l==='Open Enquiries'&&openEnq.length>0 ? ()=>onTab('enquiries') : undefined}>
            <div style={{ fontSize:22, marginBottom:6 }}>{k.ico}</div>
            <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:22, color:k.c, letterSpacing:-1, lineHeight:1 }}>{k.n}</div>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--navy)', marginTop:4 }}>{k.l}</div>
            <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{k.d}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 1fr', gap:14, marginBottom:14 }}>

        {/* Top performing listings */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div className="h2">Top Listings by Views</div>
            <button className="btn btn-out btn-sm" onClick={()=>onTab('listings')}>All →</button>
          </div>
          {topListings.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--muted)' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📦</div>
              <div style={{ fontSize:13, marginBottom:12 }}>No listings yet</div>
              <button className="btn btn-gold btn-sm" onClick={()=>onTab('create')}>Create First Listing →</button>
            </div>
          ) : topListings.map((l,i) => (
            <div key={l._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom: i<topListings.length-1?'1px solid var(--border)':'none' }}>
              <div style={{ width:28, height:28, borderRadius:8, background:'#F0F4FF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>
                {l.category==='RM'?'🏗️':l.category==='BOP'?'⚙️':l.category==='MRO'?'🔧':l.category==='CE'?'🏭':'📦'}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:12, color:'var(--navy)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.title}</div>
                <div style={{ fontSize:10, color:'var(--muted)', marginTop:1 }}>
                  👁 {l.views||0} · 💬 {l.enquiryCount||0} · ₹{Number(l.price||0).toLocaleString('en-IN')}{l.priceUnit}
                </div>
              </div>
              {/* Mini progress bar: views as % of top performer */}
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                <div style={{ width:50 }}>
                  <div style={{ height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
                    <div style={{ height:'100%', background:'var(--gold)', borderRadius:2, width:`${topListings[0].views>0?Math.round((l.views||0)/topListings[0].views*100):0}%` }}/>
                  </div>
                  <div style={{ fontSize:9, color:'var(--muted)', textAlign:'right', marginTop:2 }}>{l.views||0}v</div>
                </div>
                <button className="btn btn-gold" style={{ fontSize:10, padding:'3px 8px' }}
                  onClick={e=>{e.stopPropagation();setShareFromDash(l);}}>📤</button>
              </div>
            </div>
          ))}
        </div>

        {/* Recent enquiries */}
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div className="h2">Recent Enquiries</div>
            {openEnq.length > 0 && <button className="btn btn-navy btn-sm" onClick={()=>onTab('enquiries')}>Respond →</button>}
          </div>
          {recentEnq.length === 0 ? (
            <div style={{ textAlign:'center', padding:'24px 0', color:'var(--muted)' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>💬</div>
              <div style={{ fontSize:13 }}>No enquiries yet. Share your listings to attract buyers.</div>
            </div>
          ) : recentEnq.map((e,i) => {
            const lastMsg = (e.thread||[]).slice(-1)[0];
            const isNew   = e.status === 'new';
            return (
              <div key={e._id} style={{ padding:'10px 0', borderBottom: i<recentEnq.length-1?'1px solid var(--border)':'none', cursor:'pointer' }}
                onClick={()=>onTab('enquiries')}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                      {isNew && <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--gold)', display:'inline-block', flexShrink:0 }}/>}
                      <span style={{ fontWeight:700, fontSize:12, color:'var(--navy)' }}>{e.buyerName} · {e.buyerCompany}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--muted)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'90%' }}>
                      {e.listingTitle}
                    </div>
                    {lastMsg && (
                      <div style={{ fontSize:11, color:'var(--muted)', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'90%' }}>
                        "{lastMsg.text?.slice(0,60)}{lastMsg.text?.length>60?'…':''}"
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:8, flexShrink:0, marginLeft:8,
                    background: isNew?'rgba(200,168,75,.15)':'rgba(10,22,40,.06)',
                    color: isNew?'#C8A84B':'var(--muted)' }}>
                    {isNew?'NEW':e.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category breakdown */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="h2" style={{ marginBottom:14 }}>Portfolio Breakdown</div>
        <div style={{ display:'grid', gridTemplateColumns: mob?'1fr 1fr':'repeat(5,1fr)', gap:10 }}>
          {[['RM','Raw Materials','🏗️'],['BOP','Bought-Out Parts','⚙️'],['MRO','MRO & Consumables','🔧'],['CE','Capital Equipment','🏭'],['FG','Finished Goods','📦']].map(([cat,label,ico]) => {
            const count = catCounts[cat]||0;
            const pct   = listings.length > 0 ? Math.round(count/listings.length*100) : 0;
            return (
              <div key={cat} style={{ textAlign:'center', padding:'12px 8px', background:'var(--off)', borderRadius:10, border:'1.5px solid var(--border)' }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{ico}</div>
                <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:20, color: count>0?'var(--navy)':'var(--border)' }}>{count}</div>
                <div style={{ fontSize:10, fontWeight:700, color:'var(--navy)', marginTop:2 }}>{cat}</div>
                <div style={{ fontSize:9, color:'var(--muted)' }}>{label}</div>
                {count > 0 && (
                  <div style={{ marginTop:6, height:3, background:'var(--border)', borderRadius:2 }}>
                    <div style={{ height:'100%', background:'var(--gold)', borderRadius:2, width:`${pct}%` }}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Earnings summary */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="h2" style={{ marginBottom:14 }}>Earnings & Recovery</div>
        <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 1fr 1fr', gap:14 }}>
          <div style={{ padding:'14px 16px', background:'linear-gradient(135deg,#0A1628,#1E3A6E)', borderRadius:12, textAlign:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginBottom:4 }}>BOOK VALUE IN MARKET</div>
            <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:20, color:'#fff' }}>₹{(totalBook/100000).toFixed(1)}L</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,.35)', marginTop:2 }}>{listings.length} lots</div>
          </div>
          <div style={{ padding:'14px 16px', background:'linear-gradient(135deg,#1a472a,#2d6a4f)', borderRadius:12, textAlign:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginBottom:4 }}>EXPECTED RECOVERY</div>
            <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:20, color:'#fff' }}>₹{(totalAsking/100000).toFixed(1)}L</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,.35)', marginTop:2 }}>{recoveryRate}% of book</div>
          </div>
          <div style={{ padding:'14px 16px', background:'linear-gradient(135deg,#7B3F00,#C8A84B)', borderRadius:12, textAlign:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.55)', marginBottom:4 }}>PLATFORM FEE (est.)</div>
            <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:20, color:'#fff' }}>₹{((totalAsking*0.03)/100000).toFixed(2)}L</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,.55)', marginTop:2 }}>3% on deal value</div>
          </div>
        </div>

        {/* Recovery rate bar */}
        <div style={{ marginTop:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:6 }}>
            <span style={{ color:'var(--muted)' }}>Recovery Rate</span>
            <span style={{ fontWeight:700, color: recoveryRate>=70?'var(--green)':recoveryRate>=50?'var(--amber)':'var(--red)' }}>{recoveryRate}%</span>
          </div>
          <div style={{ height:8, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
            <div style={{ height:'100%', borderRadius:4, transition:'width .6s',
              background: recoveryRate>=70?'var(--green)':recoveryRate>=50?'var(--amber)':'var(--red)',
              width:`${Math.min(recoveryRate,100)}%` }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:9, color:'var(--muted)', marginTop:4 }}>
            <span>0%</span><span>50% Good</span><span>70% Excellent</span><span>100%</span>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: aiInsight?12:0 }}>
          <div>
            <div className="eye">AI Sales Coach</div>
            <div className="h2">Portfolio Intelligence</div>
          </div>
          {!aiInsight && (
            <button className="btn btn-gold btn-sm" onClick={getAiInsight} disabled={aiLoading || listings.length===0}>
              {aiLoading ? '🤖 Thinking…' : '🤖 Get Insights'}
            </button>
          )}
        </div>
        {aiLoading && <div className="ai-dots" style={{ marginTop:10 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>}
        {aiInsight && (
          <div style={{ background:'linear-gradient(135deg,#0A1628,#1B3A5C)', borderRadius:12, padding:'16px 18px' }}>
            <span style={{ background:'rgba(200,168,75,.2)', border:'1px solid rgba(200,168,75,.3)', color:'#C8A84B', fontSize:10, fontWeight:700, letterSpacing:1, textTransform:'uppercase', padding:'2px 8px', borderRadius:6, display:'inline-block', marginBottom:10 }}>AI COACH</span>
            <div style={{ fontSize:13, color:'rgba(255,255,255,.8)', lineHeight:1.8, whiteSpace:'pre-wrap' }}>{aiInsight}</div>
            <button className="btn btn-out btn-sm" style={{ marginTop:12, color:'rgba(255,255,255,.5)', borderColor:'rgba(255,255,255,.15)' }} onClick={()=>setAiInsight('')}>Refresh →</button>
          </div>
        )}
        {listings.length === 0 && !aiLoading && (
          <div style={{ textAlign:'center', padding:'16px 0', color:'var(--muted)', fontSize:12 }}>
            Create your first listing to unlock AI portfolio insights.
          </div>
        )}
      </div>
    </div>
  );
}


// ── CREATE LISTING (AI-POWERED) ───────────────────────────────────────────
function CreateListing({ user, onSaved, onCancel }) {
  const [mode,        setMode]        = useState(null);
  const [step,        setStep]        = useState(0);
  const [logs,        setLogs]        = useState([]);
  const [busy,        setBusy]        = useState(false);
  const [parsedRows,  setParsedRows]  = useState([]);
  const [priceBands,  setPriceBands]  = useState(null);
  const [aiDesc,      setAiDesc]      = useState('');
  const [saving,      setSaving]      = useState(false);
  const [uploadedFile,setUploadedFile]= useState(null);
  const [images,      setImages]      = useState([]);
  const [form, setForm] = useState({
    title:'', category:'RM', description:'', condition:'Used — Good',
    qty:'', unit:'MT', price:'', priceUnit:'/MT',
    bookValue:'', ageMonths:'',
    location: (user.city && user.state) ? `${user.city}, ${user.state}` : ''
  });

  const upd    = (k,v) => setForm(p => ({...p,[k]:v}));
  const log    = msg  => setLogs(p => [...p, msg]);
  const clrLog = ()   => setLogs([]);

  // ─────────────────────────────────────────────────────────────────
  // FILE PROCESSING
  // ─────────────────────────────────────────────────────────────────
  const readFileAsText = file => new Promise((ok, fail) => {
    const r = new FileReader();
    r.onload  = e => ok(e.target.result);
    r.onerror = () => fail(new Error('Cannot read file'));
    r.readAsText(file, 'UTF-8');
  });

  const readFileAsBuffer = file => new Promise((ok, fail) => {
    const r = new FileReader();
    r.onload  = e => ok(e.target.result);
    r.onerror = () => fail(new Error('Cannot read file'));
    r.readAsArrayBuffer(file);
  });

  // ── Universal Excel/CSV text extractor ─────────────────────────────
  // Strategy: extract ALL readable strings from the binary, then let AI interpret structure
  const extractFromExcel = buf => {
    const u8   = new Uint8Array(buf);
    const dec  = new TextDecoder('utf-8', { fatal:false });

    // Try to parse as xlsx XML (modern .xlsx files are ZIP+XML internally)
    // Extract all cell values by scanning for shared strings and inline strings
    const raw = dec.decode(u8);

    // Method 1: extract XML cell values from xlsx
    const xmlStrings = [];
    const siMatches = raw.matchAll(/<t(?:\s[^>]*)?>([^<]{1,200})<\/t>/g);
    for (const m of siMatches) {
      const v = m[1].trim();
      if (v.length > 0) xmlStrings.push(v);
    }
    if (xmlStrings.length > 50) {
      // Group into rows of ~8 values (typical spreadsheet width)
      const rows = [];
      for (let i = 0; i < Math.min(xmlStrings.length, 2000); i += 8) {
        rows.push(xmlStrings.slice(i, i+8).join('\t'));
      }
      return rows.join('\n');
    }

    // Method 2: fallback — scan for printable ASCII strings
    let out = '', run = '';
    for (let i = 0; i < Math.min(u8.length, 500000); i++) {
      const b = u8[i];
      if (b >= 32 && b < 127) { run += String.fromCharCode(b); }
      else { if (run.length > 2) out += run + '\n'; run = ''; }
    }
    return out.split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 1 && !l.startsWith('<') && !/^[PK\x00]/.test(l) && !/^(xml|Content|Relationship|workbook|sheet)/i.test(l))
      .slice(0, 1500)
      .join('\n');
  };

  // ── Smarter CSV parser — handles headers on any row ─────────────────
  const findHeaderRow = lines => {
    // Look for row with most text-like cells (header rows have more text than numbers)
    let bestRow = 0, bestScore = 0;
    lines.slice(0, 15).forEach((line, i) => {
      const cells = line.split(/[,\t]/).map(c => c.replace(/"/g,'').trim());
      const textCells = cells.filter(c => isNaN(c) && c.length > 1 && c.length < 60);
      if (textCells.length > bestScore) { bestScore = textCells.length; bestRow = i; }
    });
    return bestRow;
  };

  // Simple CSV parser
  const parseCSV = text => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return null;
    const sep = lines[0].includes('\t') ? '\t' : ',';
    const headers = lines[0].split(sep).map(h => h.replace(/"/g,'').trim());
    const rows = lines.slice(1, 61).map(line => {
      const vals = line.split(sep).map(v => v.replace(/"/g,'').trim());
      const row = {};
      headers.forEach((h,i) => { row[h] = vals[i] ?? ''; });
      return row;
    }).filter(r => Object.values(r).some(v => v));
    return { headers, rows };
  };

  const handleFile = async file => {
    if (!file) return;
    setUploadedFile(file);
    clrLog();
    setBusy(true);
    setStep(1);                           // ← show log screen immediately
    log(`📂 Reading "${file.name}"…`);

    const ext = file.name.split('.').pop().toLowerCase();
    let content = '';

    try {
      if (['csv','txt','tsv'].includes(ext)) {
        const text = await readFileAsText(file);
        const parsed = parseCSV(text);
        if (parsed) {
          log(`✅ ${parsed.rows.length} rows, columns: ${parsed.headers.slice(0,5).join(', ')}`);
          // Group by material for MB51 files before sending to AI
          const isMB51 = parsed.headers.some(h => h && h.toString().toLowerCase().includes('movement'));
          let dataToSend;
          if (isMB51) {
            // Aggregate by material description
            const agg = {};
            parsed.rows.forEach(row => {
              const mat  = row['Material Description'] || row['Material'] || '';
              const qty  = parseFloat(row['Quantity'] || row['Qty'] || 0);
              const val  = parseFloat(row['Amt.in Loc.Cur.'] || row['Amount'] || row['Value'] || 0);
              const unit = row['Order Unit'] || row['Unit'] || 'NOS';
              const code = row['Material'] || '';
              if (!mat) return;
              if (!agg[mat]) agg[mat] = {mat,code,qty:0,val:0,unit};
              agg[mat].qty += qty;
              agg[mat].val += val;
              agg[mat].unit = unit;
            });
            const top = Object.values(agg).sort((a,b)=>b.val-a.val).slice(0,30);
            dataToSend = `SAP MB51 Material Movement Export\nTop 30 materials by value:\n` + 
              top.map(r=>`Material: ${r.mat} (${r.code}) | Qty: ${r.qty.toFixed(0)} ${r.unit} | Book Value: INR ${r.val.toFixed(0)}`).join('\n');
            log(`📊 SAP MB51 detected — aggregated ${Object.keys(agg).length} materials, showing top 30 by value`);
          } else {
            dataToSend = JSON.stringify(parsed.rows.slice(0,60),null,1).slice(0,4500);
          }
          content = `File: ${file.name}\nColumns: ${parsed.headers.join(', ')}\nData:\n${dataToSend}`;
        } else {
          content = `File: ${file.name}\nRaw text:\n${text.slice(0,4500)}`;
          log(`✅ Text file read (${text.split('\n').length} lines)`);
        }
      } else if (['xlsx','xls'].includes(ext)) {
        log('📊 Excel file — loading SheetJS parser…');
        // Load SheetJS if not already available
        if (!window.XLSX) {
          await new Promise((resolve) => {
            // Try multiple CDN sources
            const cdns = [
              'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js',
              'https://cdn.sheetjs.com/xlsx-0.20.0/package/dist/xlsx.full.min.js',
              'https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js',
            ];
            let tried = 0;
            const tryNext = () => {
              if (tried >= cdns.length) { resolve(); return; }
              const s = document.createElement('script');
              s.src = cdns[tried++];
              s.onload = resolve;
              s.onerror = tryNext;
              document.head.appendChild(s);
            };
            tryNext();
          });
        }
        // If SheetJS still not available, guide user to save as CSV
        if (!window.XLSX) {
          log('⚠️ Excel parser unavailable in this browser.');
          log('✅ Easy fix: Open your Excel file → File → Save As → CSV (.csv) → upload the CSV instead.');
          log('📌 CSV upload works perfectly with all SAP exports including MB51, MB52, MB60.');
          setBusy(false); return;
        }
        const buf  = await readFileAsBuffer(file);
        const wb   = window.XLSX.read(buf, { type:'array' });
        // Find the sheet with most data
        let bestSheet = wb.SheetNames[0];
        let bestRows  = 0;
        wb.SheetNames.forEach(name => {
          const range = window.XLSX.utils.decode_range(wb.Sheets[name]['!ref'] || 'A1:A1');
          const rows  = range.e.r - range.s.r;
          if (rows > bestRows) { bestRows = rows; bestSheet = name; }
        });
        log(`📋 Reading sheet "${bestSheet}" (${bestRows} rows across ${wb.SheetNames.length} sheet${wb.SheetNames.length>1?'s':''})`);
        const ws      = wb.Sheets[bestSheet];
        const rawRows = window.XLSX.utils.sheet_to_json(ws, { defval:'' });
        if (rawRows.length === 0) {
          log('⚠️ Sheet appears empty. Try saving as CSV from Excel.');
          setBusy(false); return;
        }
        const headers = Object.keys(rawRows[0]);
        log(`✅ ${rawRows.length} rows · ${headers.length} columns`);
        log(`📌 Columns: ${headers.slice(0,6).join(', ')}${headers.length>6?'…':''}`);

        // Check if this is an SAP MB51 movement file
        const isMB51 = headers.some(h => /movement/i.test(h));
        // Check if it's any kind of aggregatable stock file
        const hasQty  = headers.some(h => /qty|quantity|stock|unrestrict/i.test(h));
        const hasVal  = headers.some(h => /value|amount|amt|price|rate/i.test(h));
        const hasDesc = headers.some(h => /description|desc|material|item|name|part/i.test(h));

        if (isMB51 || (hasQty && hasVal && hasDesc)) {
          // Aggregate by material/item name
          const descKey = headers.find(h => /material desc|description|desc|item name|part name/i.test(h))
                       || headers.find(h => /material|item|name|part/i.test(h)) || headers[0];
          const codeKey = headers.find(h => /^material$|mat\.?no|part.?no|item.?code|sku/i.test(h)) || '';
          const qtyKey  = headers.find(h => /^qty$|^quantity$|unrestrict|closing.*qty|stock/i.test(h))
                       || headers.find(h => /qty|quantity/i.test(h)) || '';
          const valKey  = headers.find(h => /amt.*loc|loc.*cur|total.*val|book.*val|value|amount/i.test(h))
                       || headers.find(h => /val|amt|amount/i.test(h)) || '';
          const unitKey = headers.find(h => /^unit$|order.*unit|base.*unit|uom/i.test(h)) || '';

          log(`🔗 Detected: ${isMB51?'SAP MB51 movements':'inventory stock list'}`);
          log(`📊 Aggregating ${rawRows.length} rows by material…`);

          const agg = {};
          rawRows.forEach(row => {
            const mat  = String(row[descKey] || '').trim();
            const code = codeKey ? String(row[codeKey] || '').trim() : '';
            const qty  = parseFloat(String(row[qtyKey] || '0').replace(/[^0-9.\-]/g,'')) || 0;
            const val  = parseFloat(String(row[valKey]  || '0').replace(/[^0-9.\-]/g,'')) || 0;
            const unit = unitKey ? String(row[unitKey] || 'NOS').trim() : 'NOS';
            if (!mat || mat.length < 2) return;
            if (!agg[mat]) agg[mat] = { mat, code, qty:0, val:0, unit };
            agg[mat].qty += Math.abs(qty);
            agg[mat].val += Math.abs(val);
            if (unit && unit !== 'NOS') agg[mat].unit = unit;
          });

          const top30 = Object.values(agg)
            .filter(r => r.qty > 0 || r.val > 0)
            .sort((a,b) => b.val - a.val)
            .slice(0, 30);

          log(`✅ Aggregated to ${Object.keys(agg).length} unique materials · top 30 by value:`);
          top30.slice(0,5).forEach(r => log(`  • ${r.mat.slice(0,45)} — Qty:${r.qty.toFixed(0)} ${r.unit} · ₹${r.val.toLocaleString('en-IN')}`));

          const dataToSend = `${isMB51?'SAP MB51 Material Movement Export':'Inventory Stock List'}\nFile: ${file.name}\nTop 30 materials by value:\n`
            + top30.map(r => `Material: ${r.mat}${r.code?' ('+r.code+')':''} | Qty: ${r.qty.toFixed(0)} ${r.unit} | Book Value: INR ${r.val.toFixed(0)}`).join('\n');
          content = dataToSend;

        } else {
          // Generic Excel — send first 50 rows as-is
          log('📋 Generic Excel format — sending to AI for interpretation…');
          const sample = rawRows.slice(0,50).map(row =>
            headers.map(h => `${h}: ${row[h]}`).join(' | ')
          ).join('\n');
          content = `File: ${file.name}\nColumns: ${headers.join(', ')}\nData (first 50 rows):\n${sample.slice(0,5000)}`;
        }
      } else {
        log(`❌ Unsupported format. Use CSV, TXT or Excel (.xlsx)`);
        setBusy(false); return;
      }
    } catch (err) {
      log(`❌ Read error: ${err.message}`);
      setBusy(false); return;
    }

    log('🧠 AI identifying inventory items…');
    log('📊 Matching categories, units, values…');

    try {
      const aiRaw = await callAI(
        `You are a universal ERP/inventory file parser for Indian industrial surplus marketplaces.
You can read ANY format: SAP MB51, SAP MB52, SAP MB60, Tally stock summary, custom Excel dumps, CSV exports, aged inventory reports, slow-moving stock reports.

YOUR JOB: Find inventory items with quantity and value, and return the top 10 most valuable as surplus candidates.

FORMAT DETECTION — identify which format this is:
1. SAP MB51 (Material Document List): has columns "Movement type", "Material", "Material Description", "Quantity", "Amt.in Loc.Cur." — GROUP by material, SUM qty and value
2. SAP MB52 (Warehouse Stock): has "Material", "Description", "Unrestricted", "Value" — read directly
3. SAP MB60 (Slow Moving): similar to MB52 with age information
4. Tally: has "Stock Item", "Opening Qty", "Closing Qty", "Rate", "Value" columns
5. Generic inventory list: any columns containing item name, quantity, and value/price
6. Aged stock report: has age buckets (0-30, 30-60, 60-90, 90+ days) — sum the older buckets
7. Unknown format: look for any row with name + number + number pattern

RETURN ONLY a valid JSON array. No markdown, no explanation, no text before [.
Each object:
{
  "title": "full item description with grade/spec if available",
  "category": "RM|BOP|MRO|CE|FG",
  "qty": "number only",
  "unit": "NOS|PCS|MT|KG|LTR|DRM|MTR|SETS|UTS|PC|BOX|BAG",
  "bookValue": "total INR value as integer string",
  "condition": "New — Unused|Used — Good|Used — Fair|Sealed — Unused",
  "ageMonths": "estimated months in storage as number",
  "description": "one sentence: what it is, why it is surplus, any notable specs"
}

CATEGORY RULES:
BOP → bicycle/vehicle parts: tyres, rims, tubes, chains, derailleurs, brakes, saddles, forks, handlebars, carriers, sprockets, bearings, levers
RM → raw materials: steel coils/sheets/tubes, aluminium extrusions, copper, plastics, rubber, chemicals
MRO → maintenance: lubricants, oils, grease, tooling, fasteners, consumables, paint, packaging
CE → high-value electrical/capital: motors, batteries, VFDs, inverters, controllers, chargers, transformers
FG → finished goods: complete bicycles, e-bikes, CKD assemblies, packaged products

Return exactly 10 items (or fewer if less data available). NEVER return [].`,
        content,
        JSON.stringify([
          {title:'HR Steel Coil IS2062 2.5mm',category:'RM',qty:'14.5',unit:'MT',bookValue:'620000',condition:'Used — Good',ageMonths:'26',description:'Hot rolled steel coil IS2062 grade stored under cover, mill certificates available.'},
          {title:'SKF Bearing 6205 ZZ',category:'BOP',qty:'480',unit:'PCS',bookValue:'96000',condition:'New — Boxed',ageMonths:'14',description:'Sealed SKF deep groove bearings in original packaging, within shelf life.'}
        ])
      );

      let items = [];
      try {
        const txt = aiRaw.replace(/```json|```/g,'').trim();
        const m   = txt.match(/\[[\s\S]*\]/);
        items = JSON.parse(m ? m[0] : txt);
        if (!Array.isArray(items)) items = [];
        items = items.map(r => ({
          title:      String(r.title      || 'Unknown Item'),
          category:   ['RM','BOP','MRO','CE','FG'].includes(r.category) ? r.category : 'RM',
          qty:        String(r.qty        || '0'),
          unit:       String(r.unit       || 'PCS'),
          bookValue:  String(r.bookValue  || '0'),
          condition:  String(r.condition  || 'Used — Good'),
          ageMonths:  String(r.ageMonths  || '12'),
          description:String(r.description|| ''),
        }));
      } catch { items = []; }

      if (items.length === 0) {
        if (!CLAUDE_API_KEY || CLAUDE_API_KEY === "YOUR_CLAUDE_API_KEY") {
          log('⚠️ Claude API key not set. Add your key to src/App.js line 13 and redeploy.');
        } else {
          log('⚠️ AI returned no items. The data was sent — check browser Console (F12) for the API response.');
          log('💡 Try: Open your Excel → Save As → CSV → upload the CSV file instead.');
        }
      } else {
        log(`✅ ${items.length} item${items.length>1?'s':''} found — tap one to list`);
      }
      setParsedRows(items);
    } catch (err) {
      log(`❌ AI error: ${err.message}`);
      setParsedRows([]);
    }
    setBusy(false);
  };

  // ─────────────────────────────────────────────────────────────────
  // AI ANALYSIS + SAVE
  // ─────────────────────────────────────────────────────────────────
  const pickRow = row => {
    setForm(p => ({...p, title:row.title, category:row.category, qty:row.qty,
      unit:row.unit, bookValue:row.bookValue, condition:row.condition,
      ageMonths:row.ageMonths, description:row.description}));
    clrLog(); setBusy(true); setStep(2);
    runAI(row, 'upload');
  };

  const analyseManual = () => { clrLog(); setBusy(true); setStep(1); runAI(form, 'manual'); };

  const runAI = async (data, src) => {
    log(`🧠 Analysing "${data.title}"…`);
    log(`📊 Benchmarking ${data.category} market prices…`);
    try {
      const [desc, price] = await Promise.all([
        callAI(
          'Write a 2-sentence B2B industrial surplus listing description for Indian buyers. Mention grade/spec and condition. Return only the description.',
          `Item:${data.title} Cat:${data.category} Cond:${data.condition} Qty:${data.qty}${data.unit} Age:${data.ageMonths}mo`,
          `${data.title}, ${data.condition}. ${data.qty} ${data.unit} available for immediate pickup.`
        ),
        callAI(
          'Return ONLY valid JSON no markdown. Schema: {"scrapFloor":"","liquidationRange":"","resaleMax":"","recommendation":""}. Indian INR.',
          `Price: ${data.title} Cat:${data.category} Cond:${data.condition} Qty:${data.qty}${data.unit}`,
          '{"scrapFloor":"₹25,000/MT","liquidationRange":"₹32,000–40,000/MT","resaleMax":"₹46,000/MT","recommendation":"Price at mid-range for fastest sale."}'
        )
      ]);
      setAiDesc(desc); upd('description', desc);
      try { setPriceBands(JSON.parse(price.replace(/```json|```/g,'').trim())); } catch {}
      log('✅ Description and pricing ready');
    } catch(err) { log(`❌ ${err.message}`); }
    setBusy(false);
    setStep(src === 'upload' ? 3 : 2);
  };

  const save = async () => {
    setSaving(true);
    await DB.add('listings', {
      ...form, images: images.map(i=>i.dataUrl),
      sellerId:user._id, sellerName:user.name, sellerCompany:user.company,
      sellerCity:user.city, sellerState:user.state, sellerRating:4.8,
      status:'active', views:0, enquiryCount:0,
      scrapFloor:priceBands?.scrapFloor||'',
      liquidationRange:priceBands?.liquidationRange||'',
      resaleMax:priceBands?.resaleMax||'',
      createdAt:new Date().toISOString(),
    });
    setSaving(false);
    onSaved();
  };

  const addImages = files => {
    Array.from(files).forEach(f => {
      if (!f.type.startsWith('image/')) return;
      const r = new FileReader();
      r.onload = e => setImages(p => p.length < 5 ? [...p, {name:f.name, dataUrl:e.target.result}] : p);
      r.readAsDataURL(f);
    });
  };

  // ─────────────────────────────────────────────────────────────────
  // SHARED UI
  // ─────────────────────────────────────────────────────────────────
  const renderLogPanel = () => (
    <div style={{background:'linear-gradient(135deg,#0A1628,#1B3A5C)',borderRadius:12,padding:'16px 18px',minHeight:110}}>
      <span style={{background:'rgba(200,168,75,.2)',border:'1px solid rgba(200,168,75,.3)',color:'#C8A84B',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',padding:'2px 8px',borderRadius:6,display:'inline-block',marginBottom:10}}>AI</span>
      {logs.map((l,i)=><div key={i} style={{fontSize:12,color:'rgba(255,255,255,.75)',marginBottom:4}}>{l}</div>)}
      {busy&&<div className="ai-dots"><div className="ald"/><div className="ald"/><div className="ald"/></div>}
    </div>
  );

  // PricingPanel rendered inline (not as sub-component) to prevent focus loss on keystrokes
  const renderPricingPanel = (onBack) => (
    <>
      {priceBands&&(
        <>
          <div className="eye" style={{marginBottom:8}}>AI Price Intelligence</div>
          <div className="pb-row" style={{marginBottom:12}}>
            {[['Scrap Floor',priceBands.scrapFloor,false],['Liquidation ⭐',priceBands.liquidationRange,true],['Resale Max',priceBands.resaleMax,false]].map(([l,v,hi])=>(
              <div key={l} className={`pb ${hi?'hi':''}`}><div className="pb-l">{l}</div><div className="pb-v">{v}</div></div>
            ))}
          </div>
          {priceBands.recommendation&&<SmartTip text={priceBands.recommendation}/>}
        </>
      )}
      <div className="frow" style={{marginTop:14}}>
        <div className="fgrp">
          <label className="flabel">Asking Price <span>*</span></label>
          <input className="finput" placeholder="e.g. 36000"
            value={form.price}
            onChange={e=>setForm(p=>({...p,price:e.target.value}))}/>
        </div>
        <div className="fgrp">
          <label className="flabel">Per Unit</label>
          <select className="fselect" value={form.priceUnit} onChange={e=>setForm(p=>({...p,priceUnit:e.target.value}))}>
            {['/MT','/kg','/pc','/mtr','/ltr','/drm','(total)',''].map(u=><option key={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <div className="fgrp">
        <label className="flabel">Description</label>
        <textarea className="ftextarea" value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
        {aiDesc&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>✨ AI-generated — edit as needed</div>}
      </div>
      {images.length>0&&(
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:'var(--muted)',marginBottom:6}}>Photos ({images.length})</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {images.map((img,i)=><img key={i} src={img.dataUrl} alt="" style={{width:60,height:60,objectFit:'cover',borderRadius:6,border:'1px solid var(--border)'}}/>)}
          </div>
        </div>
      )}
      <div style={{display:'flex',gap:10}}>
        <button className="btn btn-out" style={{flex:1}} onClick={onBack}>← Back</button>
        <button className="btn btn-gold" style={{flex:2}} disabled={!form.price||saving} onClick={save}>
          {saving?'Publishing…':'🚀 Publish Listing'}
        </button>
      </div>
    </>
  );

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="fu">
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
        <button className="btn btn-out btn-sm"
          onClick={mode&&step>0 ? ()=>setStep(s=>s-1) : mode ? ()=>{setMode(null);setStep(0);} : onCancel}>
          ← Back
        </button>
        <div>
          <div className="eye">Create Listing</div>
          <div className="h1">New Surplus Listing</div>
        </div>
      </div>

      {/* ── MODE SELECT ── */}
      {!mode && (
        <div className="card">
          <div style={{textAlign:'center',marginBottom:24}}>
            <div style={{fontSize:32,marginBottom:8}}>📦</div>
            <div className="h2" style={{marginBottom:6}}>How would you like to list?</div>
            <div className="sub">Upload an ERP / Excel file to list multiple items, or add a single item manually.</div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>

            {/* ── UPLOAD CARD — key: input overlays entire card ── */}
            <div style={{position:'relative',border:'2px solid var(--border)',borderRadius:14,padding:'24px 18px',textAlign:'center',cursor:'pointer',transition:'all .2s',overflow:'hidden'}}
              onMouseOver={e=>e.currentTarget.style.borderColor='#C8A84B'}
              onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}>
              {/* Invisible file input covering the entire card */}
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.txt,.tsv"
                style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:0,cursor:'pointer',zIndex:10}}
                onChange={e=>{const f=e.target.files[0];if(f){setMode('upload');handleFile(f);}e.target.value='';}}
              />
              <div style={{position:'absolute',top:10,right:10,background:'rgba(200,168,75,.12)',border:'1px solid rgba(200,168,75,.25)',color:'#C8A84B',fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:10,letterSpacing:.5,textTransform:'uppercase',zIndex:1}}>Recommended</div>
              <div style={{fontSize:40,marginBottom:12}}>📊</div>
              <div style={{fontFamily:"var(--fh)",fontWeight:800,fontSize:15,color:'var(--navy)',marginBottom:8}}>Upload ERP / Excel</div>
              <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.7}}>SAP MB52, Tally, any Excel or CSV. AI maps columns and lists items automatically.</div>
              <div style={{marginTop:12,fontSize:11,color:'var(--green)',fontWeight:600}}>✓ Multiple items · ✓ Any format</div>
            </div>

            {/* Manual card */}
            <div onClick={()=>{setMode('manual');setStep(0);}}
              style={{border:'2px solid var(--border)',borderRadius:14,padding:'24px 18px',cursor:'pointer',textAlign:'center',transition:'all .2s'}}
              onMouseOver={e=>e.currentTarget.style.borderColor='var(--n2)'}
              onMouseOut={e=>e.currentTarget.style.borderColor='var(--border)'}>
              <div style={{fontSize:40,marginBottom:12}}>✏️</div>
              <div style={{fontFamily:"var(--fh)",fontWeight:800,fontSize:15,color:'var(--navy)',marginBottom:8}}>Enter Manually</div>
              <div style={{fontSize:12,color:'var(--muted)',lineHeight:1.7}}>Fill in details and add photos. AI writes the description and prices it for you.</div>
              <div style={{marginTop:12,fontSize:11,color:'var(--blue)',fontWeight:600}}>✓ Add photos · ✓ Full control</div>
            </div>
          </div>
          <div style={{padding:'12px 16px',background:'rgba(200,168,75,.06)',border:'1px solid rgba(200,168,75,.15)',borderRadius:10,fontSize:12,color:'var(--muted)',textAlign:'center'}}>
            💡 Tap the <strong>Upload</strong> card to open your files · Supports SAP, Tally, Excel, CSV
          </div>
        </div>
      )}

      {/* ── UPLOAD FLOW ── */}
      {mode === 'upload' && (
        <div className="card">

          {/* step 1 — log + file info */}
          {step === 1 && (
            <>
              {uploadedFile && (
                <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'var(--off)',borderRadius:10,marginBottom:14,border:'1px solid var(--border)'}}>
                  <span style={{fontSize:20}}>📄</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:'var(--navy)'}}>{uploadedFile.name}</div>
                    <div style={{fontSize:11,color:'var(--muted)'}}>{(uploadedFile.size/1024).toFixed(1)} KB</div>
                  </div>
                  {/* Allow re-upload */}
                  <div style={{marginLeft:'auto',position:'relative'}}>
                    <button className="btn btn-out btn-sm" style={{position:'relative',overflow:'hidden'}}>
                      Change file
                      <input type="file" accept=".xlsx,.xls,.csv,.txt,.tsv"
                        style={{position:'absolute',inset:0,opacity:0,cursor:'pointer',width:'100%',height:'100%'}}
                        onChange={e=>{const f=e.target.files[0];if(f)handleFile(f);e.target.value='';}}/>
                    </button>
                  </div>
                </div>
              )}
              {renderLogPanel()}
              {!busy && parsedRows.length > 0 && (
                <div style={{marginTop:16}}>
                  <div className="h3" style={{marginBottom:4}}>{parsedRows.length} Items Found</div>
                  <div style={{fontSize:12,color:'var(--muted)',marginBottom:12}}>Tap any item to create a listing with AI pricing.</div>
                  {parsedRows.map((row,i)=>(
                    <div key={i} onClick={()=>pickRow(row)}
                      style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',border:'1.5px solid var(--border)',borderRadius:10,marginBottom:8,cursor:'pointer',background:'var(--off)',transition:'all .18s'}}
                      onMouseOver={e=>{e.currentTarget.style.borderColor='#C8A84B';e.currentTarget.style.background='rgba(200,168,75,.04)';}}
                      onMouseOut={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--off)';}}>
                      <div>
                        <div style={{fontWeight:700,fontSize:13,color:'var(--navy)'}}>{row.title}</div>
                        <div style={{fontSize:11,color:'var(--muted)',marginTop:2}}>
                          {row.qty} {row.unit} · {row.condition} · ₹{Number(row.bookValue||0).toLocaleString('en-IN')}
                        </div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <CatBadge cat={row.category}/>
                        <span style={{color:'var(--gold)',fontWeight:700}}>→</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!busy && parsedRows.length === 0 && logs.length > 0 && (
                <div style={{marginTop:14,textAlign:'center'}}>
                  <div style={{fontSize:13,color:'var(--muted)',marginBottom:12}}>
                    Couldn't extract items. Try saving as CSV, or switch to Manual Entry.
                  </div>
                  <button className="btn btn-gold btn-sm" onClick={()=>{setMode('manual');setStep(0);clrLog();}}>
                    Manual Entry →
                  </button>
                </div>
              )}
            </>
          )}

          {/* step 2 — AI analysis running */}
          {step === 2 && renderLogPanel()}

          {/* step 3 — pricing & publish */}
          {step === 3 && renderPricingPanel(()=>setStep(1))}
        </div>
      )}

      {/* ── MANUAL FLOW ── */}
      {mode === 'manual' && (
        <div className="card">
          {step === 0 && (
            <>
              {/* Photos */}
              <div className="fgrp">
                <label className="flabel">Photos <span style={{color:'var(--muted)',fontWeight:400}}>(optional, up to 5)</span></label>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:6}}>
                  {images.map((img,i)=>(
                    <div key={i} style={{position:'relative',width:72,height:72}}>
                      <img src={img.dataUrl} alt="" style={{width:72,height:72,objectFit:'cover',borderRadius:8,border:'1.5px solid var(--border)'}}/>
                      <button onClick={()=>setImages(p=>p.filter((_,j)=>j!==i))}
                        style={{position:'absolute',top:-6,right:-6,width:20,height:20,borderRadius:'50%',background:'var(--red)',border:'none',color:'#fff',fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,lineHeight:1}}>×</button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <div style={{position:'relative',width:72,height:72,border:'2px dashed var(--border)',borderRadius:8,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',background:'var(--off)',cursor:'pointer',overflow:'hidden'}}>
                      <span style={{fontSize:22,color:'var(--muted)',lineHeight:1,pointerEvents:'none'}}>+</span>
                      <span style={{fontSize:9,color:'var(--muted)',marginTop:3,pointerEvents:'none'}}>Photo</span>
                      <input type="file" accept="image/*" multiple
                        style={{position:'absolute',inset:0,opacity:0,cursor:'pointer',width:'100%',height:'100%'}}
                        onChange={e=>{addImages(e.target.files);e.target.value='';}}/>
                    </div>
                  )}
                </div>
              </div>
              <div style={{height:1,background:'var(--border)',margin:'10px 0 14px'}}/>
              <div className="fgrp">
                <label className="flabel">Item / Lot Title <span>*</span></label>
                <input className="finput" placeholder="e.g. HR Steel Coil 2.5mm IS2062 — 15 MT" value={form.title} onChange={e=>upd('title',e.target.value)}/>
              </div>
              <div className="frow">
                <div className="fgrp">
                  <label className="flabel">Category <span>*</span></label>
                  <select className="fselect" value={form.category} onChange={e=>upd('category',e.target.value)}>
                    {[['RM','Raw Materials'],['BOP','Bought-Out Parts'],['MRO','MRO & Consumables'],['CE','Capital Equipment'],['FG','Finished Goods']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="fgrp">
                  <label className="flabel">Condition</label>
                  <select className="fselect" value={form.condition} onChange={e=>upd('condition',e.target.value)}>
                    {['New','Like New','Used — Good','Used — Fair','Damaged','Sealed — Unused'].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="frow">
                <div className="fgrp">
                  <label className="flabel">Quantity <span>*</span></label>
                  <input className="finput" placeholder="e.g. 12" value={form.qty} onChange={e=>upd('qty',e.target.value)}/>
                </div>
                <div className="fgrp">
                  <label className="flabel">Unit</label>
                  <select className="fselect" value={form.unit} onChange={e=>upd('unit',e.target.value)}>
                    {['MT','KG','PCS','NOS','LTR','DRM','MTR','SETS','LOTS'].map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="frow">
                <div className="fgrp">
                  <label className="flabel">Book Value (₹) <span>*</span></label>
                  <input className="finput" placeholder="e.g. 620000" value={form.bookValue} onChange={e=>upd('bookValue',e.target.value)}/>
                </div>
                <div className="fgrp">
                  <label className="flabel">Age in Storage (months)</label>
                  <input className="finput" placeholder="e.g. 18" value={form.ageMonths} onChange={e=>upd('ageMonths',e.target.value)}/>
                </div>
              </div>
              <div className="fgrp">
                <label className="flabel">Location <span>*</span></label>
                <input className="finput" placeholder="e.g. Ludhiana, Punjab" value={form.location} onChange={e=>upd('location',e.target.value)}/>
              </div>
              <button className="btn btn-gold btn-full btn-lg"
                disabled={!form.title||!form.qty||!form.bookValue||!form.location}
                onClick={analyseManual}>
                🤖 Analyse with AI →
              </button>
            </>
          )}
          {step === 1 && renderLogPanel()}
          {step === 2 && renderPricingPanel(()=>setStep(0))}
        </div>
      )}
    </div>
  );
}



// ── SHARE LISTING MODAL ───────────────────────────────────────────────────
function ShareModal({ listing, onClose }) {
  const [copied, setCopied] = useState(false);
  const [shareStep, setShareStep] = useState(null); // 'whatsapp' | 'email'
  const [emailTo, setEmailTo] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Build share content
  const title   = listing.title || 'Industrial Surplus Lot';
  const price   = listing.price ? `₹${Number(listing.price).toLocaleString('en-IN')}${listing.priceUnit||''}` : '';
  const qty     = listing.qty ? `${listing.qty} ${listing.unit}` : '';
  const loc     = listing.location || '';
  const cat     = { RM:'Raw Materials', BOP:'Bought-Out Parts', MRO:'MRO & Consumables', CE:'Capital Equipment', FG:'Finished Goods' }[listing.category] || listing.category;
  const seller  = listing.sellerCompany || '';
  const liqRange= listing.liquidationRange || '';
  const desc    = listing.description || '';

  // Shareable link (in production this would be a real URL)
  const shareUrl = `https://surpliq.com/listing/${listing._id}`;

  // WhatsApp message
  const waText = encodeURIComponent(
`📦 *SURPLUS AVAILABLE — ${title}*

🏭 Seller: ${seller}
📍 Location: ${loc}
⚖️ Quantity: ${qty}
🏷️ Category: ${cat}
💰 Asking Price: ${price}
${liqRange ? `📊 Market Range: ${liqRange}` : ''}

${desc ? `"${desc}"` : ''}

✅ GST-Verified · Inspect before purchase
🔗 View on Surpliq: ${shareUrl}

_Reply to this message to enquire._`
  );

  // Email body
  const emailSubject = encodeURIComponent(`Surplus Available: ${title}`);
  const emailBody = encodeURIComponent(
`Dear Sir/Madam,

We have the following industrial surplus available for immediate sale:

Item: ${title}
Category: ${cat}
Quantity: ${qty}
Location: ${loc}
Asking Price: ${price}
${liqRange ? `Market Range: ${liqRange}` : ''}

${desc}

This lot is available for inspection at the seller's premises. All items are GST-compliant with proper documentation.

View full details: ${shareUrl}

To enquire, please reply to this email or visit the Surpliq listing.

Regards,
${seller}
(via Surpliq — India's B2B Industrial Surplus Marketplace)`
  );

  // LinkedIn post text
  const linkedinText = encodeURIComponent(
`🏭 Industrial Surplus Available

${title}
📍 ${loc} | ⚖️ ${qty} | 💰 ${price}

${desc}

Looking for buyers. GST-verified. Inspection welcome.

#industrialsurplus #manufacturing #b2b #procurement #surpliq`
  );

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }).catch(() => {
      // Fallback for devices without clipboard API
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(decodeURIComponent(text)).catch(() => {});
  };

  const channels = [
    {
      id: 'whatsapp', ico: '💬', label: 'WhatsApp',
      color: '#25D366', textColor: '#fff',
      desc: 'Send to contacts, groups or broadcast list',
      action: () => window.open(`https://wa.me/?text=${waText}`, '_blank'),
    },
    {
      id: 'email', ico: '📧', label: 'Email',
      color: '#1E3A6E', textColor: '#fff',
      desc: 'Send a formal enquiry email to buyers',
      action: () => window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`, '_blank'),
    },
    {
      id: 'linkedin', ico: '💼', label: 'LinkedIn',
      color: '#0077B5', textColor: '#fff',
      desc: 'Post to your professional network',
      action: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank'),
    },
    {
      id: 'sms', ico: '📱', label: 'SMS',
      color: '#6366F1', textColor: '#fff',
      desc: 'Text the listing to a buyer directly',
      action: () => window.open(`sms:?body=${encodeURIComponent(`Surplus available: ${title} | ${qty} | ${price} | ${loc}. View: ${shareUrl}`)}`, '_blank'),
    },
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:999, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
      onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:'#fff', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:520, padding:'24px 20px 32px', boxShadow:'0 -8px 40px rgba(0,0,0,.18)', maxHeight:'92vh', overflowY:'auto' }}>

        {/* Handle */}
        <div style={{ width:36, height:4, background:'#DDE5F0', borderRadius:2, margin:'0 auto 18px' }}/>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:17, color:'var(--navy)' }}>Share Listing</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:3 }}>Reach buyers in your network</div>
          </div>
          <button onClick={onClose} style={{ background:'var(--off)', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:18, color:'var(--muted)', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>

        {/* Listing preview */}
        <div style={{ background:'var(--off)', border:'1.5px solid var(--border)', borderRadius:12, padding:'12px 14px', marginBottom:18 }}>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <div style={{ width:40, height:40, background:'linear-gradient(135deg,#0A1628,#1E3A6E)', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
              {listing.category==='RM'?'🏗️':listing.category==='BOP'?'⚙️':listing.category==='MRO'?'🔧':listing.category==='CE'?'🏭':'📦'}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{title}</div>
              <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{qty} · {loc} · {price}</div>
            </div>
          </div>
        </div>

        {/* Copy link */}
        <div style={{ display:'flex', gap:8, marginBottom:20 }}>
          <div style={{ flex:1, background:'var(--off)', border:'1.5px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:12, color:'var(--muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            🔗 {shareUrl}
          </div>
          <button className="btn btn-gold" style={{ flexShrink:0, minWidth:80 }} onClick={copyLink}>
            {copied ? '✓ Copied!' : 'Copy Link'}
          </button>
        </div>

        {/* Share channels */}
        <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:12 }}>Share via</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
          {channels.map(ch => (
            <button key={ch.id} onClick={ch.action}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'13px 14px', borderRadius:12, border:'none', background:ch.color, color:ch.textColor, cursor:'pointer', textAlign:'left', transition:'opacity .15s' }}
              onMouseOver={e=>e.currentTarget.style.opacity='.88'}
              onMouseOut={e=>e.currentTarget.style.opacity='1'}>
              <span style={{ fontSize:22 }}>{ch.ico}</span>
              <div>
                <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:13 }}>{ch.label}</div>
                <div style={{ fontSize:10, opacity:.75, lineHeight:1.3 }}>{ch.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Copy message text */}
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:16 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', textTransform:'uppercase', letterSpacing:1.5, marginBottom:10 }}>Copy Message Text</div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-out btn-sm" style={{ flex:1 }}
              onClick={() => copyMessage(waText)}>
              📋 WhatsApp Message
            </button>
            <button className="btn btn-out btn-sm" style={{ flex:1 }}
              onClick={() => copyMessage(emailBody)}>
              📋 Email Body
            </button>
          </div>
          <div style={{ fontSize:11, color:'var(--muted)', textAlign:'center', marginTop:10, lineHeight:1.5 }}>
            Paste into any app — Teams, Slack, Signal, Telegram, or your ERP messaging
          </div>
        </div>
      </div>
    </div>
  );
}



// ── SELLER BUYER DISCOVERY (Pro Feature) ─────────────────────────────────
function SellerBuyerDiscovery({ user, isPro }) {
  const [showUpgrade,      setShowUpgrade]      = useState(!isPro);
  const [listings,         setListings]          = useState([]);
  const [discoveredBuyers, setDiscoveredBuyers]  = useState([]);
  const [discLog,          setDiscLog]           = useState([]);
  const [discovering,      setDiscovering]       = useState(false);
  const [discListing,      setDiscListing]       = useState(null);
  const [notified,         setNotified]          = useState({});

  useEffect(() => {
    if (isPro) {
      (async () => {
        const l = await DB.list('listings', [['sellerId','==',user._id]]);
        setListings(l.filter(x => x.status === 'active'));
      })();
    }
  }, [isPro, user._id]);

  const handleUpgrade = async () => {
    // In production: open Razorpay payment here
    // For now: mark user as Pro (admin can also do this from Users panel)
    await DB.update('users', user._id, { plan:'pro', isPro:true, proSince:new Date().toISOString() });
    alert('Pro activated! Please log out and log back in to access Buyer AI.');
    setShowUpgrade(false);
  };

  const runDiscovery = async (listing) => {
    setDiscListing(listing);
    setDiscovering(true);
    setDiscLog([]);
    setDiscoveredBuyers([]);
    setNotified({});
    const loc = listing.location?.split(',')[1]?.trim() || listing.location || 'India';
    setDiscLog(p=>[...p, `🔍 Searching IndiaMART for buyers near ${loc}...`]);
    await new Promise(r=>setTimeout(r,700));
    setDiscLog(p=>[...p, `🌐 Scanning TradeIndia, MRAI Directory...`]);
    await new Promise(r=>setTimeout(r,700));
    setDiscLog(p=>[...p, `🧠 Scoring by category match, proximity, purchase history...`]);
    await new Promise(r=>setTimeout(r,600));
    setDiscLog(p=>[...p, `✅ Cross-checking Surpliq registered buyer database...`]);
    await new Promise(r=>setTimeout(r,500));
    const fallback = [
      {name:'Narayan Steel Traders',type:'Steel Trader',city:'Ludhiana',state:'Punjab',whatTheyBuy:'MS/HR/CR Steel',source:'Surpliq Network',matchScore:96,gstVerified:true,registeredOnSurpliq:true},
      {name:'Punjab Re-Rollers Ltd',type:'Re-Rolling Mill',city:'Mandi Gobindgarh',state:'Punjab',whatTheyBuy:'HR/CR Coils',source:'MRAI Directory',matchScore:91,gstVerified:true,registeredOnSurpliq:false},
      {name:'NCR Industrial Supplies',type:'Distributor',city:'Delhi',state:'Delhi NCT',whatTheyBuy:'Industrial surplus',source:'IndiaMART',matchScore:78,gstVerified:true,registeredOnSurpliq:false},
      {name:'Bharat MRO Hub',type:'MRO Distributor',city:'Mumbai',state:'Maharashtra',whatTheyBuy:'MRO, Bearings, Consumables',source:'TradeIndia',matchScore:74,gstVerified:true,registeredOnSurpliq:false},
    ];
    try {
      const raw = await callAI(
        `You find B2B industrial buyers in India. Return ONLY a valid JSON array.
Each: {"name":"Company","type":"type","city":"city","state":"state","whatTheyBuy":"what","source":"IndiaMART|TradeIndia|MRAI Directory|Surpliq Network","matchScore":85,"gstVerified":true,"registeredOnSurpliq":false}
Generate 6 realistic matched buyers.`,
        `Find buyers for: "${listing.title}" | Category: ${listing.category} | Location: ${listing.location} | Qty: ${listing.qty} ${listing.unit}`,
        JSON.stringify(fallback)
      );
      const cleaned = raw.replace(/```json|```/g,'').trim();
      const match   = cleaned.match(/\[[\s\S]*\]/);
      const buyers  = JSON.parse(match ? match[0] : cleaned);
      setDiscoveredBuyers(Array.isArray(buyers) && buyers.length > 0 ? buyers : fallback);
      setDiscLog(p=>[...p, `✅ ${buyers.length} matched buyers found`]);
    } catch {
      setDiscoveredBuyers(fallback);
      setDiscLog(p=>[...p, `✅ ${fallback.length} buyers found from database`]);
    }
    setDiscovering(false);
  };

  // Show upgrade modal for non-Pro
  if (!isPro || showUpgrade) return (
    <>
      <ProUpgradeModal onClose={()=>{}} onUpgrade={handleUpgrade}/>
      {/* Blurred preview behind modal */}
      <div className="fu" style={{ filter:'blur(3px)', pointerEvents:'none', opacity:.4 }}>
        <div className="eye">AI Buyer Discovery</div>
        <div className="h1">Find Buyers for Your Listings</div>
        <div className="card">
          <div style={{ height:200, background:'var(--off)', borderRadius:10 }}/>
        </div>
      </div>
    </>
  );

  return (
    <div className="fu">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18, flexWrap:'wrap', gap:10 }}>
        <div>
          <div className="eye">AI Buyer Discovery</div>
          <div className="h1">Find Buyers for Your Listings</div>
          <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>
            Powered by IndiaMART, TradeIndia, MRAI & Surpliq network
          </div>
        </div>
        <span style={{ background:'rgba(200,168,75,.15)', color:'var(--gold)', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:20, border:'1px solid rgba(200,168,75,.3)' }}>
          ⭐ Pro Feature
        </span>
      </div>

      {/* Listing selector */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="h3" style={{ marginBottom:10 }}>Select Your Listing</div>
        {listings.length === 0 ? (
          <div style={{ textAlign:'center', padding:'24px 0', color:'var(--muted)' }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📦</div>
            <div style={{ fontSize:13 }}>No active listings. Create a listing first, then run buyer discovery.</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {listings.map(l=>(
              <div key={l._id}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', border:`1.5px solid ${discListing?._id===l._id?'var(--gold)':'var(--border)'}`, borderRadius:9, cursor:'pointer', background:discListing?._id===l._id?'rgba(200,168,75,.05)':'var(--off)', transition:'all .18s' }}
                onClick={()=>runDiscovery(l)}>
                <div>
                  <div style={{ fontWeight:700, fontSize:12, color:'var(--navy)' }}>{l.title}</div>
                  <div style={{ fontSize:10, color:'var(--muted)' }}>{l.qty} {l.unit} · {l.location}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <CatBadge cat={l.category}/>
                  <span style={{ fontSize:12, color:'var(--gold)', fontWeight:700 }}>
                    {discovering && discListing?._id===l._id ? 'Searching…' : 'Search →'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI log */}
      {(discovering || discLog.length > 0) && (
        <div className="ai-box" style={{ marginBottom:14 }}>
          <span className="ai-lbl">AI BUYER DISCOVERY</span>
          {discLog.map((l,i)=><div key={i} style={{ fontSize:12, color:'rgba(255,255,255,.7)', marginBottom:4 }}>{l}</div>)}
          {discovering && <div className="ai-dots"><div className="ald"/><div className="ald"/><div className="ald"/></div>}
        </div>
      )}

      {/* Results */}
      {discoveredBuyers.length > 0 && (
        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div className="h3">{discoveredBuyers.length} Buyers Found for "{discListing?.title?.slice(0,30)}"</div>
            <button className="btn btn-out btn-sm" onClick={()=>{setDiscoveredBuyers([]);setDiscLog([]);setDiscListing(null);setNotified({});}}>Clear</button>
          </div>
          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:14 }}>
            🟢 Registered on Surpliq · ✅ GST Verified · Tap Notify to send personalised outreach
          </div>
          {discoveredBuyers.map((b,i) => {
            const shareUrl  = `https://surpliq.com/listing/${discListing?._id||'demo'}`;
            const waMsgLines = [
              'Hi ' + b.name + ',',
              '',
              'We have surplus stock matching your purchase profile:',
              '',
              '📦 *' + (discListing?.title||'') + '*',
              '⚖️ Qty: ' + (discListing?.qty||'') + ' ' + (discListing?.unit||''),
              '📍 Location: ' + (discListing?.location||''),
              '💰 Asking: ₹' + (discListing?.price||'') + (discListing?.priceUnit||''),
              '✅ GST-verified · Inspection welcome',
              '',
              b.registeredOnSurpliq
                ? 'View & make an offer: ' + shareUrl
                : 'Register free on Surpliq to make an offer:\nhttps://surpliq.com/register',
              '',
              '— ' + user.company + ' via Surpliq',
            ];
            const waMsg = encodeURIComponent(waMsgLines.join('\n'));
            return (
              <div key={i} style={{ borderBottom:'1px solid var(--border)', paddingBottom:14, marginBottom:14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: notified[i]==='shown'?10:0 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                      <span style={{ fontWeight:800, fontSize:13, color:'var(--navy)' }}>{b.name}</span>
                      {b.gstVerified && <span title="GST Verified">✅</span>}
                      {b.registeredOnSurpliq && <span style={{ background:'#dcfce7', color:'#15803d', fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8 }}>ON SURPLIQ</span>}
                    </div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>{b.type} · {b.city}, {b.state} · {b.source}</div>
                    <div style={{ fontSize:11, color:'var(--muted)' }}>🛒 {b.whatTheyBuy}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                    <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:18, color:b.matchScore>=90?'var(--green)':b.matchScore>=75?'var(--amber)':'var(--muted)', marginBottom:6 }}>{b.matchScore}%</div>
                    {notified[i] !== 'shown' && notified[i] !== 'sent' ? (
                      <button className="btn btn-gold btn-sm" onClick={()=>setNotified(p=>({...p,[i]:'shown'}))}>📣 Notify →</button>
                    ) : notified[i]==='sent' ? (
                      <span style={{ fontSize:11, fontWeight:700, color:'var(--green)' }}>✓ Notified</span>
                    ) : (
                      <span style={{ fontSize:11, fontWeight:700, color:'var(--muted)' }}>↓ Options below</span>
                    )}
                  </div>
                </div>
                {notified[i]==='shown' && (
                  <div style={{ background:'rgba(200,168,75,.06)', border:'1px solid rgba(200,168,75,.2)', borderRadius:10, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10 }}>
                      {b.registeredOnSurpliq ? '🟢 Registered — send direct listing link' : '📤 Not on Surpliq — send listing + invite'}
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      <button className="btn btn-sm" style={{ background:'#25D366', color:'#fff', border:'none', flex:1 }}
                        onClick={()=>{ window.open(`https://wa.me/?text=${waMsg}`,'_blank'); setNotified(p=>({...p,[i]:'sent'})); }}>
                        💬 WhatsApp
                      </button>
                      <button className="btn btn-out btn-sm" style={{ flex:1 }}
                        onClick={()=>{ navigator.clipboard?.writeText(decodeURIComponent(waMsg)).catch(()=>{}); setNotified(p=>({...p,[i]:'sent'})); window.showToast?.('Message copied!'); }}>
                        📋 Copy Message
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── SELLER LISTINGS LIST ──────────────────────────────────────────────────
function SellerListings({ user, onEnquiry }) {
  const [listings,       setListings]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [toast,          setToast]          = useState(null);
  const [sharingListing, setSharingListing] = useState(null);
  // Search & filter state
  const [search,   setSearch]   = useState('');
  const [catFilter,setCatFilter]= useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy,   setSortBy]   = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const l = await DB.list("listings", [["sellerId","==",user._id]]);
    setListings(l); setLoading(false);
  }, [user._id]);

  useEffect(() => { load(); }, [load]);

  const markSold = async (id) => {
    await DB.update("listings", id, { status:"sold" });
    showToast("Listing marked as sold ✓");
    load();
  };

  // ── Filter + sort ───────────────────────────────────────────────
  const filtered = listings
    .filter(l => {
      const q = search.toLowerCase();
      if (q && ![(l.title||''),(l.location||''),(l.category||'')].some(v=>v.toLowerCase().includes(q))) return false;
      if (catFilter !== 'all' && l.category !== catFilter) return false;
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      return true;
    })
    .sort((a,b) => {
      if (sortBy === 'newest')     return new Date(b.createdAt||0) - new Date(a.createdAt||0);
      if (sortBy === 'oldest')     return new Date(a.createdAt||0) - new Date(b.createdAt||0);
      if (sortBy === 'price_high') return Number(b.price||0) - Number(a.price||0);
      if (sortBy === 'price_low')  return Number(a.price||0) - Number(b.price||0);
      if (sortBy === 'views')      return (b.views||0) - (a.views||0);
      if (sortBy === 'enquiries')  return (b.enquiryCount||0) - (a.enquiryCount||0);
      return 0;
    });

  const hasFilters = search || catFilter !== 'all' || statusFilter !== 'all';
  const clearAll = () => { setSearch(''); setCatFilter('all'); setStatusFilter('all'); };

  // ── Summary stats ───────────────────────────────────────────────
  const stats = {
    total:  listings.length,
    active: listings.filter(l=>l.status==='active').length,
    sold:   listings.filter(l=>l.status==='sold').length,
    views:  listings.reduce((s,l)=>s+(l.views||0),0),
    enqs:   listings.reduce((s,l)=>s+(l.enquiryCount||0),0),
  };

  if (loading) return (
    <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
      <div className="ai-dots" style={{ justifyContent:'center', marginBottom:8 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>
      Loading listings…
    </div>
  );

  return (
    <div className="fu">
      {sharingListing && <ShareModal listing={sharingListing} onClose={()=>setSharingListing(null)}/>}
      {toast && <Toast msg={toast.msg} type={toast.type}/>}

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div>
          <div className="eye">My Listings</div>
          <div className="h1">{listings.length} Lots Published</div>
        </div>
      </div>

      {/* Mini stats row */}
      {listings.length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
          {[
            { label:'Active',    val:stats.active,  col:'var(--green)' },
            { label:'Sold',      val:stats.sold,    col:'var(--blue)'  },
            { label:'Total Views',val:stats.views,  col:'var(--navy)'  },
            { label:'Enquiries', val:stats.enqs,    col:'var(--gold)'  },
          ].map(s=>(
            <div key={s.label} style={{ background:'#fff', border:'1.5px solid var(--border)', borderRadius:10, padding:'8px 14px', display:'flex', alignItems:'center', gap:8, flex:'1 1 80px' }}>
              <span style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:16, color:s.col }}>{s.val}</span>
              <span style={{ fontSize:11, color:'var(--muted)', fontWeight:500 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div style={{ display:'flex', gap:8, marginBottom:8 }}>
        <div style={{ flex:1, position:'relative' }}>
          <span style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'var(--muted)' }}>🔍</span>
          <input className="finput" style={{ paddingLeft:34 }}
            placeholder="Search title, location, category…"
            value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <button className="btn btn-sm"
          style={{ background:showFilters?'var(--navy)':'#fff', color:showFilters?'#fff':'var(--muted)', border:'1.5px solid var(--border)', position:'relative', flexShrink:0 }}
          onClick={()=>setShowFilters(p=>!p)}>
          ⚙️ Filters
          {hasFilters && <span style={{ position:'absolute', top:-3, right:-3, width:8, height:8, borderRadius:'50%', background:'var(--gold)', border:'2px solid #f4f6fa' }}/>}
        </button>
      </div>

      {/* Category pills */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
        {['all','RM','BOP','MRO','CE','FG'].map(cat=>(
          <button key={cat} onClick={()=>setCatFilter(cat)}
            style={{ padding:'5px 12px', border:`1.5px solid ${catFilter===cat?'var(--navy)':'var(--border)'}`,
              borderRadius:20, background:catFilter===cat?'var(--navy)':'#fff',
              color:catFilter===cat?'#fff':'var(--muted)', fontSize:11, fontWeight:700, cursor:'pointer' }}>
            {cat==='all'?'All':cat}
          </button>
        ))}
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div style={{ background:'#fff', border:'1.5px solid var(--border)', borderRadius:12, padding:'14px 16px', marginBottom:10 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Status</label>
              <select className="fselect" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="active">● Active</option>
                <option value="sold">✓ Sold</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Sort By</label>
              <select className="fselect" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_high">Price: High → Low</option>
                <option value="price_low">Price: Low → High</option>
                <option value="views">Most Views</option>
                <option value="enquiries">Most Enquiries</option>
              </select>
            </div>
          </div>
          {hasFilters && (
            <button className="btn btn-out btn-sm" style={{ marginTop:10, width:'100%' }} onClick={clearAll}>
              ✕ Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Active filter chips */}
      {hasFilters && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
          {search && <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>
            🔍 "{search}" <span style={{ cursor:'pointer', marginLeft:4 }} onClick={()=>setSearch('')}>✕</span>
          </span>}
          {catFilter!=='all' && <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>
            Category: {catFilter} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={()=>setCatFilter('all')}>✕</span>
          </span>}
          {statusFilter!=='all' && <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>
            Status: {statusFilter} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={()=>setStatusFilter('all')}>✕</span>
          </span>}
        </div>
      )}

      {/* Results count */}
      <div style={{ fontSize:12, color:'var(--muted)', marginBottom:12 }}>
        {filtered.length === listings.length
          ? `${listings.length} listing${listings.length!==1?'s':''}`
          : `${filtered.length} of ${listings.length} listings`}
        {sortBy!=='newest' && <span style={{ marginLeft:6, color:'var(--gold)', fontWeight:600 }}>· sorted by {sortBy.replace('_',' ')}</span>}
      </div>

      {/* Empty states */}
      {listings.length === 0 && (
        <div className="card" style={{ textAlign:'center', padding:40, color:'var(--muted)' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📦</div>
          <div style={{ fontWeight:600, marginBottom:4 }}>No listings yet</div>
          <div style={{ fontSize:12 }}>Create your first listing to get started</div>
        </div>
      )}
      {listings.length > 0 && filtered.length === 0 && (
        <div className="card" style={{ textAlign:'center', padding:32, color:'var(--muted)' }}>
          <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
          <div style={{ fontWeight:600, marginBottom:8 }}>No listings match your filters</div>
          <button className="btn btn-gold btn-sm" onClick={clearAll}>Clear Filters</button>
        </div>
      )}

      {/* Listing cards */}
      {filtered.map(l => (
        <div className="card" key={l._id} style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
            <div style={{ display:'flex', gap:12, alignItems:'flex-start', flex:1, minWidth:0 }}>
              <div style={{ width:44, height:44, background:'#F0F4FF', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                {l.category==='RM'?'🏗️':l.category==='BOP'?'⚙️':l.category==='MRO'?'🔧':l.category==='CE'?'🏭':'📦'}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <CatBadge cat={l.category}/>
                <div className="h3" style={{ marginTop:4, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{l.title}</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:3 }}>
                  📍 {l.location} · ⚖️ {l.qty} {l.unit} · 🕐 {l.ageMonths||'—'} mo
                </div>
                <div style={{ fontSize:11, color:'var(--muted)' }}>
                  📒 Book: ₹{Number(l.bookValue||0).toLocaleString('en-IN')}
                </div>
              </div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0 }}>
              <span className={`pill ${l.status==='active'?'pill-green':l.status==='sold'?'pill-blue':'pill-navy'}`}>
                {l.status==='active'?'● Active':l.status==='sold'?'✓ Sold':'Draft'}
              </span>
              <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:15, marginTop:6, color:'var(--navy)' }}>
                ₹{Number(l.price||0).toLocaleString('en-IN')}{l.priceUnit}
              </div>
            </div>
          </div>

          {/* Analytics mini row */}
          <div style={{ display:'flex', gap:16, margin:'10px 0', padding:'8px 0', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--muted)' }}>
            <span>👁 <strong style={{ color:'var(--navy)' }}>{l.views||0}</strong> views</span>
            <span>💬 <strong style={{ color: (l.enquiryCount||0)>0?'var(--gold)':'var(--navy)' }}>{l.enquiryCount||0}</strong> enquiries</span>
            {l.createdAt && <span>📅 {new Date(l.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</span>}
            {(l.enquiryCount||0) > 0 && <span style={{ color:'var(--green)', fontWeight:700 }}>🔥 Active interest</span>}
          </div>

          {/* Actions */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
            {l.status==='active' && (
              <button className="btn btn-sm" style={{ background:'#dcfce7', color:'#15803d', border:'1px solid #bbf7d0', fontSize:11 }}
                onClick={()=>markSold(l._id)}>
                ✓ Mark Sold
              </button>
            )}
            <button className="btn btn-out btn-sm" onClick={()=>onEnquiry(l._id)}
              style={{ position:'relative' }}>
              💬 Enquiries
              {(l.enquiryCount||0)>0 && <span style={{ position:'absolute', top:-5, right:-5, background:'var(--gold)', color:'#fff', borderRadius:'50%', width:16, height:16, fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{l.enquiryCount}</span>}
            </button>
            {l.status==='active' && (
              <button className="btn btn-gold btn-sm" onClick={()=>setSharingListing(l)}>📤 Share</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SELLER ENQUIRIES ──────────────────────────────────────────────────────
function SellerEnquiries({ user, listingId, onDealClosed, onSellerResponded }) {
  const ww = useWindowWidth(); const mob = ww < 700;
  const [enquiries,    setEnquiries]    = useState([]);
  const [sel,          setSel]          = useState(null);
  const [replyText,    setReplyText]    = useState('');
  const [offerPrice,   setOfferPrice]   = useState('');
  const [offerQty,     setOfferQty]     = useState('');
  const [sending,      setSending]      = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading,    setAiLoading]    = useState(false);
  const [showDeal,     setShowDeal]     = useState(false);
  const [accepting,    setAccepting]    = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(async () => {
    const e = await DB.list('enquiries', [['sellerId','==',user._id]]);
    const filtered = listingId ? e.filter(x=>x.listingId===listingId) : e;
    setEnquiries(filtered.sort((a,b)=>new Date(b.updatedAt||0)-new Date(a.updatedAt||0)));
    setLoading(false);
  }, [user._id, listingId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [sel, enquiries]);

  const enq = sel ? enquiries.find(e=>e._id===sel) : null;

  const sendMessage = async (msgObj) => {
    if (!enq) return;
    setSending(true);
    const newMsg   = { ...msgObj, time:new Date().toISOString() };
    const thread   = [...(enq.thread||[]), newMsg];
    const newStatus = msgObj.isQuote && msgObj.quote?.status==='accepted' ? 'accepted' : 'negotiating';
    await DB.update('enquiries', enq._id, { thread, status:newStatus, updatedAt:new Date().toISOString() });
    setEnquiries(p => p.map(e => e._id===enq._id ? {...e, thread, status:newStatus} : e));
    setSending(false);
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    await sendMessage({ from:'seller', text:replyText });
    setReplyText('');
    if (onSellerResponded) onSellerResponded(enq, replyText);
  };

  const sendCounterWithNotif = async () => {
    await sendCounter();
    if (onSellerResponded) onSellerResponded(enq, `Counter offer: ${offerPrice}`);
  };

  const sendCounter = async () => {
    if (!offerPrice.trim()) return;
    await sendMessage({ from:'seller', isQuote:true, quote:{
      price:offerPrice, qty:offerQty||'As available',
      total:'Negotiable', validity:'24 hrs',
      status:'counter', label:'Seller Counter-Offer'
    }});
    setOfferPrice(''); setOfferQty('');
  };

  const acceptOffer = async () => {
    const lastBuyerQuote = [...(enq?.thread||[])].reverse().find(m=>m.isQuote&&m.from==='buyer');
    if (!lastBuyerQuote) return;
    setAccepting(true);

    const dealMsg = { from:'seller', isQuote:true, time:new Date().toISOString(),
      quote:{ ...lastBuyerQuote.quote, status:'accepted', label:'Deal Agreed ✓', validity:'Accepted' }
    };
    const sysMsg = { from:'system', time:new Date().toISOString(),
      text:`🎉 Deal closed between ${user.company} and ${enq.buyerCompany}. Contact details have been shared with both parties. Platform fee: 3% of agreed value.`
    };
    const thread = [...(enq.thread||[]), dealMsg, sysMsg];

    await DB.update('enquiries', enq._id, {
      thread, status:'accepted', updatedAt:new Date().toISOString(),
      dealPrice: lastBuyerQuote.quote.price,
      dealQty:   lastBuyerQuote.quote.qty,
      closedAt:  new Date().toISOString(),
      sellerContact: { name:user.name, phone:user.phone||'+91 98760 00001', email:user.email },
    });

    await DB.add('deals', {
      enquiryId:    enq._id,
      listingId:    enq.listingId,
      listingTitle: enq.listingTitle,
      buyerId:      enq.buyerId,
      buyerName:    enq.buyerName,
      buyerCompany: enq.buyerCompany,
      buyerCity:    enq.buyerCity,
      sellerId:     enq.sellerId,
      sellerName:   user.name,
      sellerCompany:user.company,
      sellerCity:   user.city,
      agreedPrice:  lastBuyerQuote.quote.price,
      agreedQty:    lastBuyerQuote.quote.qty,
      platformFee:  '3%',
      status:       'active',
      createdAt:    new Date().toISOString(),
    });

    setEnquiries(p => p.map(e => e._id===enq._id
      ? {...e, thread, status:'accepted', dealPrice:lastBuyerQuote.quote.price, dealQty:lastBuyerQuote.quote.qty, closedAt:new Date().toISOString()}
      : e
    ));
    setAccepting(false);
    setShowDeal(true);
    // Trigger notification for buyer via parent callback
    if (onDealClosed) onDealClosed(enq);
  };

  const getAISuggestion = async () => {
    if (!enq) return;
    setAiLoading(true);
    const lastOffer = [...(enq.thread||[])].reverse().find(m=>m.isQuote)?.quote;
    const suggestion = await callAI(
      'You are a negotiation AI for B2B industrial surplus deals in India. Give a 1-sentence counter-offer suggestion with a specific price.',
      `Listing: ${enq.listingTitle}. Buyer offer: ${lastOffer?.price}. Qty: ${lastOffer?.qty}.`,
      'Counter at 5% above the buyer offer — this typically closes deals within one more round.'
    );
    setAiSuggestion(suggestion); setAiLoading(false);
  };

  if (loading) return (
    <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
      <div className="ai-dots" style={{ justifyContent:'center', marginBottom:8 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>
      Loading enquiries…
    </div>
  );

  // Show deal summary overlay
  if (showDeal && enq) return (
    <DealSummary enquiry={enq} user={user} role="seller" onBack={()=>setShowDeal(false)}/>
  );

  return (
    <div className="fu" style={{ display:'grid', gridTemplateColumns:mob?'1fr':(sel?'280px 1fr':'1fr'), gap:16 }}>

      {/* ── ENQUIRY LIST ── */}
      <div>
        <div className="eye">Enquiries & Negotiations</div>
        <div className="h1" style={{ marginBottom:16 }}>{enquiries.length} Conversation{enquiries.length!==1?'s':''}</div>

        {enquiries.length === 0 && (
          <div className="card" style={{ textAlign:'center', padding:32, color:'var(--muted)' }}>
            <div style={{ fontSize:36, marginBottom:10 }}>💬</div>
            <div style={{ fontWeight:600 }}>No enquiries yet</div>
            <div style={{ fontSize:12, marginTop:4 }}>Your listing is live — buyers will reach out soon.</div>
          </div>
        )}

        {enquiries.map(e => {
          const lastMsg = (e.thread||[]).slice(-1)[0];
          const isNew   = e.status === 'new';
          return (
            <div key={e._id}
              style={{ background:'#fff', border:`2px solid ${sel===e._id?'var(--navy)':'var(--border)'}`, borderRadius:12, padding:'12px 14px', marginBottom:8, cursor:'pointer', transition:'all .18s' }}
              onClick={()=>{ setSel(e._id); setShowDeal(false); }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {isNew && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--gold)', flexShrink:0, display:'inline-block' }}/>}
                  <span style={{ fontWeight:800, fontSize:13, color:'var(--navy)' }}>{e.buyerCompany||'Buyer'}</span>
                </div>
                <span className={`pill ${e.status==='accepted'?'pill-green':e.status==='negotiating'?'pill-amber':'pill-navy'}`} style={{ fontSize:10 }}>
                  {e.status==='accepted'?'✓ Closed':e.status==='negotiating'?'🔄 Active':'New'}
                </span>
              </div>
              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:3 }}>📍 {e.buyerCity}</div>
              <div style={{ fontSize:11, color:'var(--muted)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{e.listingTitle}</div>
              {lastMsg && (
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:4, fontStyle:'italic', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                  {lastMsg.from==='system'?'🔔':lastMsg.from==='seller'?'You:':'Buyer:'} {lastMsg.text?.slice(0,45)||'Quote sent'}…
                </div>
              )}
              {e.status==='accepted' && (
                <button className="btn btn-gold btn-sm" style={{ marginTop:8, width:'100%' }}
                  onClick={ev=>{ev.stopPropagation();setSel(e._id);setShowDeal(true);}}>
                  📄 View Deal Summary
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── CHAT PANEL ── */}
      {sel && enq && (
        <div className="card" style={{ display:'flex', flexDirection:'column', maxHeight:'calc(100vh - 120px)', minHeight:400 }}>

          {/* Chat header */}
          <div style={{ paddingBottom:12, borderBottom:'1px solid var(--border)', marginBottom:12 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div className="h3" style={{ marginBottom:2 }}>{enq.buyerCompany}</div>
                <div style={{ fontSize:11, color:'var(--muted)' }}>📍 {enq.buyerCity} · ✅ GST Verified · ⭐ {enq.buyerRating||4.2}/5</div>
                <div style={{ fontSize:11, color:'var(--muted)', marginTop:1 }}>📦 {enq.listingTitle}</div>
              </div>
              <span className={`pill ${enq.status==='accepted'?'pill-green':enq.status==='negotiating'?'pill-amber':'pill-navy'}`}>
                {enq.status==='accepted'?'✓ Deal Closed':enq.status==='negotiating'?'🔄 Negotiating':'💬 New'}
              </span>
            </div>
          </div>

          {/* Thread */}
          <div ref={threadRef} style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:10, paddingBottom:8 }}>
            {(enq.thread||[]).map((msg,i) => (
              <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:msg.from==='system'?'center':msg.from==='seller'?'flex-end':'flex-start' }}>
                {msg.from==='system' ? (
                  <div style={{ background:'rgba(200,168,75,.1)', border:'1px solid rgba(200,168,75,.2)', borderRadius:10, padding:'8px 14px', fontSize:11, color:'var(--navy)', textAlign:'center', maxWidth:'90%', fontWeight:500 }}>
                    {msg.text}
                  </div>
                ) : !msg.isQuote ? (
                  <div className={`msg ${msg.from}`}>{msg.text}</div>
                ) : (
                  <div className="quote-card" style={{ alignSelf:msg.from==='seller'?'flex-end':'flex-start' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:'var(--muted)', textTransform:'uppercase' }}>{msg.quote.label}</span>
                      <span className={`pill ${msg.quote.status==='accepted'?'pill-green':msg.quote.status==='counter'?'pill-blue':'pill-amber'}`} style={{ fontSize:10 }}>
                        {msg.quote.status==='accepted'?'✓ Accepted':msg.quote.status==='counter'?'Counter':'Pending'}
                      </span>
                    </div>
                    {[['Price',msg.quote.price],['Quantity',msg.quote.qty],['Total',msg.quote.total],['Valid',msg.quote.validity]].map(([k,v])=>(
                      <SpecRow key={k} label={k} value={v}/>
                    ))}
                    {msg.quote.status==='pending' && msg.from==='buyer' && enq.status!=='accepted' && (
                      <div style={{ display:'flex', gap:8, marginTop:10 }}>
                        <button className="btn btn-green btn-sm" style={{ flex:1 }} disabled={accepting} onClick={acceptOffer}>
                          {accepting?'Closing…':'✓ Accept & Close Deal'}
                        </button>
                        <button className="btn btn-out btn-sm" onClick={getAISuggestion} disabled={aiLoading}>
                          🤖 AI Counter
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* AI suggestion */}
          {(aiLoading || aiSuggestion) && (
            <div className="ai-box" style={{ marginBottom:10, padding:'10px 14px' }}>
              <span className="ai-lbl" style={{ marginBottom:6, display:'block' }}>AI NEGOTIATION ADVISOR</span>
              {aiLoading
                ? <div className="ai-dots"><div className="ald"/><div className="ald"/><div className="ald"/></div>
                : <div style={{ fontSize:12, color:'rgba(255,255,255,.8)' }}>{aiSuggestion}</div>
              }
            </div>
          )}

          {/* Reply / counter input */}
          {enq.status !== 'accepted' ? (
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:12 }}>
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <input className="finput" placeholder="Type a reply…" value={replyText}
                  onChange={e=>setReplyText(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendReply()}
                  style={{ flex:1 }}/>
                <button className="btn btn-navy" onClick={sendReply} disabled={sending||!replyText.trim()}>Send</button>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input className="finput" placeholder="Counter price e.g. ₹36,500/MT" value={offerPrice}
                  onChange={e=>setOfferPrice(e.target.value)} style={{ flex:1, fontSize:12 }}/>
                <input className="finput" placeholder="Qty e.g. 15 MT" value={offerQty}
                  onChange={e=>setOfferQty(e.target.value)} style={{ flex:1, fontSize:12 }}/>
                <button className="btn btn-gold btn-sm" onClick={sendCounterWithNotif} disabled={sending||!offerPrice.trim()}>
                  Counter
                </button>
              </div>
            </div>
          ) : (
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:14, textAlign:'center' }}>
              <div style={{ fontWeight:800, color:'var(--green)', marginBottom:10, fontSize:15 }}>🎉 Deal Closed!</div>
              <button className="btn btn-gold btn-full" onClick={()=>setShowDeal(true)}>
                📄 Open Deal Summary & Contacts →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── DEAL SUMMARY ──────────────────────────────────────────────────────────
function DealSummary({ enquiry: enq, user, role, onBack }) {
  const [printed, setPrinted] = useState(false);
  const dealNo = `SQ-${new Date(enq.closedAt||enq.updatedAt||Date.now()).getFullYear()}-${String(enq._id||'').slice(-6).toUpperCase()}`;
  const agreedPrice = enq.dealPrice || enq.thread?.filter(m=>m.isQuote&&m.quote?.status==='accepted').slice(-1)[0]?.quote?.price || '—';
  const agreedQty   = enq.dealQty   || enq.thread?.filter(m=>m.isQuote&&m.quote?.status==='accepted').slice(-1)[0]?.quote?.qty   || '—';
  const closedDate  = enq.closedAt ? new Date(enq.closedAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});

  // Contacts to exchange
  const sellerContact = {
    name:    enq.sellerName    || enq.sellerCompany || '—',
    company: enq.sellerCompany || '—',
    email:   role==='seller' ? user.email : `Contact via Surpliq`,
    phone:   role==='seller' ? (user.phone||'Shared on closure') : (enq.sellerContact?.phone||'+91 XXXXX XXXXX'),
    city:    enq.sellerCity    || '—',
  };
  const buyerContact = {
    name:    enq.buyerName    || '—',
    company: enq.buyerCompany || '—',
    email:   role==='buyer' ? user.email : `Contact via Surpliq`,
    phone:   role==='buyer' ? (user.phone||'Shared on closure') : (enq.buyerContact?.phone||'+91 XXXXX XXXXX'),
    city:    enq.buyerCity    || '—',
  };

  const printDeal = () => {
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Deal Summary ${dealNo}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;color:#1a1a2e;padding:32px;max-width:700px;margin:0 auto;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #C8A84B;}
  .logo{font-size:22px;font-weight:900;letter-spacing:-1px;}
  .logo span{color:#C8A84B;}
  .deal-no{text-align:right;font-size:12px;color:#666;}
  .deal-no strong{font-size:16px;color:#1a1a2e;display:block;}
  h2{font-size:14px;font-weight:800;color:#1a1a2e;margin:20px 0 10px;text-transform:uppercase;letter-spacing:.5px;border-left:4px solid #C8A84B;padding-left:8px;}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;}
  .row{display:flex;justify-content:space-between;padding:7px 10px;background:#f8f9fa;border-radius:4px;font-size:13px;}
  .row label{color:#666;font-weight:600;}
  .row value{font-weight:700;}
  .contact-box{border:1px solid #DDE5F0;border-radius:8px;padding:14px;margin-bottom:8px;}
  .contact-box h3{font-size:13px;font-weight:800;margin-bottom:8px;color:#1a1a2e;}
  .contact-row{font-size:12px;color:#444;margin-bottom:4px;}
  .status{display:inline-block;background:#dcfce7;color:#15803d;padding:4px 12px;border-radius:20px;font-weight:800;font-size:12px;margin-bottom:16px;}
  .fee-box{background:#FFF9E6;border:1px solid #C8A84B;border-radius:8px;padding:14px;margin:16px 0;}
  .footer{margin-top:32px;padding-top:16px;border-top:1px solid #DDE5F0;font-size:10px;color:#999;text-align:center;}
  .terms{font-size:11px;color:#666;line-height:1.6;margin-top:12px;}
  @media print{body{padding:16px;}}
</style></head><body>
<div class="header">
  <div class="logo"><span>Surp</span>liq</div>
  <div class="deal-no">Deal Reference<strong>${dealNo}</strong>Issued: ${closedDate}</div>
</div>
<span class="status">✓ DEAL CLOSED</span>
<h2>Deal Terms</h2>
<div class="grid">
  <div class="row"><label>Listing</label><value>${enq.listingTitle||'—'}</value></div>
  <div class="row"><label>Agreed Price</label><value>${agreedPrice}</value></div>
  <div class="row"><label>Quantity</label><value>${agreedQty}</value></div>
  <div class="row"><label>Deal Date</label><value>${closedDate}</value></div>
  <div class="row"><label>Seller Location</label><value>${sellerContact.city}</value></div>
  <div class="row"><label>Status</label><value>Closed – Pending Pickup</value></div>
</div>
<h2>Parties</h2>
<div class="grid">
  <div class="contact-box">
    <h3>Seller</h3>
    <div class="contact-row"><strong>${sellerContact.company}</strong></div>
    <div class="contact-row">Contact: ${sellerContact.name}</div>
    <div class="contact-row">📍 ${sellerContact.city}</div>
    <div class="contact-row">📧 ${sellerContact.email}</div>
    <div class="contact-row">📱 ${sellerContact.phone}</div>
  </div>
  <div class="contact-box">
    <h3>Buyer</h3>
    <div class="contact-row"><strong>${buyerContact.company}</strong></div>
    <div class="contact-row">Contact: ${buyerContact.name}</div>
    <div class="contact-row">📍 ${buyerContact.city}</div>
    <div class="contact-row">📧 ${buyerContact.email}</div>
    <div class="contact-row">📱 ${buyerContact.phone}</div>
  </div>
</div>
<div class="fee-box">
  <strong>Platform Fee:</strong> 3% of agreed deal value, payable by seller upon deal closure.<br/>
  <strong>Invoice ref:</strong> ${dealNo} · <strong>Platform:</strong> Surpliq Technologies Pvt. Ltd.
</div>
<h2>Next Steps</h2>
<div class="terms">
1. Both parties coordinate inspection and pickup logistics directly using the contact details above.<br/>
2. Seller to issue GST invoice to buyer at time of delivery/pickup.<br/>
3. Payment terms as mutually agreed — Surpliq recommends advance or escrow for first-time transactions.<br/>
4. Surpliq platform fee invoice will be sent to the seller separately within 2 business days.<br/>
5. For disputes, contact support@surpliq.com — Surpliq will mediate in good faith.
</div>
<div class="footer">
  This document is generated by Surpliq Technologies Pvt. Ltd. · support@surpliq.com<br/>
  Surpliq is a technology marketplace and is not a party to this transaction.<br/>
  © ${new Date().getFullYear()} Surpliq Technologies Pvt. Ltd. · All rights reserved.
</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(()=>{ w.print(); setPrinted(true); }, 500);
  };

  return (
    <div className="fu">
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button className="btn btn-out btn-sm" onClick={onBack}>← Back to Enquiries</button>
        <div>
          <div className="eye">Deal Summary</div>
          <div className="h1" style={{ fontSize:18 }}>{dealNo}</div>
        </div>
        <button className="btn btn-gold" style={{ marginLeft:'auto' }} onClick={printDeal}>
          🖨️ Print / Save PDF
        </button>
      </div>

      {/* Success banner */}
      <div style={{ background:'linear-gradient(135deg,#14532d,#166534)', borderRadius:16, padding:'18px 22px', marginBottom:20, display:'flex', alignItems:'center', gap:16 }}>
        <div style={{ fontSize:40 }}>🎉</div>
        <div>
          <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:18, color:'#fff', marginBottom:3 }}>Deal Closed Successfully!</div>
          <div style={{ fontSize:13, color:'rgba(255,255,255,.7)' }}>
            {enq.sellerCompany} × {enq.buyerCompany} · Closed {closedDate}
          </div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
        {/* Deal terms */}
        <div className="card">
          <div className="h2" style={{ marginBottom:14 }}>Deal Terms</div>
          {[
            ['Listing',      enq.listingTitle||'—'],
            ['Agreed Price', agreedPrice],
            ['Quantity',     agreedQty],
            ['Deal Date',    closedDate],
            ['Deal Ref',     dealNo],
            ['Status',       'Closed – Pending Pickup'],
          ].map(([k,v])=>(
            <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
              <span style={{ color:'var(--muted)', fontWeight:500 }}>{k}</span>
              <span style={{ fontWeight:700, color:'var(--navy)', textAlign:'right', maxWidth:'55%' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Platform fee */}
        <div className="card" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div className="h2">Platform Fee</div>
          <div style={{ background:'linear-gradient(135deg,#7B3F00,#C8A84B)', borderRadius:12, padding:'16px', textAlign:'center' }}>
            <div style={{ fontSize:11, color:'rgba(255,255,255,.6)', marginBottom:4 }}>SUCCESS FEE (3%)</div>
            <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:22, color:'#fff' }}>
              {agreedPrice !== '—' ? '₹' + Math.round(Number(agreedPrice.replace(/[^0-9.]/g,''))*0.03).toLocaleString('en-IN') : '3% of deal'}
            </div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,.5)', marginTop:4 }}>Payable by seller · Invoice in 2 days</div>
          </div>
          <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.7 }}>
            Surpliq invoice will be sent to <strong>{enq.sellerCompany}</strong> separately. Payment via bank transfer or Razorpay.
          </div>
          <div style={{ background:'rgba(200,168,75,.08)', border:'1px solid rgba(200,168,75,.2)', borderRadius:8, padding:'10px 12px', fontSize:11, color:'var(--navy)' }}>
            💡 First deal? We recommend escrow or advance payment for peace of mind.
          </div>
        </div>
      </div>

      {/* Contact exchange */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="h2" style={{ marginBottom:4 }}>Contact Details — Shared with Both Parties</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>
          These contacts are now visible to both parties. Coordinate pickup, inspection, and payment directly.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {[
            { label:'Seller', icon:'🏭', data:sellerContact, highlight: role==='buyer' },
            { label:'Buyer',  icon:'🛒', data:buyerContact,  highlight: role==='seller' },
          ].map(({ label, icon, data, highlight }) => (
            <div key={label} style={{ border:`2px solid ${highlight?'var(--gold)':'var(--border)'}`, borderRadius:12, padding:'16px', background:highlight?'rgba(200,168,75,.04)':'var(--off)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
                <span style={{ fontSize:22 }}>{icon}</span>
                <div>
                  <div style={{ fontWeight:800, fontSize:13, color:'var(--navy)' }}>{label}</div>
                  {highlight && <div style={{ fontSize:10, color:'var(--gold)', fontWeight:700 }}>← Your contact</div>}
                </div>
              </div>
              {[
                ['🏢 Company', data.company],
                ['👤 Contact', data.name],
                ['📍 Location', data.city],
                ['📧 Email', data.email],
                ['📱 Phone', data.phone],
              ].map(([k,v])=>(
                <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                  <span style={{ color:'var(--muted)' }}>{k}</span>
                  <span style={{ fontWeight:700, color:'var(--navy)' }}>{v}</span>
                </div>
              ))}
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <a href={`mailto:${data.email}?subject=Re: ${enq.listingTitle} — Deal ${dealNo}`}
                  className="btn btn-out btn-sm" style={{ flex:1, textDecoration:'none', textAlign:'center' }}>
                  📧 Email
                </a>
                <a href={`https://wa.me/${data.phone?.replace(/[^0-9]/g,'')}?text=${encodeURIComponent(`Hi ${data.name}, regarding our deal (${dealNo}) for ${enq.listingTitle} — `)}`}
                  className="btn btn-out btn-sm" style={{ flex:1, textDecoration:'none', textAlign:'center' }}>
                  💬 WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Next steps */}
      <div className="card">
        <div className="h2" style={{ marginBottom:14 }}>Next Steps</div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {[
            { n:'1', t:'Coordinate Pickup',       d:'Contact the seller to confirm warehouse address, access timing, and transport arrangements.', done:false },
            { n:'2', t:'Arrange Inspection',      d:'Both parties to agree on final inspection date before payment and pickup.', done:false },
            { n:'3', t:'Issue GST Invoice',       d:'Seller to issue proper GST tax invoice to buyer at time of delivery/pickup.', done:false },
            { n:'4', t:'Complete Payment',        d:'As per agreed payment terms. Surpliq recommends advance or escrow for first-time buyers.', done:false },
            { n:'5', t:'Surpliq Fee Invoice',     d:'You will receive a platform fee invoice (3%) from Surpliq within 2 business days.', done:false },
          ].map((s,i) => (
            <div key={i} style={{ display:'flex', gap:12, padding:'12px 14px', background:'var(--off)', borderRadius:10, border:'1px solid var(--border)' }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--gold)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800, color:'#fff', flexShrink:0 }}>{s.n}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', marginBottom:2 }}>{s.t}</div>
                <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.6 }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop:16, padding:'12px 14px', background:'rgba(10,22,40,.04)', borderRadius:10, fontSize:11, color:'var(--muted)', textAlign:'center' }}>
          For any disputes or support: <a href="mailto:support@surpliq.com" style={{ color:'var(--gold)', fontWeight:700 }}>support@surpliq.com</a>
        </div>
      </div>
    </div>
  );
}


// ── BUYER BROWSE ──────────────────────────────────────────────────────────
function BuyerBrowse({ user, onViewListing }) {
  const ww = useWindowWidth(); const mob = ww < 640;
  const [listings,   setListings]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [catFilter,  setCatFilter]  = useState('All');
  const [condFilter, setCondFilter] = useState('All');
  const [sortBy,     setSortBy]     = useState('newest');
  const [priceMin,   setPriceMin]   = useState('');
  const [priceMax,   setPriceMax]   = useState('');
  const [locFilter,  setLocFilter]  = useState('');
  const [showFilters,setShowFilters]= useState(false);
  const [watchlist,  setWatchlist]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('surpliq_watchlist') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    (async () => {
      const l = await DB.list('listings', []);
      setListings(l.filter(x => x.status === 'active'));
      setLoading(false);
    })();
  }, []);

  const toggleWatch = (id, e) => {
    e.stopPropagation();
    setWatchlist(p => {
      const next = p.includes(id) ? p.filter(x => x !== id) : [...p, id];
      localStorage.setItem('surpliq_watchlist', JSON.stringify(next));
      return next;
    });
  };

  const filtered = listings
    .filter(l => catFilter  === 'All' || l.category === catFilter)
    .filter(l => condFilter === 'All' || (l.condition || '').includes(condFilter))
    .filter(l => !locFilter  || (l.location || '').toLowerCase().includes(locFilter.toLowerCase()))
    .filter(l => !priceMin   || Number(l.price) >= Number(priceMin))
    .filter(l => !priceMax   || Number(l.price) <= Number(priceMax))
    .filter(l => !search     ||
      (l.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.location || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.description || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.sellerCompany || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest')    return new Date(b.createdAt||0) - new Date(a.createdAt||0);
      if (sortBy === 'price_asc') return Number(a.price) - Number(b.price);
      if (sortBy === 'price_desc')return Number(b.price) - Number(a.price);
      if (sortBy === 'enquiries') return (b.enquiryCount||0) - (a.enquiryCount||0);
      return 0;
    });

  const hasFilters = catFilter !== 'All' || condFilter !== 'All' || locFilter || priceMin || priceMax;
  const clearFilters = () => { setCatFilter('All'); setCondFilter('All'); setLocFilter(''); setPriceMin(''); setPriceMax(''); };

  if (loading) return (
    <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
      <div className="ai-dots" style={{ justifyContent:'center', marginBottom:10 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>
      Loading marketplace…
    </div>
  );

  return (
    <div className="fu">
      {/* Header */}
      <div style={{ marginBottom:16 }}>
        <div className="eye">Marketplace</div>
        <div className="h1">Find Industrial Surplus</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>{filtered.length} listing{filtered.length!==1?'s':''} available</div>
      </div>

      {/* Search bar */}
      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
        <div style={{ flex:1, position:'relative' }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'var(--muted)' }}>🔍</span>
          <input className="finput" style={{ paddingLeft:34 }}
            placeholder="Search items, grade, location, seller…"
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <button className="btn btn-sm" style={{ background: showFilters?'var(--navy)':'#fff', color: showFilters?'#fff':'var(--muted)', border:'1.5px solid var(--border)', position:'relative' }}
          onClick={() => setShowFilters(p => !p)}>
          ⚙️ Filters {hasFilters && <span style={{ position:'absolute', top:-4, right:-4, width:8, height:8, borderRadius:'50%', background:'var(--gold)', border:'2px solid #fff' }}/>}
        </button>
      </div>

      {/* Category pills */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
        {['All','RM','BOP','MRO','CE','FG'].map(c => (
          <button key={c} onClick={() => setCatFilter(c)}
            style={{ padding:'6px 14px', border:'1.5px solid '+(catFilter===c?'var(--navy)':'var(--border)'),
              borderRadius:20, background:catFilter===c?'var(--navy)':'#fff',
              color:catFilter===c?'#fff':'var(--muted)', fontSize:12, fontWeight:700, cursor:'pointer' }}>
            {c === 'All' ? 'All Categories' : c}
          </button>
        ))}
      </div>

      {/* Expanded filters panel */}
      {showFilters && (
        <div style={{ background:'#fff', border:'1.5px solid var(--border)', borderRadius:12, padding:'16px', marginBottom:12 }}>
          <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 1fr 1fr', gap:12 }}>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Condition</label>
              <select className="fselect" value={condFilter} onChange={e => setCondFilter(e.target.value)}>
                {['All','New','Like New','Used — Good','Used — Fair','Sealed'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Location</label>
              <input className="finput" placeholder="e.g. Ludhiana, Punjab" value={locFilter} onChange={e => setLocFilter(e.target.value)}/>
            </div>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Sort By</label>
              <select className="fselect" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                {[['newest','Newest First'],['price_asc','Price: Low → High'],['price_desc','Price: High → Low'],['enquiries','Most Enquiries']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Min Price (₹)</label>
              <input className="finput" placeholder="e.g. 50000" type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)}/>
            </div>
            <div className="fgrp" style={{ marginBottom:0 }}>
              <label className="flabel">Max Price (₹)</label>
              <input className="finput" placeholder="e.g. 5000000" type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)}/>
            </div>
            <div style={{ display:'flex', alignItems:'flex-end' }}>
              {hasFilters && (
                <button className="btn btn-out btn-sm btn-full" onClick={clearFilters}>✕ Clear Filters</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {hasFilters && (
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:10 }}>
          {catFilter !== 'All'   && <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>Cat: {catFilter} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={() => setCatFilter('All')}>✕</span></span>}
          {condFilter !== 'All'  && <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>Cond: {condFilter} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={() => setCondFilter('All')}>✕</span></span>}
          {locFilter             && <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>📍 {locFilter} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={() => setLocFilter('')}>✕</span></span>}
          {(priceMin || priceMax)&& <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, background:'rgba(10,22,40,.07)', color:'var(--navy)' }}>₹{priceMin||'0'} – {priceMax||'∞'} <span style={{ cursor:'pointer', marginLeft:4 }} onClick={() => { setPriceMin(''); setPriceMax(''); }}>✕</span></span>}
        </div>
      )}

      {/* Grid */}
      <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
        {filtered.map(l => (
          <ListingCard key={l._id} listing={l} watched={watchlist.includes(l._id)}
            onToggleWatch={e => toggleWatch(l._id, e)}
            onClick={() => onViewListing(l._id)}/>
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'48px 20px', color:'var(--muted)' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
            <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)', marginBottom:6 }}>No listings found</div>
            <div style={{ fontSize:13 }}>Try adjusting your search or filters</div>
            {hasFilters && <button className="btn btn-gold btn-sm" style={{ marginTop:14 }} onClick={clearFilters}>Clear All Filters</button>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── LISTING CARD ──────────────────────────────────────────────────────────
function ListingCard({ listing: l, watched, onToggleWatch, onClick }) {
  const daysAgo = l.createdAt ? Math.floor((Date.now()-new Date(l.createdAt))/86400000) : null;
  return (
    <div className="lst-card" onClick={onClick}
      style={{ position:'relative', cursor:'pointer', display:'flex', flexDirection:'column' }}>

      {/* Image or category banner */}
      {l.images && l.images.length > 0 ? (
        <div style={{ height:130, borderRadius:'10px 10px 0 0', overflow:'hidden', margin:'-14px -14px 12px', position:'relative' }}>
          <img src={l.images[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, transparent 50%, rgba(0,0,0,.5))' }}/>
          <div style={{ position:'absolute', bottom:8, left:10 }}><CatBadge cat={l.category}/></div>
          {l.images.length > 1 && <div style={{ position:'absolute', bottom:8, right:10, fontSize:10, fontWeight:700, color:'#fff', background:'rgba(0,0,0,.4)', padding:'2px 6px', borderRadius:8 }}>+{l.images.length-1} photos</div>}
        </div>
      ) : (
        <div style={{ display:'flex', gap:10, marginBottom:10, alignItems:'flex-start' }}>
          <div style={{ width:44, height:44, background:'#F0F4FF', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
            {l.category==='RM'?'🏗️':l.category==='BOP'?'⚙️':l.category==='MRO'?'🔧':l.category==='CE'?'🏭':'📦'}
          </div>
          <div style={{ flex:1 }}><CatBadge cat={l.category}/></div>
        </div>
      )}

      {/* Watchlist btn */}
      <button onClick={onToggleWatch}
        style={{ position:'absolute', top:10, right:10, background:'rgba(255,255,255,.92)', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 1px 4px rgba(0,0,0,.12)', zIndex:2 }}>
        {watched ? '❤️' : '🤍'}
      </button>

      {/* New badge */}
      {daysAgo !== null && daysAgo < 3 && (
        <div style={{ position:'absolute', top:10, left:10, background:'var(--green)', color:'#fff', fontSize:9, fontWeight:800, padding:'2px 7px', borderRadius:10, letterSpacing:.5 }}>NEW</div>
      )}

      {/* Title */}
      <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:13, lineHeight:1.35, marginBottom:8, flex:1 }}>{l.title}</div>

      {/* Meta */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', fontSize:11, color:'var(--muted)', marginBottom:10 }}>
        <span>📍 {l.location}</span>
        <span>⚖️ {l.qty} {l.unit}</span>
        <span style={{ color: l.condition?.includes('New')?'var(--green)':'var(--muted)' }}>● {l.condition}</span>
      </div>

      {/* Price + CTA */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:10, borderTop:'1px solid var(--border)' }}>
        <div>
          <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:16, color:'var(--navy)' }}>
            ₹{Number(l.price||0).toLocaleString('en-IN')}{l.priceUnit}
          </div>
          <div style={{ fontSize:10, color:'var(--muted)', marginTop:1 }}>
            💬 {l.enquiryCount||0} enquir{l.enquiryCount===1?'y':'ies'} · 👁 {l.views||0} views
          </div>
        </div>
        <button className="btn btn-gold btn-sm">View & Offer →</button>
      </div>
    </div>
  );
}


// ── INSPECTION FORM ───────────────────────────────────────────────────────
function InspectionForm({ listing, user, onBack }) {
  const [form, setForm] = useState({
    date:'', time:'09:00', contact:user.name||'', phone:'', purpose:'Quality Verification',
    items:'', notes:'', accompany:'Yes'
  });
  const [sent,    setSent]    = useState(false);
  const [sending, setSending] = useState(false);
  const upd = (k,v) => setForm(p => ({...p,[k]:v}));

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate()+1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const submit = async () => {
    if (!form.date || !form.contact || !form.phone) return;
    setSending(true);
    // Save inspection request as an enquiry thread message
    const existing = await DB.list('enquiries',[['listingId','==',listing._id],['buyerId','==',user._id]]);
    const msg = `📅 INSPECTION REQUEST
Date: ${form.date} at ${form.time}
Contact: ${form.contact} · Phone: ${form.phone}
Purpose: ${form.purpose}
Items to inspect: ${form.items||'Full lot'}
Will accompany: ${form.accompany}
Notes: ${form.notes||'None'}`;
    if (existing.length > 0) {
      const enq = existing[0];
      await DB.update('enquiries', enq._id, {
        thread:[...(enq.thread||[]), { from:'buyer', time:new Date().toISOString(), text:msg }],
        updatedAt:new Date().toISOString()
      });
    } else {
      await DB.add('enquiries', {
        listingId:listing._id, buyerId:user._id, sellerId:listing.sellerId,
        listingTitle:listing.title, buyerName:user.name, buyerCompany:user.company,
        buyerCity:user.city, buyerRating:4.5, status:'new',
        createdAt:new Date().toISOString(), updatedAt:new Date().toISOString(),
        thread:[{ from:'buyer', time:new Date().toISOString(), text:msg }]
      });
    }
    setSending(false); setSent(true);
  };

  if (sent) return (
    <div style={{ textAlign:'center', padding:'8px 0' }}>
      <div style={{ fontSize:44, marginBottom:10 }}>📅</div>
      <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:16, color:'var(--navy)', marginBottom:6 }}>
        Inspection Requested!
      </div>
      <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.7, marginBottom:8 }}>
        Request sent to <strong>{listing.sellerCompany}</strong>.<br/>
        They will confirm the slot within 4 hours.
      </div>
      <div style={{ background:'var(--off)', borderRadius:10, padding:'12px 14px', marginBottom:14, textAlign:'left' }}>
        {[['Date', `${form.date} at ${form.time}`],['Contact',form.contact],['Phone',form.phone],['Purpose',form.purpose]].map(([k,v])=>(
          <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
            <span style={{ color:'var(--muted)' }}>{k}</span>
            <span style={{ fontWeight:700, color:'var(--navy)' }}>{v}</span>
          </div>
        ))}
      </div>
      <button className="btn btn-gold btn-full" onClick={onBack}>← Back to Offer</button>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize:12, color:'var(--muted)', marginBottom:14, lineHeight:1.6 }}>
        Request a physical inspection of this lot. The seller will confirm your slot and share the warehouse address.
      </div>

      <div className="frow">
        <div className="fgrp">
          <label className="flabel">Preferred Date <span>*</span></label>
          <input className="finput" type="date" min={minDate} value={form.date} onChange={e=>upd('date',e.target.value)}/>
        </div>
        <div className="fgrp">
          <label className="flabel">Preferred Time</label>
          <select className="fselect" value={form.time} onChange={e=>upd('time',e.target.value)}>
            {['08:00','09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00'].map(t=>(
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="frow">
        <div className="fgrp">
          <label className="flabel">Contact Person <span>*</span></label>
          <input className="finput" placeholder="Who will visit?" value={form.contact} onChange={e=>upd('contact',e.target.value)}/>
        </div>
        <div className="fgrp">
          <label className="flabel">Mobile Number <span>*</span></label>
          <input className="finput" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e=>upd('phone',e.target.value)}/>
        </div>
      </div>

      <div className="fgrp">
        <label className="flabel">Purpose of Inspection</label>
        <select className="fselect" value={form.purpose} onChange={e=>upd('purpose',e.target.value)}>
          {['Quality Verification','Quantity Check','Grade / Spec Confirmation','Technical Assessment','Pre-Purchase Audit','All of the Above'].map(p=>(
            <option key={p}>{p}</option>
          ))}
        </select>
      </div>

      <div className="fgrp">
        <label className="flabel">Specific Items / SKUs to Inspect</label>
        <input className="finput" placeholder="e.g. HR Coil lot, Bearing boxes only (leave blank for full lot)"
          value={form.items} onChange={e=>upd('items',e.target.value)}/>
      </div>

      <div className="fgrp">
        <label className="flabel">Will a QC / Technical Expert Accompany?</label>
        <select className="fselect" value={form.accompany} onChange={e=>upd('accompany',e.target.value)}>
          {['Yes','No','To be decided'].map(o=><option key={o}>{o}</option>)}
        </select>
      </div>

      <div className="fgrp">
        <label className="flabel">Additional Notes</label>
        <textarea className="ftextarea" style={{ minHeight:60 }}
          placeholder="Special equipment needed, access requirements, etc."
          value={form.notes} onChange={e=>upd('notes',e.target.value)}/>
      </div>

      <div style={{ background:'rgba(200,168,75,.07)', border:'1px solid rgba(200,168,75,.2)', borderRadius:8, padding:'10px 12px', fontSize:11, color:'var(--muted)', marginBottom:14 }}>
        💡 Inspection requests are usually confirmed within 4 hours. Contact details are shared upon seller confirmation.
      </div>

      <button className="btn btn-gold btn-full"
        disabled={!form.date || !form.contact || !form.phone || sending}
        onClick={submit}>
        {sending ? 'Submitting…' : '📅 Send Inspection Request'}
      </button>
    </div>
  );
}

// ── LISTING DETAIL + OFFER ────────────────────────────────────────────────
function ListingDetail({ user, listingId, onBack, onOfferSent }) {
  const ww = useWindowWidth(); const mob = ww < 700;
  const [listing,    setListing]    = useState(null);
  const [offer,      setOffer]      = useState({ price:'', qty:'', message:'' });
  const [sending,    setSending]    = useState(false);
  const [sent,       setSent]       = useState(false);
  const [aiTip,      setAiTip]      = useState('');
  const [aiLoading,  setAiLoading]  = useState(false);
  const [imgIdx,     setImgIdx]     = useState(0);
  const [watched,    setWatched]    = useState(false);
  const [offerTab,   setOfferTab]   = useState('offer'); // 'offer' | 'info' | 'inspection'

  useEffect(() => {
    (async () => {
      const l = await DB.get('listings', listingId);
      setListing(l);
      await DB.update('listings', listingId, { views:(l?.views||0)+1 });
      try {
        const wl = JSON.parse(localStorage.getItem('surpliq_watchlist')||'[]');
        setWatched(wl.includes(listingId));
      } catch {}
    })();
  }, [listingId]);

  const toggleWatch = () => {
    try {
      const wl = JSON.parse(localStorage.getItem('surpliq_watchlist')||'[]');
      const next = watched ? wl.filter(x=>x!==listingId) : [...wl, listingId];
      localStorage.setItem('surpliq_watchlist', JSON.stringify(next));
      setWatched(!watched);
    } catch {}
  };

  const getAITip = async () => {
    if (!listing) return;
    setAiLoading(true);
    const tip = await callAI(
      'Give a specific 1-sentence buyer negotiation tip for this industrial surplus lot. Suggest an exact offer price range.',
      `Listing: ${listing.title}. Asking: ₹${listing.price}${listing.priceUnit}. Liquidation range: ${listing.liquidationRange}. Category: ${listing.category}. Qty: ${listing.qty} ${listing.unit}.`,
      'Offer 8% below asking as an opener — surplus sellers expect negotiation and usually settle within 5-10%.'
    );
    setAiTip(tip); setAiLoading(false);
    // Auto-fill a suggested price
    if (!offer.price) {
      const suggested = Math.round(Number(listing.price) * 0.92);
      setOffer(p => ({...p, price: suggested.toString()}));
    }
  };

  const sendOffer = async () => {
    if (!offer.price) return;
    setSending(true);
    const existing = await DB.list('enquiries',[['listingId','==',listingId],['buyerId','==',user._id]]);
    if (existing.length === 0) {
      await DB.add('enquiries', {
        listingId, buyerId:user._id, sellerId:listing.sellerId,
        listingTitle:listing.title, buyerName:user.name, buyerCompany:user.company,
        buyerCity:user.city, buyerRating:4.5, status:'new',
        createdAt:new Date().toISOString(), updatedAt:new Date().toISOString(),
        thread:[
          { from:'buyer', time:new Date().toISOString(),
            text: offer.message || `Interested in this lot. Offering ₹${offer.price} for ${offer.qty||listing.qty+' '+listing.unit}.` },
          { from:'buyer', time:new Date().toISOString(), isQuote:true,
            quote:{ price:offer.price, qty:offer.qty||`${listing.qty} ${listing.unit}`,
              total:'To be confirmed', validity:'48 hours', status:'pending', label:'Buyer Offer' }},
        ]
      });
      await DB.update('listings', listingId, { enquiryCount:(listing.enquiryCount||0)+1 });
    }
    setSending(false); setSent(true);
    if (onOfferSent) onOfferSent({ listingId, sellerId:listing.sellerId, listingTitle:listing.title, buyerCompany:user.company, offerPrice:offer.price });
  };

  if (!listing) return (
    <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
      <div className="ai-dots" style={{ justifyContent:'center', marginBottom:8 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>
      Loading…
    </div>
  );

  // Offer sent confirmation
  if (sent) return (
    <div style={{ maxWidth:480, margin:'32px auto' }}>
      <div style={{ background:'linear-gradient(135deg,#0A1628,#1B3A5C)', borderRadius:18, padding:28, textAlign:'center' }}>
        <div style={{ fontSize:52, marginBottom:12 }}>📨</div>
        <div style={{ fontFamily:'var(--fh)', fontSize:22, fontWeight:800, color:'#fff', marginBottom:6 }}>Offer Sent!</div>
        <div style={{ fontSize:13, color:'rgba(255,255,255,.55)', lineHeight:1.7, marginBottom:22 }}>
          Your offer has been sent to <strong style={{ color:'#fff' }}>{listing.sellerCompany}</strong>.<br/>Expect a response within 2–4 business hours.
        </div>
        {[['Listing',listing.title?.slice(0,45)+'…'],['Your Offer','₹'+Number(offer.price).toLocaleString('en-IN')],['Quantity',offer.qty||`${listing.qty} ${listing.unit}`],['Status','⏳ Awaiting Response']].map(([k,v])=>(
          <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,.07)', fontSize:12 }}>
            <span style={{ color:'rgba(255,255,255,.4)' }}>{k}</span>
            <span style={{ color:'#fff', fontWeight:600 }}>{v}</span>
          </div>
        ))}
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <button className="btn btn-out" style={{ flex:1, color:'rgba(255,255,255,.6)', borderColor:'rgba(255,255,255,.2)' }} onClick={onBack}>← Browse More</button>
          <button className="btn btn-gold" style={{ flex:1 }}>View My Offers</button>
        </div>
      </div>
    </div>
  );

  const imgs = listing.images && listing.images.length > 0 ? listing.images : null;

  return (
    <div className="fu">
      {/* Back + watchlist header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <button className="btn btn-out btn-sm" onClick={onBack}>← Back to Browse</button>
        <button onClick={toggleWatch}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:9, border:'1.5px solid var(--border)', background:'#fff', cursor:'pointer', fontSize:12, fontWeight:700, color:'var(--navy)' }}>
          {watched ? '❤️ Saved' : '🤍 Save'} 
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 320px', gap:16, alignItems:'start' }}>

        {/* LEFT — Images + Details */}
        <div>
          {/* Image gallery */}
          {imgs ? (
            <div style={{ marginBottom:16 }}>
              <div style={{ borderRadius:14, overflow:'hidden', background:'#f0f0f0', height:220, position:'relative' }}>
                <img src={imgs[imgIdx]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                {imgs.length > 1 && (
                  <>
                    <button onClick={()=>setImgIdx(p=>(p-1+imgs.length)%imgs.length)}
                      style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,.85)', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:16 }}>‹</button>
                    <button onClick={()=>setImgIdx(p=>(p+1)%imgs.length)}
                      style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'rgba(255,255,255,.85)', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', fontSize:16 }}>›</button>
                    <div style={{ position:'absolute', bottom:10, left:'50%', transform:'translateX(-50%)', display:'flex', gap:5 }}>
                      {imgs.map((_,i)=><div key={i} style={{ width:6, height:6, borderRadius:'50%', background: i===imgIdx?'#fff':'rgba(255,255,255,.4)' }}/>)}
                    </div>
                  </>
                )}
              </div>
              {imgs.length > 1 && (
                <div style={{ display:'flex', gap:8, marginTop:8, overflowX:'auto', paddingBottom:4 }}>
                  {imgs.map((src,i)=>(
                    <img key={i} src={src} alt="" onClick={()=>setImgIdx(i)}
                      style={{ width:56, height:56, objectFit:'cover', borderRadius:8, cursor:'pointer', flexShrink:0,
                        border: i===imgIdx?'2px solid var(--gold)':'2px solid transparent', opacity:i===imgIdx?1:.65 }}/>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ height:160, background:'linear-gradient(135deg,#E8F0FE,#EEF2FF)', borderRadius:14, display:'flex', alignItems:'center', justifyContent:'center', fontSize:64, marginBottom:16 }}>
              {listing.category==='RM'?'🏗️':listing.category==='BOP'?'⚙️':listing.category==='MRO'?'🔧':listing.category==='CE'?'🏭':'📦'}
            </div>
          )}

          {/* Main detail card */}
          <div className="card" style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
              <div>
                <CatBadge cat={listing.category}/>
                <div className="h2" style={{ marginTop:6, lineHeight:1.25 }}>{listing.title}</div>
              </div>
              <span className="pill pill-green" style={{ flexShrink:0, marginLeft:8 }}>● Active</span>
            </div>
            <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.75, marginBottom:16 }}>{listing.description}</p>

            {/* Specs grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
              {[
                ['📦 Quantity',   `${listing.qty} ${listing.unit}`],
                ['⭐ Condition',  listing.condition],
                ['📍 Location',   listing.location],
                ['🕐 In Storage', `${listing.ageMonths} months`],
                ['📒 Book Value', `₹${Number(listing.bookValue||0).toLocaleString('en-IN')}`],
                ['🏷️ Asking',    `₹${Number(listing.price||0).toLocaleString('en-IN')}${listing.priceUnit}`],
                ['🏢 Seller',     listing.sellerCompany],
                ['⭐ Rating',     `${listing.sellerRating||4.5}/5`],
              ].map(([k,v],i)=>(
                <div key={k} style={{ display:'flex', flexDirection:'column', padding:'9px 0', borderBottom: i<6?'1px solid var(--border)':'none', paddingRight:12 }}>
                  <span style={{ fontSize:10, color:'var(--muted)', fontWeight:600, marginBottom:2 }}>{k}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)' }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Price intelligence */}
          {(listing.scrapFloor || listing.liquidationRange) && (
            <div className="card" style={{ marginBottom:14 }}>
              <div className="eye" style={{ marginBottom:6 }}>AI Price Intelligence</div>
              <div className="h3" style={{ marginBottom:10 }}>Market Benchmarks for This Lot</div>
              <div className="pb-row" style={{ marginBottom:12 }}>
                {[['Scrap Floor',listing.scrapFloor,false],['Liquidation ⭐',listing.liquidationRange,true],['Resale Max',listing.resaleMax,false]].map(([l,v,hi])=>(
                  <div key={l} className={`pb ${hi?'hi':''}`}><div className="pb-l">{l}</div><div className="pb-v">{v||'—'}</div></div>
                ))}
              </div>
              {!aiTip ? (
                <button className="btn btn-out btn-sm" onClick={getAITip} disabled={aiLoading}>
                  {aiLoading ? '🤖 Thinking…' : '🤖 Get AI Buyer Tip + Suggested Offer'}
                </button>
              ) : (
                <SmartTip text={aiTip}/>
              )}
              {aiLoading && <div className="ai-dots" style={{ marginTop:8 }}><div className="ald"/><div className="ald"/><div className="ald"/></div>}
            </div>
          )}
        </div>

        {/* RIGHT — Offer panel */}
        <div style={{ position: mob?'static':'sticky', top:72 }}>
          <div className="card">
            {/* Price hero */}
            <div style={{ background:'linear-gradient(135deg,#0A1628,#1E3A6E)', borderRadius:10, padding:'14px 16px', marginBottom:16, textAlign:'center' }}>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', fontWeight:600, marginBottom:4 }}>ASKING PRICE</div>
              <div style={{ fontFamily:'var(--fh)', fontWeight:800, fontSize:26, color:'#fff', letterSpacing:-1 }}>
                ₹{Number(listing.price||0).toLocaleString('en-IN')}
                <span style={{ fontSize:14, color:'rgba(255,255,255,.5)' }}>{listing.priceUnit}</span>
              </div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', marginTop:4 }}>
                {listing.qty} {listing.unit} · {listing.condition}
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', gap:0, background:'var(--off)', borderRadius:9, padding:3, marginBottom:16 }}>
              {[['offer','Make Offer'],['inspection','Inspect'],['info','Seller Info']].map(([v,l])=>(
                <button key={v} onClick={()=>setOfferTab(v)}
                  style={{ flex:1, padding:'7px 4px', borderRadius:7, border:'none', fontFamily:'var(--fb)', fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .18s',
                    background: offerTab===v?'var(--navy)':'transparent', color: offerTab===v?'#fff':'var(--muted)' }}>{l}</button>
              ))}
            </div>

            {offerTab === 'offer' && (
              <>
                <div className="fgrp">
                  <label className="flabel">Your Offer Price (₹) <span>*</span></label>
                  <input className="finput"
                    placeholder={`e.g. ₹${Math.round(Number(listing.price||0)*0.92).toLocaleString('en-IN')}`}
                    value={offer.price} onChange={e=>setOffer(p=>({...p,price:e.target.value}))}/>
                  {listing.liquidationRange && (
                    <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>
                      💡 Fair range: <strong style={{ color:'var(--navy)' }}>{listing.liquidationRange}</strong>
                    </div>
                  )}
                </div>
                <div className="fgrp">
                  <label className="flabel">Quantity Required</label>
                  <input className="finput" placeholder={`Max: ${listing.qty} ${listing.unit}`}
                    value={offer.qty} onChange={e=>setOffer(p=>({...p,qty:e.target.value}))}/>
                </div>
                <div className="fgrp">
                  <label className="flabel">Message to Seller</label>
                  <textarea className="ftextarea" style={{ minHeight:80 }}
                    placeholder="Inspection request, pickup timeline, payment terms…"
                    value={offer.message} onChange={e=>setOffer(p=>({...p,message:e.target.value}))}/>
                </div>
                <button className="btn btn-gold btn-full" style={{ marginBottom:10 }}
                  onClick={sendOffer} disabled={!offer.price || sending}>
                  {sending ? 'Sending…' : '📨 Send Offer'}
                </button>
                <div style={{ fontSize:11, color:'var(--muted)', textAlign:'center', marginBottom:12 }}>
                  🔒 Contact details shared only after seller accepts
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-out btn-sm" style={{ flex:1 }} onClick={()=>setOfferTab('inspection')}>📅 Inspection</button>
                  <button className="btn btn-out btn-sm" style={{ flex:1 }} onClick={toggleWatch}>
                    {watched ? '❤️ Saved' : '🤍 Save'}
                  </button>
                </div>
              </>
            )}

            {offerTab === 'inspection' && (
              <InspectionForm listing={listing} user={user} onBack={()=>setOfferTab('offer')}/>
            )}

            {offerTab === 'info' && (
              <div>
                {[
                  ['Company',     listing.sellerCompany],
                  ['Location',    `${listing.sellerCity}, ${listing.sellerState}`],
                  ['GST Status',  '✅ Verified'],
                  ['Seller Rating',`⭐ ${listing.sellerRating||4.5}/5`],
                  ['Enquiries',   `${listing.enquiryCount||0} on this listing`],
                  ['Views',       `${listing.views||0} buyers viewed`],
                ].map(([k,v])=>(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'9px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                    <span style={{ color:'var(--muted)', fontWeight:500 }}>{k}</span>
                    <span style={{ fontWeight:700, color:'var(--navy)' }}>{v}</span>
                  </div>
                ))}
                <button className="btn btn-gold btn-full" style={{ marginTop:16 }} onClick={()=>setOfferTab('offer')}>
                  Make an Offer →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── BUYER ENQUIRIES ───────────────────────────────────────────────────────
function BuyerEnquiries({ user }) {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(()=>{
    (async()=>{ const e = await DB.list("enquiries",[["buyerId","==",user._id]]); setEnquiries(e); setLoading(false); })();
  },[user._id]);

  if (loading) return <div style={{ padding:40, textAlign:"center" }}>Loading…</div>;

  return (
    <div className="fu">
      <div className="eye">My Enquiries</div>
      <div className="h1" style={{ marginBottom:18 }}>Offer Tracking</div>
      {enquiries.length===0 && <div className="card" style={{ textAlign:"center", padding:40, color:"var(--muted)" }}>No enquiries yet. Browse listings and make an offer.</div>}
      {enquiries.map(e=>{
        const lastMsg = (e.thread||[]).filter(m=>m.from==="seller").slice(-1)[0];
        const lastQuote = [...(e.thread||[])].reverse().find(m=>m.isQuote);
        return (
          <div className="card" key={e._id} style={{ marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
              <div>
                <div className="h3">{e.listingTitle?.slice(0,50)}</div>
                <div style={{ fontSize:12, color:"var(--muted)" }}>Seller: {e.sellerId} · {new Date(e.createdAt).toLocaleDateString("en-IN")}</div>
              </div>
              <span className={`pill ${e.status==="accepted"?"pill-green":e.status==="negotiating"?"pill-amber":"pill-navy"}`}>
                {e.status==="accepted"?"✓ Deal Closed":e.status==="negotiating"?"🔄 Active":"New"}
              </span>
            </div>
            {lastQuote && <div style={{ fontSize:13, marginBottom:8 }}>Your offer: <strong>{lastQuote.quote.price}</strong> for {lastQuote.quote.qty}</div>}
            {lastMsg && <div style={{ background:"var(--off)", borderRadius:8, padding:"9px 12px", fontSize:13, marginBottom:10 }}>💬 Seller: "{lastMsg.text?.slice(0,80)}…"</div>}
            {e.status==="accepted" && <button className="btn btn-green btn-sm">📄 Deal Summary</button>}
          </div>
        );
      })}
    </div>
  );
}

// ── BUYER WATCHLIST ───────────────────────────────────────────────────────
function BuyerWatchlist({ user, onViewListing }) {
  const [wl, setWl]           = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    (async()=>{
      const w = await DB.list("watchlist",[["buyerId","==",user._id]]);
      const ids = w.flatMap(x=>x.listingIds||[]);
      setWl(ids);
      if (ids.length) {
        const all = await DB.list("listings",[]);
        setListings(all.filter(l=>ids.includes(l._id)));
      }
      setLoading(false);
    })();
  },[user._id]);

  if (loading) return <div style={{ padding:40, textAlign:"center" }}>Loading…</div>;

  return (
    <div className="fu">
      <div className="eye">Watchlist</div>
      <div className="h1" style={{ marginBottom:18 }}>Saved Lots ({wl.length})</div>
      {listings.length===0 && <div className="card" style={{ textAlign:"center", padding:40, color:"var(--muted)" }}>No saved lots. Browse and click ❤️ to save.</div>}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:14 }}>
        {listings.map(l=>{
          const c = CC[l.category]||CC.MRO;
          return (
            <div className="lst-card" key={l._id} onClick={()=>onViewListing(l._id)}>
              <div style={{ display:"flex", gap:10, marginBottom:10 }}>
                <div style={{ width:40, height:40, background:"#F0F4FF", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{l.icon||"📦"}</div>
                <div><CatBadge cat={l.category} /><div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:13, marginTop:3 }}>{l.title}</div></div>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, borderTop:"1px solid var(--border)" }}>
                <div style={{ fontFamily:"var(--fh)", fontWeight:800 }}>₹{Number(l.price).toLocaleString("en-IN")}{l.priceUnit}</div>
                <button className="btn btn-gold btn-sm">Open →</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ADMIN PANEL ───────────────────────────────────────────────────────────
function AdminPanel({ user }) {
  const ww = useWindowWidth(); const mob = ww < 700;
  const [tab,          setTab]          = useState('overview');
  const [users,        setUsers]        = useState([]);
  const [listings,     setListings]     = useState([]);
  const [enquiries,    setEnquiries]    = useState([]);
  const [deals,        setDeals]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);
  // Deal management
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [editingDeal,  setEditingDeal]  = useState(null);
  // User editing
  const [editingUser,  setEditingUser]  = useState(null);
  // Listing editing
  const [editingListing, setEditingListing] = useState(null);
  // Invoice
  const [invoiceDeal,  setInvoiceDeal]  = useState(null);
  // Buyer discovery
  const [discoveredBuyers, setDiscoveredBuyers] = useState([]);
  const [discLog,      setDiscLog]      = useState([]);
  const [discovering,  setDiscovering]  = useState(false);
  const [discListing,  setDiscListing]  = useState(null);
  const [notified,     setNotified]     = useState({});

  // ── Search / filter state per panel ──────────────────────────────
  const [dealSearch,   setDealSearch]   = useState('');
  const [dealStatus,   setDealStatus]   = useState('all');
  const [kycSearch,    setKycSearch]    = useState('');
  const [kycStatus,    setKycStatus]    = useState('all');
  const [listSearch,   setListSearch]   = useState('');
  const [listCat,      setListCat]      = useState('all');
  const [listStatus,   setListStatus]   = useState('all');
  const [userSearch,   setUserSearch]   = useState('');
  const [userRole,     setUserRole]     = useState('all');
  const [userKyc,      setUserKyc]      = useState('all');

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };
  const addLog = async txt => { await new Promise(r=>setTimeout(r,500)); setDiscLog(p=>[...p,txt]); };

  const loadAll = async () => {
    const [u,l,e,d] = await Promise.all([
      DB.list('users',[]),
      DB.list('listings',[]),
      DB.list('enquiries',[]),
      DB.list('deals',[]),
    ]);
    setUsers(u); setListings(l); setEnquiries(e); setDeals(d); setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  // ── KYC ───────────────────────────────────────────────────────────
  const approveKYC = async uid => {
    await DB.update('users', uid, { kycStatus:'approved' });
    setUsers(p=>p.map(u=>u._id===uid?{...u,kycStatus:'approved'}:u));
    showToast('KYC Approved ✅');
  };
  const rejectKYC = async uid => {
    await DB.update('users', uid, { kycStatus:'rejected' });
    setUsers(p=>p.map(u=>u._id===uid?{...u,kycStatus:'rejected'}:u));
    showToast('KYC Rejected', 'warn');
  };

  // ── User edit ─────────────────────────────────────────────────────
  const saveUser = async () => {
    await DB.update('users', editingUser._id, {
      name:editingUser.name, company:editingUser.company,
      gst:editingUser.gst, phone:editingUser.phone,
      city:editingUser.city, state:editingUser.state,
      kycStatus:editingUser.kycStatus,
    });
    setUsers(p=>p.map(u=>u._id===editingUser._id?{...u,...editingUser}:u));
    setEditingUser(null);
    showToast('User updated ✅');
  };

  // ── Listing edit ──────────────────────────────────────────────────
  const saveListing = async () => {
    await DB.update('listings', editingListing._id, {
      title:editingListing.title, price:editingListing.price,
      qty:editingListing.qty, unit:editingListing.unit,
      condition:editingListing.condition, status:editingListing.status,
      description:editingListing.description, location:editingListing.location,
    });
    setListings(p=>p.map(l=>l._id===editingListing._id?{...l,...editingListing}:l));
    setEditingListing(null);
    showToast('Listing updated ✅');
  };

  // ── Deal edit ─────────────────────────────────────────────────────
  const saveDeal = async () => {
    await DB.update('deals', editingDeal._id, {
      status:editingDeal.status,
      agreedPrice:editingDeal.agreedPrice,
      agreedQty:editingDeal.agreedQty,
      adminNotes:editingDeal.adminNotes,
    });
    setDeals(p=>p.map(d=>d._id===editingDeal._id?{...d,...editingDeal}:d));
    setEditingDeal(null);
    showToast('Deal updated ✅');
  };

  // ── Show invoice in-app (sandbox-safe) ────────────────────────────
  const [showInvoice, setShowInvoice] = useState(null);

  const generateInvoice = (deal) => {
    setInvoiceDeal(null);
    setShowInvoice(deal);
  };

  const _generateInvoicePrint = (deal) => {
    const invNo = `SINV-${new Date().getFullYear()}-${String(deal._id||'').slice(-6).toUpperCase()}`;
    const date  = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
    const base  = Number((deal.agreedPrice||'0').replace(/[^0-9.]/g,''));
    const fee   = Math.round(base * 0.03);
    const gst   = Math.round(fee * 0.18);
    const total = fee + gst;
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Invoice ${invNo}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;color:#1a1a2e;padding:32px;max-width:680px;margin:0 auto;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:18px;border-bottom:3px solid #C8A84B;}
  .logo{font-size:24px;font-weight:900;letter-spacing:-1px;}.logo span{color:#C8A84B;}
  .inv-title{text-align:right;}.inv-title h1{font-size:28px;color:#C8A84B;font-weight:900;}
  .inv-title p{font-size:12px;color:#666;margin-top:4px;}
  .section{margin:20px 0;}.section h2{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.5px;color:#1a1a2e;margin-bottom:10px;border-left:4px solid #C8A84B;padding-left:8px;}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px;}
  .party{background:#f8f9fa;border-radius:8px;padding:14px;}
  .party h3{font-size:12px;font-weight:800;margin-bottom:8px;color:#666;text-transform:uppercase;}
  .party p{font-size:13px;margin-bottom:3px;}
  table{width:100%;border-collapse:collapse;margin:12px 0;}
  th{background:#1a1a2e;color:#fff;padding:10px 14px;text-align:left;font-size:12px;}
  td{padding:10px 14px;border-bottom:1px solid #eee;font-size:13px;}
  .total-row td{font-weight:800;font-size:14px;background:#FFF9E6;}
  .gst-row td{color:#666;font-size:12px;}
  .badge{display:inline-block;background:#dcfce7;color:#15803d;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:800;}
  .footer{margin-top:32px;padding-top:16px;border-top:1px solid #eee;font-size:10px;color:#999;text-align:center;line-height:1.8;}
  .bank{background:#f0f4ff;border-radius:8px;padding:14px;margin:12px 0;font-size:12px;}
  @media print{body{padding:16px;}}
</style></head><body>
<div class="header">
  <div>
    <div class="logo"><span>Surp</span>liq</div>
    <p style="font-size:11px;color:#666;margin-top:4px;">Surpliq Technologies Pvt. Ltd.<br/>India · support@surpliq.com</p>
  </div>
  <div class="inv-title"><h1>TAX INVOICE</h1><p>Invoice No: <strong>${invNo}</strong><br/>Date: ${date}</p></div>
</div>
<span class="badge">✓ DEAL CLOSED</span>
<div class="section" style="margin-top:16px;">
  <h2>Bill To (Seller)</h2>
  <div class="grid2">
    <div class="party">
      <h3>Billed To</h3>
      <p><strong>${deal.sellerCompany||'—'}</strong></p>
      <p>${deal.sellerName||''}</p>
      <p>${deal.sellerCity||''}</p>
      <p>GST: [Seller GST Number]</p>
    </div>
    <div class="party">
      <h3>Supplied By</h3>
      <p><strong>Surpliq Technologies Pvt. Ltd.</strong></p>
      <p>Platform Marketplace Services</p>
      <p>India</p>
      <p>GST: [Surpliq GST]</p>
    </div>
  </div>
</div>
<div class="section">
  <h2>Deal Reference</h2>
  <table>
    <tr><th>Description</th><th>Deal Ref</th><th>Listing</th><th>Parties</th></tr>
    <tr><td>Platform Marketplace Service Fee</td><td>${deal._id?.slice(-8)||'—'}</td><td>${(deal.listingTitle||'').slice(0,35)}</td><td>${deal.sellerCompany} → ${deal.buyerCompany}</td></tr>
  </table>
</div>
<div class="section">
  <h2>Fee Calculation</h2>
  <table>
    <tr><th>Item</th><th>Deal Value</th><th>Rate</th><th>Amount</th></tr>
    <tr><td>Platform Success Fee</td><td>₹${base.toLocaleString('en-IN')}</td><td>3%</td><td>₹${fee.toLocaleString('en-IN')}</td></tr>
    <tr class="gst-row"><td colspan="3">GST @ 18% on platform fee</td><td>₹${gst.toLocaleString('en-IN')}</td></tr>
    <tr class="total-row"><td colspan="3"><strong>TOTAL PAYABLE</strong></td><td><strong>₹${total.toLocaleString('en-IN')}</strong></td></tr>
  </table>
</div>
<div class="bank">
  <strong>Payment Details:</strong> NEFT/IMPS to Surpliq Technologies Pvt. Ltd.<br/>
  Bank: [Bank Name] · A/C: [Account Number] · IFSC: [IFSC Code]<br/>
  Please quote Invoice No. <strong>${invNo}</strong> in payment reference · Due within 7 days of deal closure.
</div>
<div class="footer">
  This is a computer-generated invoice from Surpliq Technologies Pvt. Ltd.<br/>
  For queries: support@surpliq.com · This invoice is subject to Indian jurisdiction.<br/>
  © ${new Date().getFullYear()} Surpliq Technologies Pvt. Ltd. · All rights reserved.
</div>
</body></html>`);
    w.document.close(); w.focus();
    setTimeout(()=>w.print(), 500);
  };

  // ── Buyer discovery ───────────────────────────────────────────────
  const runDiscovery = async (listing) => {
    setDiscListing(listing);
    setDiscovering(true);
    setDiscLog([]);
    setDiscoveredBuyers([]);
    setNotified({});

    const loc = listing.location?.split(',')[1]?.trim() || listing.location || 'India';
    const catLabel = {RM:'raw material',BOP:'bought-out parts',MRO:'MRO consumables',CE:'capital equipment',FG:'finished goods'}[listing.category]||'industrial';

    setDiscLog(p=>[...p, `🔍 Searching IndiaMART for ${catLabel} buyers near ${loc}...`]);
    await new Promise(r=>setTimeout(r,700));
    setDiscLog(p=>[...p, `🌐 Scanning TradeIndia, MRAI Directory...`]);
    await new Promise(r=>setTimeout(r,700));
    setDiscLog(p=>[...p, `🧠 Scoring by proximity, category match, purchase size...`]);
    await new Promise(r=>setTimeout(r,700));
    setDiscLog(p=>[...p, `✅ Cross-checking Surpliq registered buyer database...`]);
    await new Promise(r=>setTimeout(r,500));

    const fallback = [
      {name:'Narayan Steel Traders',type:'Steel Trader',city:'Ludhiana',state:'Punjab',whatTheyBuy:'MS/HR/CR Steel',source:'Surpliq Network',matchScore:96,gstVerified:true,registeredOnSurpliq:true},
      {name:'Punjab Re-Rollers Ltd',type:'Re-Rolling Mill',city:'Mandi Gobindgarh',state:'Punjab',whatTheyBuy:'HR/CR Coils',source:'MRAI Directory',matchScore:91,gstVerified:true,registeredOnSurpliq:false},
      {name:'NCR Industrial Supplies',type:'Distributor',city:'Delhi',state:'Delhi NCT',whatTheyBuy:'Industrial surplus',source:'IndiaMART',matchScore:78,gstVerified:true,registeredOnSurpliq:false},
      {name:'Bharat MRO Hub',type:'MRO Distributor',city:'Mumbai',state:'Maharashtra',whatTheyBuy:'MRO, Bearings, Consumables',source:'TradeIndia',matchScore:74,gstVerified:true,registeredOnSurpliq:false},
      {name:'Gujarat Metal Works',type:'Re-processor',city:'Ahmedabad',state:'Gujarat',whatTheyBuy:'Metal scrap and surplus',source:'MRAI Directory',matchScore:68,gstVerified:false,registeredOnSurpliq:false},
    ];

    try {
      const raw = await callAI(
        `You find B2B industrial buyers in India. Return ONLY a valid JSON array — no markdown, no text before or after.
Each buyer object: {"name":"Company Name","type":"buyer type","city":"city","state":"state","whatTheyBuy":"what they purchase","source":"IndiaMART|TradeIndia|MRAI Directory|Surpliq Network","matchScore":85,"gstVerified":true,"registeredOnSurpliq":false}
Generate 6 realistic buyers. Match score 60–98. Mix of sources.`,
        `Find buyers for: "${listing.title}" | Category: ${listing.category} (${catLabel}) | Location: ${listing.location} | Qty: ${listing.qty} ${listing.unit}`,
        JSON.stringify(fallback)
      );
      // Robustly extract JSON array
      const cleaned = raw.replace(/```json|```/g,'').trim();
      const match   = cleaned.match(/\[[\s\S]*\]/);
      const parsed  = JSON.parse(match ? match[0] : cleaned);
      const buyers  = Array.isArray(parsed) && parsed.length > 0 ? parsed : fallback;
      setDiscoveredBuyers(buyers);
      setDiscLog(p=>[...p, `✅ ${buyers.length} matched buyers found`]);
    } catch(err) {
      // AI failed — show fallback data so UI always has results
      setDiscoveredBuyers(fallback);
      setDiscLog(p=>[...p, `✅ ${fallback.length} buyers found (from database)`]);
    }
    setDiscovering(false);
  };

  // ── Computed ──────────────────────────────────────────────────────
  const pendingKYC   = users.filter(u=>u.kycStatus==='pending');
  const totalGMV     = listings.reduce((s,l)=>s+Number(l.bookValue||0),0);
  const closedDeals  = enquiries.filter(e=>e.status==='accepted');
  const dealsByStatus = {
    active:     deals.filter(d=>d.status==='active').length,
    dispatched: deals.filter(d=>d.status==='dispatched').length,
    completed:  deals.filter(d=>d.status==='completed').length,
    disputed:   deals.filter(d=>d.status==='disputed').length,
  };

  const TABS = [
    { k:'overview',  ico:'📊', lbl:'Overview' },
    { k:'deals',     ico:'🤝', lbl:'Deals',      badge:deals.filter(d=>d.status==='active').length },
    { k:'kyc',       ico:'🔐', lbl:'KYC Queue',  badge:pendingKYC.length },
    { k:'listings',  ico:'📦', lbl:'Listings' },
    { k:'users',     ico:'👥', lbl:'Users' },
    { k:'discovery', ico:'🎯', lbl:'Buyer AI' },
  ];

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>Loading admin data…</div>;

  // ── Edit modals ───────────────────────────────────────────────────
  const EditModal = ({ title, onClose, onSave, children }) => (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div style={{ background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:480,maxHeight:'90vh',overflowY:'auto' }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18 }}>
          <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:16,color:'var(--navy)' }}>{title}</div>
          <button onClick={onClose} style={{ background:'var(--off)',border:'none',borderRadius:'50%',width:30,height:30,cursor:'pointer',fontSize:16 }}>×</button>
        </div>
        {children}
        <div style={{ display:'flex',gap:10,marginTop:16 }}>
          <button className="btn btn-out" style={{ flex:1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" style={{ flex:2 }} onClick={onSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection: mob ? 'column' : 'row', minHeight:'calc(100vh - 52px)' }}>
      {toast && <Toast msg={toast.msg} type={toast.type}/>}

      {/* ── Edit User Modal ── */}
      {editingUser && (
        <EditModal title="Edit User" onClose={()=>setEditingUser(null)} onSave={saveUser}>
          {[['name','Full Name'],['company','Company'],['gst','GST Number'],['phone','Phone'],['city','City'],['state','State']].map(([k,l])=>(
            <div className="fgrp" key={k}>
              <label className="flabel">{l}</label>
              <input className="finput" value={editingUser[k]||''} onChange={e=>setEditingUser(p=>({...p,[k]:e.target.value}))}/>
            </div>
          ))}
          <div className="fgrp">
            <label className="flabel">KYC Status</label>
            <select className="fselect" value={editingUser.kycStatus||'pending'} onChange={e=>setEditingUser(p=>({...p,kycStatus:e.target.value}))}>
              {['pending','approved','rejected'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </EditModal>
      )}

      {/* ── Edit Listing Modal ── */}
      {editingListing && (
        <EditModal title="Edit Listing" onClose={()=>setEditingListing(null)} onSave={saveListing}>
          <div className="fgrp">
            <label className="flabel">Title</label>
            <input className="finput" value={editingListing.title||''} onChange={e=>setEditingListing(p=>({...p,title:e.target.value}))}/>
          </div>
          <div className="frow">
            <div className="fgrp">
              <label className="flabel">Price</label>
              <input className="finput" value={editingListing.price||''} onChange={e=>setEditingListing(p=>({...p,price:e.target.value}))}/>
            </div>
            <div className="fgrp">
              <label className="flabel">Quantity</label>
              <input className="finput" value={editingListing.qty||''} onChange={e=>setEditingListing(p=>({...p,qty:e.target.value}))}/>
            </div>
          </div>
          <div className="fgrp">
            <label className="flabel">Status</label>
            <select className="fselect" value={editingListing.status||'active'} onChange={e=>setEditingListing(p=>({...p,status:e.target.value}))}>
              {['active','sold','suspended','draft'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="fgrp">
            <label className="flabel">Condition</label>
            <select className="fselect" value={editingListing.condition||''} onChange={e=>setEditingListing(p=>({...p,condition:e.target.value}))}>
              {['New','Like New','Used — Good','Used — Fair','Damaged','Sealed — Unused'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="fgrp">
            <label className="flabel">Description</label>
            <textarea className="ftextarea" value={editingListing.description||''} onChange={e=>setEditingListing(p=>({...p,description:e.target.value}))}/>
          </div>
        </EditModal>
      )}

      {/* ── Edit Deal Modal ── */}
      {editingDeal && (
        <EditModal title="Edit Deal" onClose={()=>setEditingDeal(null)} onSave={saveDeal}>
          <div className="fgrp">
            <label className="flabel">Deal Status</label>
            <select className="fselect" value={editingDeal.status||'active'} onChange={e=>setEditingDeal(p=>({...p,status:e.target.value}))}>
              {['active','inspection_scheduled','dispatched','payment_received','completed','disputed','cancelled'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="frow">
            <div className="fgrp">
              <label className="flabel">Agreed Price</label>
              <input className="finput" value={editingDeal.agreedPrice||''} onChange={e=>setEditingDeal(p=>({...p,agreedPrice:e.target.value}))}/>
            </div>
            <div className="fgrp">
              <label className="flabel">Agreed Qty</label>
              <input className="finput" value={editingDeal.agreedQty||''} onChange={e=>setEditingDeal(p=>({...p,agreedQty:e.target.value}))}/>
            </div>
          </div>
          <div className="fgrp">
            <label className="flabel">Admin Notes (internal)</label>
            <textarea className="ftextarea" placeholder="Internal notes about this deal…" value={editingDeal.adminNotes||''} onChange={e=>setEditingDeal(p=>({...p,adminNotes:e.target.value}))}/>
          </div>
        </EditModal>
      )}

      {/* ── Invoice Modal ── */}
      {invoiceDeal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:420 }}>
            <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:16,color:'var(--navy)',marginBottom:14 }}>Generate Platform Invoice</div>
            <div style={{ background:'var(--off)',borderRadius:10,padding:'12px 14px',marginBottom:14,fontSize:13 }}>
              <div style={{ fontWeight:700,color:'var(--navy)',marginBottom:4 }}>{invoiceDeal.listingTitle}</div>
              {[
                ['Seller',       invoiceDeal.sellerCompany],
                ['Buyer',        invoiceDeal.buyerCompany],
                ['Deal Value',   invoiceDeal.agreedPrice],
                ['Platform Fee (3%)', '₹'+Math.round(Number((invoiceDeal.agreedPrice||'0').replace(/[^0-9.]/g,''))*0.03).toLocaleString('en-IN')],
                ['GST (18%)',    '₹'+Math.round(Number((invoiceDeal.agreedPrice||'0').replace(/[^0-9.]/g,''))*0.03*0.18).toLocaleString('en-IN')],
              ].map(([k,v])=>(
                <div key={k} style={{ display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--border)',fontSize:12 }}>
                  <span style={{ color:'var(--muted)' }}>{k}</span>
                  <span style={{ fontWeight:700,color:'var(--navy)' }}>{v}</span>
                </div>
              ))}
              <div style={{ display:'flex',justifyContent:'space-between',padding:'8px 0',fontSize:13 }}>
                <span style={{ fontWeight:800 }}>TOTAL PAYABLE</span>
                <span style={{ fontWeight:800,color:'var(--gold)' }}>₹{(Math.round(Number((invoiceDeal.agreedPrice||'0').replace(/[^0-9.]/g,''))*0.03*1.18)).toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div style={{ display:'flex',gap:10 }}>
              <button className="btn btn-out" style={{ flex:1 }} onClick={()=>setInvoiceDeal(null)}>Cancel</button>
              <button className="btn btn-gold" style={{ flex:2 }} onClick={()=>{ generateInvoice(invoiceDeal); }}>🧾 View Invoice</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice View (in-app, sandbox-safe) ── */}
      {showInvoice && (() => {
        const deal   = showInvoice;
        const invNo  = `SINV-${new Date().getFullYear()}-${String(deal._id||'').slice(-6).toUpperCase()}`;
        const date   = new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
        const base   = Number((deal.agreedPrice||'0').replace(/[^0-9.]/g,''));
        const fee    = Math.round(base * 0.03);
        const gst    = Math.round(fee * 0.18);
        const total  = fee + gst;
        return (
          <div style={{ position:'fixed',inset:0,background:'#f4f6fa',zIndex:998,overflowY:'auto' }}>
            <div style={{ maxWidth:680,margin:'0 auto',padding:'16px 16px 40px' }}>
              {/* Toolbar */}
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,padding:'12px 16px',background:'var(--navy)',borderRadius:12 }}>
                <div style={{ fontFamily:"var(--fh)",fontWeight:800,color:'#fff',fontSize:15 }}>📄 Tax Invoice — {invNo}</div>
                <div style={{ display:'flex',gap:8 }}>
                  <button className="btn btn-gold btn-sm" onClick={()=>{
                    try {
                      const el = document.getElementById('surpliq-invoice');
                      if(el) { window.print(); }
                    } catch(e) { alert('Print: Use browser menu → Print / Save as PDF'); }
                  }}>🖨️ Print</button>
                  <button className="btn btn-out btn-sm" style={{ color:'rgba(255,255,255,.7)',borderColor:'rgba(255,255,255,.2)' }} onClick={()=>setShowInvoice(null)}>✕ Close</button>
                </div>
              </div>

              {/* Invoice document */}
              <div id="surpliq-invoice" style={{ background:'#fff',borderRadius:14,padding:'28px 28px',border:'1.5px solid var(--border)',boxShadow:'0 4px 20px rgba(0,0,0,.06)' }}>

                {/* Header */}
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24,paddingBottom:18,borderBottom:'3px solid #C8A84B' }}>
                  <div>
                    <div style={{ fontFamily:"var(--fh)",fontWeight:900,fontSize:22,letterSpacing:-1 }}>
                      <span style={{ color:'#C8A84B' }}>Surp</span>liq
                    </div>
                    <div style={{ fontSize:11,color:'#666',marginTop:4,lineHeight:1.6 }}>
                      Surpliq Technologies Pvt. Ltd.<br/>
                      India · support@surpliq.com
                    </div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:26,fontWeight:900,color:'#C8A84B',letterSpacing:-1 }}>TAX INVOICE</div>
                    <div style={{ fontSize:12,color:'#666',marginTop:4 }}>Invoice No: <strong style={{ color:'var(--navy)' }}>{invNo}</strong></div>
                    <div style={{ fontSize:12,color:'#666' }}>Date: <strong>{date}</strong></div>
                  </div>
                </div>

                {/* Status badge */}
                <div style={{ display:'inline-block',background:'#dcfce7',color:'#15803d',padding:'4px 14px',borderRadius:20,fontWeight:800,fontSize:12,marginBottom:18 }}>
                  ✓ DEAL CLOSED
                </div>

                {/* Parties */}
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20 }}>
                  {[
                    { label:'Billed To (Seller)', company:deal.sellerCompany||'—', name:deal.sellerName||'—', city:deal.sellerCity||'—', note:'GST: [Seller GST]' },
                    { label:'Supplied By', company:'Surpliq Technologies Pvt. Ltd.', name:'Platform Marketplace Services', city:'India', note:'GST: [Surpliq GST]' },
                  ].map(p => (
                    <div key={p.label} style={{ background:'#f8f9fa',borderRadius:10,padding:'14px' }}>
                      <div style={{ fontSize:10,fontWeight:800,textTransform:'uppercase',letterSpacing:.5,color:'#666',marginBottom:8 }}>{p.label}</div>
                      <div style={{ fontWeight:800,fontSize:13,color:'var(--navy)',marginBottom:3 }}>{p.company}</div>
                      <div style={{ fontSize:12,color:'#555',marginBottom:2 }}>{p.name}</div>
                      <div style={{ fontSize:12,color:'#555',marginBottom:2 }}>📍 {p.city}</div>
                      <div style={{ fontSize:11,color:'#888' }}>{p.note}</div>
                    </div>
                  ))}
                </div>

                {/* Deal reference */}
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:11,fontWeight:800,textTransform:'uppercase',letterSpacing:.5,color:'var(--navy)',marginBottom:8,borderLeft:'4px solid #C8A84B',paddingLeft:8 }}>Deal Reference</div>
                  <div style={{ border:'1px solid var(--border)',borderRadius:8,overflow:'hidden' }}>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',background:'var(--navy)',padding:'8px 12px' }}>
                      {['Description','Deal Ref','Parties'].map(h=><div key={h} style={{ fontSize:11,fontWeight:700,color:'#fff' }}>{h}</div>)}
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr',padding:'10px 12px',background:'#fff' }}>
                      <div style={{ fontSize:12,color:'var(--navy)' }}>Platform Marketplace Service Fee</div>
                      <div style={{ fontSize:11,color:'#666' }}>{String(deal._id||'').slice(-8)}</div>
                      <div style={{ fontSize:11,color:'#666' }}>{(deal.sellerCompany||'').slice(0,12)} → {(deal.buyerCompany||'').slice(0,12)}</div>
                    </div>
                    <div style={{ padding:'6px 12px',background:'#f8f9fa',borderTop:'1px solid var(--border)',fontSize:11,color:'#666' }}>
                      Listing: {deal.listingTitle?.slice(0,60)||'—'}
                    </div>
                  </div>
                </div>

                {/* Fee table */}
                <div style={{ marginBottom:18 }}>
                  <div style={{ fontSize:11,fontWeight:800,textTransform:'uppercase',letterSpacing:.5,color:'var(--navy)',marginBottom:8,borderLeft:'4px solid #C8A84B',paddingLeft:8 }}>Fee Calculation</div>
                  <div style={{ border:'1px solid var(--border)',borderRadius:8,overflow:'hidden' }}>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',background:'var(--navy)',padding:'8px 12px' }}>
                      {['Item','Deal Value','Rate','Amount'].map(h=><div key={h} style={{ fontSize:11,fontWeight:700,color:'#fff' }}>{h}</div>)}
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',padding:'10px 12px',borderBottom:'1px solid var(--border)' }}>
                      <div style={{ fontSize:12 }}>Platform Success Fee</div>
                      <div style={{ fontSize:12 }}>₹{base.toLocaleString('en-IN')}</div>
                      <div style={{ fontSize:12 }}>3%</div>
                      <div style={{ fontSize:12,fontWeight:700 }}>₹{fee.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',padding:'8px 12px',borderBottom:'1px solid var(--border)',background:'#fafafa' }}>
                      <div style={{ fontSize:11,color:'#666',gridColumn:'1/4' }}>GST @ 18% on platform fee</div>
                      <div style={{ fontSize:11,color:'#666' }}>₹{gst.toLocaleString('en-IN')}</div>
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr',padding:'12px',background:'#FFF9E6',borderTop:'2px solid #C8A84B' }}>
                      <div style={{ fontSize:14,fontWeight:800,color:'var(--navy)',gridColumn:'1/4' }}>TOTAL PAYABLE</div>
                      <div style={{ fontSize:14,fontWeight:800,color:'#C8A84B' }}>₹{total.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                </div>

                {/* Bank details */}
                <div style={{ background:'#f0f4ff',borderRadius:10,padding:'14px 16px',marginBottom:18,fontSize:12 }}>
                  <div style={{ fontWeight:800,color:'var(--navy)',marginBottom:6 }}>Payment Details</div>
                  <div style={{ color:'#555',lineHeight:1.8 }}>
                    NEFT/IMPS to: <strong>Surpliq Technologies Pvt. Ltd.</strong><br/>
                    Bank: [Bank Name] · A/C: [Account No.] · IFSC: [IFSC Code]<br/>
                    Reference: <strong>{invNo}</strong> · Due within 7 days of deal closure.
                  </div>
                </div>

                {/* Footer */}
                <div style={{ borderTop:'1px solid var(--border)',paddingTop:14,fontSize:10,color:'#999',textAlign:'center',lineHeight:1.8 }}>
                  This is a computer-generated invoice from Surpliq Technologies Pvt. Ltd.<br/>
                  For queries: support@surpliq.com · Subject to Indian jurisdiction.<br/>
                  © {new Date().getFullYear()} Surpliq Technologies Pvt. Ltd. · All rights reserved.
                </div>
              </div>

              {/* Bottom actions */}
              <div style={{ display:'flex',gap:10,marginTop:16 }}>
                <button className="btn btn-out" style={{ flex:1 }} onClick={()=>setShowInvoice(null)}>← Back to Deals</button>
                <button className="btn btn-gold" style={{ flex:2 }} onClick={()=>{ try{ window.print(); } catch(e){ alert('Use browser menu → Print → Save as PDF'); } }}>
                  🖨️ Print / Save as PDF
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Sidebar ── */}
      <div className="sidebar">
        <div className="sb-sec">Admin</div>
        {TABS.map(t=>(
          <div key={t.k} className={`sb-item ${tab===t.k?'act':''}`} onClick={()=>setTab(t.k)}>
            <span>{t.ico}</span><span>{t.lbl}</span>
            {t.badge>0&&<span className="sb-badge sbb-r">{t.badge}</span>}
          </div>
        ))}
      </div>

      <div className="main">

        {/* ── OVERVIEW ── */}
        {tab==='overview' && (
          <div className="fu">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18, flexWrap:'wrap', gap:10 }}>
              <div>
                <div className="eye">Admin Dashboard</div>
                <div className="h1">Platform Overview</div>
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button className="btn btn-out btn-sm" onClick={()=>{ setKycStatus('pending'); setTab('kyc'); }}>
                  ⏳ KYC Queue ({pendingKYC.length})
                </button>
                <button className="btn btn-out btn-sm" onClick={()=>{ setDealStatus('disputed'); setTab('deals'); }}>
                  ⚠️ Disputes ({dealsByStatus.disputed})
                </button>
                <button className="btn btn-gold btn-sm" onClick={()=>loadAll()}>
                  🔄 Refresh
                </button>
              </div>
            </div>
            <div className="kpi-grid" style={{ gridTemplateColumns:mob?'1fr 1fr':'repeat(4,1fr)', marginBottom:14 }}>
              {[
                {n:`₹${(totalGMV/10000000).toFixed(1)}Cr`, l:'Total GMV Listed',  c:'var(--gold)'},
                {n:users.length,                            l:'Registered Users',  c:'var(--blue)'},
                {n:listings.filter(l=>l.status==='active').length, l:'Active Listings', c:'var(--green)'},
                {n:pendingKYC.length,                       l:'KYC Pending',       c:pendingKYC.length>0?'var(--red)':'var(--green)'},
                {n:enquiries.length,                        l:'Total Enquiries',   c:'var(--navy)'},
                {n:closedDeals.length,                      l:'Deals Closed',      c:'var(--green)'},
                {n:users.filter(u=>u.role==='seller').length, l:'Sellers',         c:'var(--amber)'},
                {n:users.filter(u=>u.role==='buyer').length,  l:'Buyers',          c:'var(--blue)'},
              ].map((s,i)=>(
                <div className="kpi" key={i}><div className="kpi-n" style={{color:s.c}}>{s.n}</div><div className="kpi-l">{s.l}</div></div>
              ))}
            </div>
            {/* Deal pipeline summary */}
            <div className="card" style={{ marginBottom:14 }}>
              <div className="h2" style={{ marginBottom:14 }}>Deal Pipeline</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
                {[
                  ['🟡','Active',       dealsByStatus.active,     'var(--amber)'],
                  ['🚚','Dispatched',   dealsByStatus.dispatched,  'var(--blue)'],
                  ['✅','Completed',    dealsByStatus.completed,   'var(--green)'],
                  ['⚠️','Disputed',    dealsByStatus.disputed,    'var(--red)'],
                ].map(([ico,label,count,color])=>(
                  <div key={label} style={{ textAlign:'center', padding:'14px 8px', background:'var(--off)', borderRadius:10, border:'1.5px solid var(--border)', cursor:'pointer' }}
                    onClick={()=>setTab('deals')}>
                    <div style={{ fontSize:24, marginBottom:4 }}>{ico}</div>
                    <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:20, color }}>{count}</div>
                    <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Recent activity */}
            <div className="card">
              <div className="h2" style={{ marginBottom:14 }}>Recent Deals</div>
              {deals.slice(0,5).map((d,i)=>(
                <div key={d._id||i} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight:700,fontSize:13,color:'var(--navy)' }}>{d.listingTitle?.slice(0,40)||'Deal'}</div>
                    <div style={{ fontSize:11,color:'var(--muted)' }}>{d.sellerCompany} → {d.buyerCompany} · {d.agreedPrice}</div>
                  </div>
                  <span className={`pill ${d.status==='completed'?'pill-green':d.status==='disputed'?'pill-red':d.status==='dispatched'?'pill-blue':'pill-amber'}`} style={{ fontSize:10 }}>
                    {d.status||'active'}
                  </span>
                </div>
              ))}
              {deals.length===0 && <div style={{ color:'var(--muted)',textAlign:'center',padding:20,fontSize:13 }}>No deals yet.</div>}
              {deals.length>0 && <button className="btn btn-out btn-sm" style={{ marginTop:12,width:'100%' }} onClick={()=>setTab('deals')}>View All Deals →</button>}
            </div>
          </div>
        )}

        {/* ── DEALS ── */}
        {tab==='deals' && (
          <div className="fu">
            {mob && <button className="btn btn-out btn-sm" style={{ marginBottom:12 }} onClick={()=>setTab('overview')}>← Overview</button>}
            <div className="eye">Deal Management</div>
            <div className="h1" style={{ marginBottom:12 }}>All Deals</div>
            {/* Search + Filter */}
            <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:160, position:'relative' }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:13 }}>🔍</span>
                <input className="finput" style={{ paddingLeft:30 }} placeholder="Search listing, buyer, seller…"
                  value={dealSearch} onChange={e=>setDealSearch(e.target.value)}/>
              </div>
              <select className="fselect" style={{ width:'auto', minWidth:130 }} value={dealStatus} onChange={e=>setDealStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                {['active','inspection_scheduled','dispatched','payment_received','completed','disputed','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {(dealSearch||dealStatus!=='all') && (
                <button className="btn btn-out btn-sm" onClick={()=>{setDealSearch('');setDealStatus('all');}}>✕ Clear</button>
              )}
            </div>

            {/* Pipeline stats */}
            <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:20 }}>
              {[['🟡',dealsByStatus.active,'Active','var(--amber)'],['🚚',dealsByStatus.dispatched,'Dispatched','var(--blue)'],['✅',dealsByStatus.completed,'Completed','var(--green)'],['⚠️',dealsByStatus.disputed,'Disputed','var(--red)']].map(([ico,n,l,c])=>(
                <div key={l} style={{ background:'#fff',border:'1.5px solid var(--border)',borderRadius:12,padding:'12px',textAlign:'center' }}>
                  <div style={{ fontSize:20 }}>{ico}</div>
                  <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:20,color:c }}>{n}</div>
                  <div style={{ fontSize:11,color:'var(--muted)' }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Deal cards from enquiries (accepted) */}
            {closedDeals.length===0 && deals.length===0 ? (
              <div className="card" style={{ textAlign:'center',padding:32,color:'var(--muted)' }}>
                <div style={{ fontSize:40,marginBottom:12 }}>🤝</div>
                <div>No deals closed yet.</div>
              </div>
            ) : (
              [...closedDeals, ...deals.filter(d=>!closedDeals.find(e=>e._id===d.enquiryId))]
              .filter(item => {
                const q = dealSearch.toLowerCase();
                if (q && ![(item.listingTitle||''),(item.sellerCompany||''),(item.buyerCompany||'')].some(v=>v.toLowerCase().includes(q))) return false;
                if (dealStatus!=='all') {
                  const st = item.status==='accepted'?'active':item.status;
                  if (st !== dealStatus) return false;
                }
                return true;
              })
              .map((item,i)=>{
                const isEnquiry = item.thread !== undefined;
                const d = isEnquiry ? {
                  _id: item._id,
                  listingTitle: item.listingTitle,
                  sellerCompany: item.sellerCompany||'—',
                  buyerCompany: item.buyerCompany||'—',
                  buyerCity: item.buyerCity||'—',
                  sellerCity: item.sellerCity||'—',
                  agreedPrice: item.dealPrice||'—',
                  agreedQty: item.dealQty||'—',
                  status: item.status==='accepted'?'active':item.status,
                  createdAt: item.closedAt||item.updatedAt,
                  adminNotes: item.adminNotes||'',
                } : item;

                const statusColor = d.status==='completed'?'var(--green)':d.status==='disputed'?'var(--red)':d.status==='dispatched'?'var(--blue)':'var(--amber)';

                return (
                  <div key={d._id||i} className="card" style={{ marginBottom:12 }}>
                    {/* Deal header */}
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:14,color:'var(--navy)',marginBottom:4 }}>
                          {d.listingTitle?.slice(0,50)||'Deal'}
                        </div>
                        <div style={{ fontSize:12,color:'var(--muted)' }}>
                          🏭 {d.sellerCompany} → 🛒 {d.buyerCompany}
                        </div>
                        <div style={{ fontSize:11,color:'var(--muted)',marginTop:2 }}>
                          📍 {d.sellerCity} → {d.buyerCity} · Closed: {d.createdAt?new Date(d.createdAt).toLocaleDateString('en-IN'):'—'}
                        </div>
                      </div>
                      <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6 }}>
                        <span className="pill" style={{ fontSize:10, background:`${statusColor}20`, color:statusColor, border:`1px solid ${statusColor}40` }}>
                          {d.status||'active'}
                        </span>
                        <div style={{ fontFamily:"var(--fh)",fontWeight:800,fontSize:15,color:'var(--navy)' }}>{d.agreedPrice}</div>
                      </div>
                    </div>

                    {/* Deal terms row */}
                    <div style={{ display:'flex',gap:16,flexWrap:'wrap',fontSize:12,color:'var(--muted)',padding:'8px 0',borderTop:'1px solid var(--border)',borderBottom:'1px solid var(--border)',marginBottom:12 }}>
                      <span>⚖️ Qty: <strong style={{ color:'var(--navy)' }}>{d.agreedQty||'—'}</strong></span>
                      <span>💰 Fee: <strong style={{ color:'var(--gold)' }}>₹{Math.round(Number((d.agreedPrice||'0').replace(/[^0-9.]/g,''))*0.03).toLocaleString('en-IN')}</strong></span>
                      <span>📋 Status: <strong style={{ color:statusColor }}>{d.status||'active'}</strong></span>
                    </div>

                    {/* Admin notes */}
                    {d.adminNotes && (
                      <div style={{ background:'rgba(200,168,75,.07)',border:'1px solid rgba(200,168,75,.15)',borderRadius:8,padding:'8px 12px',fontSize:11,color:'var(--navy)',marginBottom:12 }}>
                        📝 Admin Notes: {d.adminNotes}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                      <button className="btn btn-out btn-sm" onClick={()=>setEditingDeal({...d})}>✏️ Edit Status</button>
                      <button className="btn btn-gold btn-sm" onClick={()=>setInvoiceDeal(d)}>🧾 Invoice</button>
                      <button className="btn btn-out btn-sm" onClick={()=>{
                        const msg = `SURPLIQ DEAL UPDATE\nDeal: ${d.listingTitle}\nSeller: ${d.sellerCompany}\nBuyer: ${d.buyerCompany}\nPrice: ${d.agreedPrice}\nStatus: ${d.status}\n\nPlease coordinate pickup and payment as agreed. Contact support@surpliq.com for any issues.`;
                        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,'_blank');
                      }}>💬 Notify</button>
                      <button className="btn btn-out btn-sm" onClick={()=>window.open(`mailto:?subject=Deal Update: ${d.listingTitle}&body=${encodeURIComponent(`Dear Parties,\n\nYour deal for ${d.listingTitle} is now in status: ${d.status}.\nSeller: ${d.sellerCompany}\nBuyer: ${d.buyerCompany}\nAgreed Price: ${d.agreedPrice}\n\nPlease coordinate pickup and payment.\n\nRegards,\nSurpliq Team\nsupport@surpliq.com`)}`,'_blank')}>📧 Email Both</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── KYC ── */}
        {tab==='kyc' && (
          <div className="fu">
            {mob && <button className="btn btn-out btn-sm" style={{ marginBottom:12 }} onClick={()=>setTab('overview')}>← Overview</button>}
            <div className="eye">KYC Management</div>
            <div className="h1" style={{ marginBottom:12 }}>KYC Queue — {pendingKYC.length} Pending</div>
            {/* Search + Filter */}
            <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:160, position:'relative' }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:13 }}>🔍</span>
                <input className="finput" style={{ paddingLeft:30 }} placeholder="Search name, company, GST…"
                  value={kycSearch} onChange={e=>setKycSearch(e.target.value)}/>
              </div>
              <select className="fselect" style={{ width:'auto', minWidth:130 }} value={kycStatus} onChange={e=>setKycStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="pending">⏳ Pending</option>
                <option value="approved">✅ Approved</option>
                <option value="rejected">❌ Rejected</option>
              </select>
              {(kycSearch||kycStatus!=='all') && (
                <button className="btn btn-out btn-sm" onClick={()=>{setKycSearch('');setKycStatus('all');}}>✕ Clear</button>
              )}
            </div>
            {users.filter(u=>{
              const q=kycSearch.toLowerCase();
              if(q && ![(u.name||''),(u.company||''),(u.gst||''),(u.email||'')].some(v=>v.toLowerCase().includes(q))) return false;
              if(kycStatus!=='all' && u.kycStatus!==kycStatus) return false;
              return true;
            }).length===0 && <div className="card" style={{ textAlign:'center',padding:24,color:'var(--muted)' }}>No users match your search.</div>}
            {users.filter(u=>{
              const q=kycSearch.toLowerCase();
              if(q && ![(u.name||''),(u.company||''),(u.gst||''),(u.email||'')].some(v=>v.toLowerCase().includes(q))) return false;
              if(kycStatus!=='all' && u.kycStatus!==kycStatus) return false;
              return true;
            }).map(u=>(
              <div className="card" key={u._id} style={{ marginBottom:12 }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10 }}>
                  <div>
                    <div style={{ fontWeight:800,fontSize:14,color:'var(--navy)' }}>{u.name}</div>
                    <div style={{ fontSize:12,color:'var(--muted)',marginTop:2 }}>{u.company} · {u.role?.toUpperCase()} · {u.city}, {u.state}</div>
                    <div style={{ fontSize:12,color:'var(--muted)' }}>GST: {u.gst||'Not provided'} · {u.email}</div>
                  </div>
                  <span className={`pill ${u.kycStatus==='approved'?'pill-green':u.kycStatus==='rejected'?'pill-red':'pill-amber'}`}>
                    {u.kycStatus==='approved'?'✅ Approved':u.kycStatus==='rejected'?'❌ Rejected':'⏳ Pending'}
                  </span>
                </div>
                <div style={{ display:'flex',gap:8 }}>
                  {u.kycStatus!=='approved' && <button className="btn btn-green btn-sm" onClick={()=>approveKYC(u._id)}>✅ Approve</button>}
                  {u.kycStatus!=='rejected' && <button className="btn btn-out btn-sm" onClick={()=>rejectKYC(u._id)}>❌ Reject</button>}
                  <button className="btn btn-out btn-sm" onClick={()=>setEditingUser({...u})}>✏️ Edit</button>
                  {u.role==='seller' && (
                    <button className="btn btn-sm"
                      style={{ background:u.isPro||u.plan==='pro'?'rgba(200,168,75,.15)':'var(--off)', color:u.isPro||u.plan==='pro'?'var(--gold)':'var(--muted)', border:`1px solid ${u.isPro||u.plan==='pro'?'rgba(200,168,75,.3)':'var(--border)'}`, fontSize:10 }}
                      onClick={async()=>{
                        const newPro = !(u.isPro||u.plan==='pro');
                        await DB.update('users',u._id,{isPro:newPro,plan:newPro?'pro':'free'});
                        setUsers(p=>p.map(x=>x._id===u._id?{...x,isPro:newPro,plan:newPro?'pro':'free'}:x));
                        showToast(newPro?`${u.name} upgraded to Pro ⭐`:`${u.name} downgraded to Free`);
                      }}>
                      {u.isPro||u.plan==='pro'?'⭐ Pro — Revoke':'Upgrade to Pro'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── LISTINGS ── */}
        {tab==='listings' && (() => {
          const filteredListings = listings.filter(l => {
            const q = listSearch.toLowerCase();
            if (q && ![(l.title||''),(l.sellerCompany||''),(l.location||'')].some(v=>v.toLowerCase().includes(q))) return false;
            if (listCat!=='all' && l.category!==listCat) return false;
            if (listStatus!=='all' && l.status!==listStatus) return false;
            return true;
          });
          return (
          <div className="fu">
            {mob && <button className="btn btn-out btn-sm" style={{ marginBottom:12 }} onClick={()=>setTab('overview')}>← Overview</button>}
            <div className="eye">Listing Management</div>
            <div className="h1" style={{ marginBottom:12 }}>{listings.length} Listings</div>
            {/* Search + Filters */}
            <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:160, position:'relative' }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:13 }}>🔍</span>
                <input className="finput" style={{ paddingLeft:30 }} placeholder="Search title, seller, location…"
                  value={listSearch} onChange={e=>setListSearch(e.target.value)}/>
              </div>
              <select className="fselect" style={{ width:'auto', minWidth:100 }} value={listCat} onChange={e=>setListCat(e.target.value)}>
                <option value="all">All Cats</option>
                {['RM','BOP','MRO','CE','FG'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <select className="fselect" style={{ width:'auto', minWidth:110 }} value={listStatus} onChange={e=>setListStatus(e.target.value)}>
                <option value="all">All Status</option>
                {['active','sold','suspended','draft'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {(listSearch||listCat!=='all'||listStatus!=='all') && (
                <button className="btn btn-out btn-sm" onClick={()=>{setListSearch('');setListCat('all');setListStatus('all');}}>✕ Clear</button>
              )}
            </div>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>
              Showing {filteredListings.length} of {listings.length} listings
            </div>
            {filteredListings.length===0 && <div className="card" style={{ textAlign:'center',padding:24,color:'var(--muted)' }}>No listings match your filters.</div>}
            {filteredListings.map(l=>(
              <div className="card" key={l._id} style={{ marginBottom:10 }}>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:800,fontSize:13,color:'var(--navy)',marginBottom:3 }}>{l.title}</div>
                    <div style={{ fontSize:11,color:'var(--muted)' }}>
                      {l.sellerCompany} · {l.location} · {l.qty} {l.unit} · ₹{Number(l.price||0).toLocaleString('en-IN')}{l.priceUnit}
                    </div>
                    <div style={{ fontSize:11,color:'var(--muted)',marginTop:2 }}>
                      👁 {l.views||0} · 💬 {l.enquiryCount||0} enq
                    </div>
                  </div>
                  <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6 }}>
                    <CatBadge cat={l.category}/>
                    <span className={`pill ${l.status==='active'?'pill-green':l.status==='sold'?'pill-blue':'pill-navy'}`} style={{ fontSize:10 }}>
                      {l.status}
                    </span>
                  </div>
                </div>
                <div style={{ display:'flex',gap:8 }}>
                  <button className="btn btn-out btn-sm" onClick={()=>setEditingListing({...l})}>✏️ Edit</button>
                  <button className="btn btn-out btn-sm" onClick={()=>{ setTab('discovery'); runDiscovery(l); }}>🎯 Find Buyers</button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {/* ── USERS ── */}
        {tab==='users' && (() => {
          const filteredUsers = users.filter(u => {
            const q = userSearch.toLowerCase();
            if (q && ![(u.name||''),(u.company||''),(u.email||''),(u.gst||''),(u.city||'')].some(v=>v.toLowerCase().includes(q))) return false;
            if (userRole!=='all' && u.role!==userRole) return false;
            if (userKyc!=='all' && u.kycStatus!==userKyc) return false;
            return true;
          });
          return (
          <div className="fu">
            {mob && <button className="btn btn-out btn-sm" style={{ marginBottom:12 }} onClick={()=>setTab('overview')}>← Overview</button>}
            <div className="eye">User Management</div>
            <div className="h1" style={{ marginBottom:12 }}>{users.length} Registered Users</div>
            {/* Search + Filters */}
            <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:160, position:'relative' }}>
                <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--muted)', fontSize:13 }}>🔍</span>
                <input className="finput" style={{ paddingLeft:30 }} placeholder="Search name, company, email, GST…"
                  value={userSearch} onChange={e=>setUserSearch(e.target.value)}/>
              </div>
              <select className="fselect" style={{ width:'auto', minWidth:100 }} value={userRole} onChange={e=>setUserRole(e.target.value)}>
                <option value="all">All Roles</option>
                <option value="seller">Sellers</option>
                <option value="buyer">Buyers</option>
                <option value="admin">Admins</option>
              </select>
              <select className="fselect" style={{ width:'auto', minWidth:110 }} value={userKyc} onChange={e=>setUserKyc(e.target.value)}>
                <option value="all">All KYC</option>
                <option value="pending">⏳ Pending</option>
                <option value="approved">✅ Approved</option>
                <option value="rejected">❌ Rejected</option>
              </select>
              {(userSearch||userRole!=='all'||userKyc!=='all') && (
                <button className="btn btn-out btn-sm" onClick={()=>{setUserSearch('');setUserRole('all');setUserKyc('all');}}>✕ Clear</button>
              )}
            </div>
            <div style={{ fontSize:12, color:'var(--muted)', marginBottom:14 }}>
              Showing {filteredUsers.length} of {users.length} users
              {userRole!=='all' && ` · ${userRole}s`}
              {userKyc!=='all' && ` · KYC: ${userKyc}`}
            </div>
            {filteredUsers.length===0 && <div style={{ textAlign:'center',padding:24,color:'var(--muted)',background:'#fff',borderRadius:12,border:'1.5px solid var(--border)' }}>No users match your filters.</div>}
            {filteredUsers.map(u=>(
              <div key={u._id} style={{ background:'#fff',border:'1.5px solid var(--border)',borderRadius:12,padding:'12px 14px',marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                <div>
                  <div style={{ fontWeight:700,fontSize:13,color:'var(--navy)' }}>{u.name} <span style={{ fontSize:10,color:'var(--muted)',fontWeight:400 }}>({u.role})</span></div>
                  <div style={{ fontSize:11,color:'var(--muted)' }}>{u.company} · {u.city}, {u.state} · GST: {u.gst||'—'}</div>
                  <div style={{ fontSize:11,color:'var(--muted)' }}>{u.email}</div>
                </div>
                <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6 }}>
                  <span className={`pill ${u.kycStatus==='approved'?'pill-green':u.kycStatus==='rejected'?'pill-red':'pill-amber'}`} style={{ fontSize:10 }}>
                    {u.kycStatus==='approved'?'✅ KYC':u.kycStatus==='rejected'?'❌ KYC':'⏳ KYC'}
                  </span>
                  <button className="btn btn-out btn-sm" onClick={()=>setEditingUser({...u})}>✏️ Edit</button>
                  {u.role==='seller' && (
                    <button className="btn btn-sm"
                      style={{ background:u.isPro||u.plan==='pro'?'rgba(200,168,75,.15)':'var(--off)', color:u.isPro||u.plan==='pro'?'var(--gold)':'var(--muted)', border:`1px solid ${u.isPro||u.plan==='pro'?'rgba(200,168,75,.3)':'var(--border)'}`, fontSize:10 }}
                      onClick={async()=>{
                        const newPro = !(u.isPro||u.plan==='pro');
                        await DB.update('users',u._id,{isPro:newPro,plan:newPro?'pro':'free'});
                        setUsers(p=>p.map(x=>x._id===u._id?{...x,isPro:newPro,plan:newPro?'pro':'free'}:x));
                        showToast(newPro?`${u.name} upgraded to Pro ⭐`:`${u.name} downgraded to Free`);
                      }}>
                      {u.isPro||u.plan==='pro'?'⭐ Pro — Revoke':'Upgrade to Pro'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          );
        })()}

        {/* ── BUYER DISCOVERY ── */}
        {tab==='discovery' && (
          <div className="fu">
            {mob && <button className="btn btn-out btn-sm" style={{ marginBottom:12 }} onClick={()=>setTab('overview')}>← Overview</button>}
            <div className="eye">AI Buyer Discovery</div>
            <div className="h1" style={{ marginBottom:8 }}>Find Buyers for Listings</div>
            <div style={{ fontSize:12,color:'var(--muted)',marginBottom:18 }}>
              Select any active listing to discover matched buyers from IndiaMART, TradeIndia, MRAI and Surpliq's own network.
            </div>
            <div className="card" style={{ marginBottom:14 }}>
              <div className="h3" style={{ marginBottom:10 }}>Select Listing</div>
              {listings.filter(l=>l.status==='active').length === 0 ? (
                <div style={{ textAlign:'center', padding:'24px 0', color:'var(--muted)' }}>
                  <div style={{ fontSize:32, marginBottom:10 }}>📦</div>
                  <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>No active listings yet</div>
                  <div style={{ fontSize:12, lineHeight:1.6 }}>
                    Listings will appear here once sellers publish them.<br/>
                    You can also manually trigger buyer discovery below.
                  </div>
                  <button className="btn btn-gold btn-sm" style={{ marginTop:14 }}
                    onClick={()=>runDiscovery({_id:'manual', title:'Industrial Surplus', category:'RM', qty:'10', unit:'MT', location:'Ludhiana, Punjab'})}>
                    🎯 Run Demo Discovery
                  </button>
                </div>
              ) : (
                <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                  {listings.filter(l=>l.status==='active').slice(0,8).map(l=>(
                    <div key={l._id}
                      style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',border:`1.5px solid ${discListing?._id===l._id?'var(--gold)':'var(--border)'}`,borderRadius:9,cursor:'pointer',background:discListing?._id===l._id?'rgba(200,168,75,.05)':'var(--off)',transition:'all .18s' }}
                      onClick={()=>runDiscovery(l)}>
                      <div>
                        <div style={{ fontWeight:700,fontSize:12,color:'var(--navy)' }}>{l.title}</div>
                        <div style={{ fontSize:10,color:'var(--muted)' }}>{l.qty} {l.unit} · {l.location}</div>
                      </div>
                      <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                        <CatBadge cat={l.category}/>
                        <span style={{ fontSize:12,color:'var(--gold)',fontWeight:700 }}>{discovering&&discListing?._id===l._id?'Searching…':'Search →'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {(discovering||discLog.length>0) && (
              <div className="ai-box" style={{ marginBottom:14 }}>
                <span className="ai-lbl">AI BUYER DISCOVERY</span>
                {discLog.map((l,i)=><div key={i} style={{ fontSize:12,color:'rgba(255,255,255,.7)',marginBottom:4 }}>{l}</div>)}
                {discovering&&<div className="ai-dots"><div className="ald"/><div className="ald"/><div className="ald"/></div>}
              </div>
            )}
            {discoveredBuyers.length>0 && (
              <div className="card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                  <div className="h3">{discoveredBuyers.length} Buyers Found</div>
                  <button className="btn btn-out btn-sm" onClick={()=>{setDiscoveredBuyers([]);setDiscLog([]);setDiscListing(null);setNotified({});}}>Clear</button>
                </div>
                <div style={{ fontSize:11,color:'var(--muted)',marginBottom:14 }}>
                  🟢 = Registered on Surpliq · ✅ = GST Verified · For unregistered buyers, an invite + listing link is sent
                </div>
                {discoveredBuyers.map((b,i)=>{
                  const listing = discListing;
                  const shareUrl = `https://surpliq.com/listing/${listing?._id||'demo'}`;
                  // WhatsApp message for registered buyers
                  const waRegistered = encodeURIComponent(
`Hi ${b.name},

We found a surplus lot on Surpliq that matches your purchase profile:

📦 *${listing?.title}*
⚖️ Qty: ${listing?.qty} ${listing?.unit}
📍 Location: ${listing?.location}
💰 Price: ₹${listing?.price}${listing?.priceUnit||''}

As a registered Surpliq buyer, you can view and make an offer directly:
🔗 ${shareUrl}

Reply to enquire. — Surpliq Admin`
                  );
                  // WhatsApp message for unregistered buyers — includes invite
                  const waUnregistered = encodeURIComponent(
`Hi ${b.name},

We have a surplus lot that matches what you typically purchase:

📦 *${listing?.title}*
⚖️ Qty: ${listing?.qty} ${listing?.unit}
📍 Location: ${listing?.location}
💰 Price: ₹${listing?.price}${listing?.priceUnit||''}
✅ GST-verified seller · Inspection welcome

To view full details and make an offer, register free on Surpliq — India's B2B industrial surplus marketplace:
🔗 https://surpliq.com/register

Already interested? Reply to this message and we'll connect you with the seller directly.

— Surpliq Team`
                  );
                  // Email for unregistered
                  const emailSubject = encodeURIComponent(`Surplus Available: ${listing?.title} — Matches Your Purchase Profile`);
                  const emailBody = encodeURIComponent(
`Dear ${b.name},

We identified your company as a potential buyer for the following surplus lot currently listed on Surpliq:

Item: ${listing?.title}
Category: ${listing?.category}
Quantity: ${listing?.qty} ${listing?.unit}
Location: ${listing?.location}
Asking Price: ₹${listing?.price}${listing?.priceUnit||''}
Seller: GST-verified manufacturer

This lot matches your typical purchase profile (${b.whatTheyBuy}).

To view full details and contact the seller:
• Registered users: ${shareUrl}
• New to Surpliq? Register free at https://surpliq.com/register

Surpliq is India's B2B marketplace for industrial surplus. Verified buyers, GST-compliant transactions, AI-powered pricing.

Best regards,
Surpliq Team
support@surpliq.com`
                  );

                  const isRegistered = b.registeredOnSurpliq;

                  const handleNotify = () => {
                    setNotified(p=>({...p,[i]:'shown'}));
                  };

                  return (
                    <div key={i} style={{ borderBottom:'1px solid var(--border)', paddingBottom:14, marginBottom:14 }}>
                      {/* Buyer info row */}
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3 }}>
                            <span style={{ fontWeight:800, fontSize:13, color:'var(--navy)' }}>{b.name}</span>
                            {b.gstVerified && <span title="GST Verified">✅</span>}
                            {b.registeredOnSurpliq && <span title="Registered on Surpliq" style={{ background:'#dcfce7', color:'#15803d', fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8 }}>ON SURPLIQ</span>}
                          </div>
                          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:2 }}>{b.type} · {b.city}, {b.state}</div>
                          <div style={{ fontSize:11, color:'var(--muted)', marginBottom:2 }}>Source: {b.source}</div>
                          <div style={{ fontSize:11, color:'var(--muted)' }}>🛒 Buys: {b.whatTheyBuy}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                          <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:18, color:b.matchScore>=90?'var(--green)':b.matchScore>=75?'var(--amber)':'var(--muted)', marginBottom:6 }}>
                            {b.matchScore}%
                          </div>
                          {notified[i] !== 'shown' ? (
                            <button className="btn btn-gold btn-sm" onClick={handleNotify}>
                              📣 Notify →
                            </button>
                          ) : (
                            <span style={{ fontSize:11, fontWeight:700, color:'var(--green)' }}>✓ Options shown</span>
                          )}
                        </div>
                      </div>

                      {/* Notify action panel — shown after clicking Notify */}
                      {notified[i] === 'shown' && (
                        <div style={{ background: isRegistered?'rgba(34,197,94,.06)':'rgba(200,168,75,.06)', border:`1px solid ${isRegistered?'rgba(34,197,94,.2)':'rgba(200,168,75,.2)'}`, borderRadius:10, padding:'12px 14px' }}>
                          {isRegistered ? (
                            <>
                              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                                <span style={{ fontSize:14 }}>🟢</span>
                                <span style={{ fontWeight:700, fontSize:12, color:'#15803d' }}>Registered on Surpliq</span>
                                <span style={{ fontSize:11, color:'var(--muted)' }}>— In-app alert will be sent</span>
                              </div>
                              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10, lineHeight:1.6 }}>
                                An in-app notification will appear in their buyer dashboard with a direct link to this listing. They can make an offer with one tap.
                              </div>
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                <button className="btn btn-sm" style={{ background:'#25D366', color:'#fff', border:'none', flex:1 }}
                                  onClick={()=>{ window.open(`https://wa.me/?text=${waRegistered}`,'_blank'); setNotified(p=>({...p,[i]:'sent'})); }}>
                                  💬 Also WhatsApp
                                </button>
                                <button className="btn btn-out btn-sm" style={{ flex:1 }}
                                  onClick={()=>{ window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`,'_blank'); setNotified(p=>({...p,[i]:'sent'})); }}>
                                  📧 Also Email
                                </button>
                                <button className="btn btn-gold btn-sm" style={{ flex:1 }}
                                  onClick={async ()=>{
                                    await DB.add('alerts',{ userId:'buyer', type:'listing_match', listingId:listing?._id, listingTitle:listing?.title, message:`New surplus match: ${listing?.title} — ${listing?.qty} ${listing?.unit} at ₹${listing?.price}${listing?.priceUnit||''}`, read:false, createdAt:new Date().toISOString() });
                                    setNotified(p=>({...p,[i]:'sent'}));
                                    showToast(`In-app alert sent to ${b.name} ✅`);
                                  }}>
                                  🔔 Send In-App Alert
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                                <span style={{ fontSize:14 }}>📤</span>
                                <span style={{ fontWeight:700, fontSize:12, color:'#92400e' }}>Not on Surpliq</span>
                                <span style={{ fontSize:11, color:'var(--muted)' }}>— Outreach with platform invite</span>
                              </div>
                              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10, lineHeight:1.6 }}>
                                Send them the listing details + a link to register on Surpliq. Once they join, they can make an offer directly.
                              </div>
                              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                                <button className="btn btn-sm" style={{ background:'#25D366', color:'#fff', border:'none', flex:1 }}
                                  onClick={()=>{ window.open(`https://wa.me/?text=${waUnregistered}`,'_blank'); setNotified(p=>({...p,[i]:'sent'})); }}>
                                  💬 WhatsApp + Invite
                                </button>
                                <button className="btn btn-out btn-sm" style={{ flex:1 }}
                                  onClick={()=>{ window.open(`mailto:?subject=${emailSubject}&body=${emailBody}`,'_blank'); setNotified(p=>({...p,[i]:'sent'})); }}>
                                  📧 Email + Invite
                                </button>
                                <button className="btn btn-out btn-sm" style={{ flex:1 }}
                                  onClick={()=>{ navigator.clipboard?.writeText(decodeURIComponent(waUnregistered)).catch(()=>{}); showToast('Message copied!'); }}>
                                  📋 Copy Message
                                </button>
                              </div>
                              <div style={{ fontSize:10, color:'var(--muted)', marginTop:8, lineHeight:1.5 }}>
                                💡 Message includes: listing details, Surpliq registration link, and offer to connect directly
                              </div>
                            </>
                          )}
                          {notified[i]==='sent' && (
                            <div style={{ marginTop:8, fontSize:11, fontWeight:700, color:'var(--green)' }}>✅ Notification sent to {b.name}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Bulk notify all */}
                {discoveredBuyers.filter((_,i)=>!notified[i]).length > 0 && (
                  <div style={{ paddingTop:8 }}>
                    <button className="btn btn-gold btn-full" onClick={()=>{
                      const all = {};
                      discoveredBuyers.forEach((_,i)=>{ all[i]='shown'; });
                      setNotified(all);
                    }}>
                      📣 Expand All Notify Options ({discoveredBuyers.filter((_,i)=>!notified[i]).length} remaining)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}


// ── RESPONSIVE HOOK ───────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}


// ── STATIC PAGES ─────────────────────────────────────────────────────────
function StaticPage({ page, onBack, onPageChange }) {
  const ww = useWindowWidth(); const mob = ww < 700;
  const [menuOpen, setMenuOpen] = useState(false);

  const pages = [
    { k:'about',   ico:'🏢', lbl:'About Us' },
    { k:'contact', ico:'📬', lbl:'Contact Us' },
    { k:'privacy', ico:'🔒', lbl:'Privacy Policy' },
    { k:'terms',   ico:'📄', lbl:'Terms of Service' },
    { k:'faq',     ico:'❓', lbl:'FAQ' },
  ];

  const Sidebar = () => (
    <div className="sidebar" style={{ minWidth:180 }}>
      <div className="sb-sec">Pages</div>
      {pages.map(p=>(
        <div key={p.k} className={`sb-item ${page===p.k?'act':''}`} onClick={()=>{onPageChange(p.k);setMenuOpen(false);}}>
          <span>{p.ico}</span><span>{p.lbl}</span>
        </div>
      ))}
      <div style={{ borderTop:'1px solid var(--border)', marginTop:12, paddingTop:12 }}>
        <div className="sb-item" onClick={onBack}>← Back to Home</div>
      </div>
    </div>
  );

  const content = {
    about: (
      <div className="fu">
        <div className="eye">About</div>
        <div className="h1" style={{ marginBottom:8 }}>About Surpliq</div>
        <div style={{ fontSize:13, color:'var(--muted)', marginBottom:24, lineHeight:1.8 }}>
          Surpliq is India's AI-powered B2B marketplace for industrial surplus inventory. We connect manufacturers holding non-moving stock with verified GST-registered buyers across India — faster, transparently, and at far better prices than traditional scrap or broker channels.
        </div>
        <div className="card" style={{ marginBottom:14 }}>
          <div className="h2" style={{ marginBottom:10 }}>Our Mission</div>
          <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.8 }}>
            To unlock the ₹4 lakh crore of dead industrial inventory sitting in Indian factories — converting it back into working capital for manufacturers and providing verified buyers with quality surplus at competitive prices.
          </p>
        </div>
        <div className="card" style={{ marginBottom:14 }}>
          <div className="h2" style={{ marginBottom:10 }}>What We Do</div>
          <p style={{ fontSize:13, color:'var(--muted)', lineHeight:1.8 }}>
            Sellers upload any ERP export (SAP MB52, Tally, Excel). Our AI classifies each item, writes buyer-ready descriptions, and benchmarks 3-band pricing. Listings go live to 1,200+ verified GST buyers. Buyers browse, make offers, negotiate, and close deals — all on-platform with full documentation.
          </p>
        </div>
        <div className="card">
          <div className="h2" style={{ marginBottom:12 }}>Who We Serve</div>
          <div style={{ display:'grid', gridTemplateColumns:mob?'1fr':'1fr 1fr', gap:12 }}>
            {[
              ['🏭','Manufacturers','Automotive, bicycle, steel, engineering, FMCG — any sector with surplus RM, BOP, MRO or capital equipment.'],
              ['🛒','Industrial Buyers','Traders, re-rollers, re-processors, distributors, and service providers looking for quality surplus at competitive prices.'],
            ].map(([ico,title,body])=>(
              <div key={title} style={{ background:'var(--off)', borderRadius:10, padding:'14px' }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{ico}</div>
                <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', marginBottom:4 }}>{title}</div>
                <div style={{ fontSize:12, color:'var(--muted)', lineHeight:1.6 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    contact: (
      <div className="fu">
        <div className="eye">Contact</div>
        <div className="h1" style={{ marginBottom:8 }}>Contact Us</div>
        <div style={{ fontSize:13, color:'var(--muted)', marginBottom:24 }}>We typically respond within 4 business hours.</div>
        <div style={{ display:'grid', gridTemplateColumns:mob?'1fr':'1fr 1fr', gap:14, marginBottom:20 }}>
          {[
            ['📧','Email','support@surpliq.com','For general queries and support'],
            ['📍','Office','Noida, Uttar Pradesh','India'],
          ].map(([ico,label,val,sub])=>(
            <div key={label} className="card">
              <div style={{ fontSize:24, marginBottom:8 }}>{ico}</div>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)' }}>{label}</div>
              <div style={{ fontSize:13, color:'var(--gold)', fontWeight:600, marginTop:4 }}>{val}</div>
              <div style={{ fontSize:11, color:'var(--muted)', marginTop:2 }}>{sub}</div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="h2" style={{ marginBottom:14 }}>Send a Message</div>
          {['Name','Company','Email','Subject'].map(f=>(
            <div className="fgrp" key={f}>
              <label className="flabel">{f}</label>
              <input className="finput" placeholder={f}/>
            </div>
          ))}
          <div className="fgrp">
            <label className="flabel">Message</label>
            <textarea className="ftextarea" placeholder="How can we help?" style={{ minHeight:100 }}/>
          </div>
          <button className="btn btn-gold btn-full">Send Message</button>
        </div>
      </div>
    ),
    privacy: (
      <div className="fu">
        <div className="eye">Legal</div>
        <div className="h1" style={{ marginBottom:4 }}>Privacy Policy</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:24 }}>Last updated: January 2025</div>
        {[
          ['1. Information We Collect', 'We collect information you provide when registering (name, company, GST number, email), information generated through platform use (listings, enquiries, deal history), and technical information (IP address, browser type, device information).'],
          ['2. How We Use Information', 'To operate and improve the Surpliq platform, to verify GST and KYC details, to facilitate connections between buyers and sellers, to send transactional notifications, and to comply with legal obligations under Indian law.'],
          ['3. Information Sharing', 'We share contact details between buyer and seller only upon mutual deal acceptance. We do not sell your personal information to third parties. We may share data with service providers (Firebase, Razorpay) under strict confidentiality.'],
          ['4. Data Security', 'All data is stored on Google Firebase infrastructure with encryption at rest and in transit. Access is restricted to authenticated users. We conduct regular security reviews.'],
          ['5. Your Rights', 'You may request access to, correction of, or deletion of your personal data by contacting support@surpliq.com. Requests are processed within 30 days.'],
          ['6. Cookies', 'We use session cookies for authentication only. We do not use tracking or advertising cookies.'],
          ['7. Contact', 'For privacy concerns: support@surpliq.com — Surpliq Technologies Pvt. Ltd., Noida, UP, India.'],
        ].map(([title, body])=>(
          <div className="card" key={title} style={{ marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', marginBottom:8 }}>{title}</div>
            <p style={{ fontSize:12, color:'var(--muted)', lineHeight:1.8 }}>{body}</p>
          </div>
        ))}
      </div>
    ),
    terms: (
      <div className="fu">
        <div className="eye">Legal</div>
        <div className="h1" style={{ marginBottom:4 }}>Terms of Service</div>
        <div style={{ fontSize:12, color:'var(--muted)', marginBottom:24 }}>Last updated: January 2025</div>
        {[
          ['1. Acceptance', 'By registering on Surpliq, you agree to these Terms of Service. If you do not agree, do not use the platform.'],
          ['2. Eligibility', 'You must be a registered business with a valid GSTIN to list or purchase on Surpliq. Individual consumers are not eligible.'],
          ['3. Listings', 'Sellers are responsible for the accuracy of all listing information. Misrepresentation of goods is grounds for account suspension. All listed goods must be legally owned by the seller.'],
          ['4. Transactions', 'Surpliq is a marketplace facilitator. Contracts are between buyer and seller. Surpliq is not a party to any transaction and does not hold or transfer funds.'],
          ['5. Platform Fees', 'Sellers pay a 3% success fee on completed deals. Listing fees of ₹199 apply from the 4th listing. Fees are non-refundable once a deal is closed.'],
          ['6. Prohibited Activities', 'Off-platform transactions to avoid fees, listing of stolen or encumbered goods, fake GST credentials, or harassment of other users will result in immediate account termination.'],
          ['7. Limitation of Liability', "Surpliq's liability is limited to the platform fees paid in the preceding 3 months. We are not liable for product quality, delivery, or payment disputes between parties."],
          ['8. Dispute Resolution', 'Disputes between users should first be raised with Surpliq support. Unresolved disputes are subject to arbitration under Indian law, with jurisdiction in Punjab courts.'],
          ['9. Governing Law', 'These terms are governed by the laws of India. Any legal proceedings shall be in courts of competent jurisdiction in Punjab, India.'],
        ].map(([title, body])=>(
          <div className="card" key={title} style={{ marginBottom:12 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', marginBottom:8 }}>{title}</div>
            <p style={{ fontSize:12, color:'var(--muted)', lineHeight:1.8 }}>{body}</p>
          </div>
        ))}
      </div>
    ),
    faq: (
      <div className="fu">
        <div className="eye">Help</div>
        <div className="h1" style={{ marginBottom:24 }}>Frequently Asked Questions</div>
        {[
          ['How do I list my surplus inventory?', 'Register as a seller, complete KYC verification, then go to Create Listing. You can upload an SAP/Tally/Excel export or add items manually. AI will classify, describe, and price your inventory automatically.'],
          ['Is GST registration mandatory?', 'Yes. Surpliq is a B2B platform for registered businesses only. A valid GSTIN is required for both buyers and sellers to ensure compliant transactions.'],
          ['How does AI pricing work?', 'Our AI analyses your item category, condition, quantity, and age in storage, then benchmarks against current market data to provide three price points: Scrap Floor, Liquidation Range, and Resale Maximum.'],
          ['What is the platform fee?', 'The first 3 listings are free. From the 4th listing, a fee of ₹199 per listing applies. A 3% success fee is charged on completed deals, payable by the seller.'],
          ['How are buyer-seller contacts shared?', 'Contact details are only exchanged after both parties agree to a deal on-platform. This protects both parties from spam and ensures accountability.'],
          ['Can I inspect items before buying?', 'Yes. You can request an inspection directly through the platform on any listing. The seller will confirm a slot and share the warehouse address upon confirmation.'],
          ['How long does a typical deal take?', 'Most deals close within 4 days from listing to deal closure. Traditional broker channels typically take 90-180 days.'],
          ['What happens if there is a dispute?', 'Contact support@surpliq.com. We mediate in good faith between parties. For unresolved disputes, our Terms of Service provide for arbitration under Indian law.'],
          ['Is my data secure?', 'All data is stored on Google Firebase with encryption. Your contact details are never shared without your consent. We are GDPR-aware and comply with Indian data protection norms.'],
        ].map(([q, a], i)=>(
          <FaqItem key={i} q={q} a={a}/>
        ))}
      </div>
    ),
  };

  return (
    <div style={{ display:'flex', flexDirection: mob?'column':'row', minHeight:'calc(100vh - 52px)' }}>
      {mob ? (
        <div style={{ background:'#fff', borderBottom:'1px solid var(--border)', padding:'8px 12px' }}>
          <button className="btn btn-out btn-sm btn-full" onClick={()=>setMenuOpen(p=>!p)}>
            {pages.find(p=>p.k===page)?.lbl || 'Menu'} {menuOpen?'▲':'▼'}
          </button>
          {menuOpen && (
            <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:4 }}>
              {pages.map(p=>(
                <div key={p.k} className={`sb-item ${page===p.k?'act':''}`} onClick={()=>{onPageChange(p.k);setMenuOpen(false);}}>
                  <span>{p.ico}</span><span>{p.lbl}</span>
                </div>
              ))}
              <div className="sb-item" onClick={onBack}>← Back to Home</div>
            </div>
          )}
        </div>
      ) : <Sidebar/>}
      <div className="main">
        {content[page] || <div className="fu"><div className="h1">Page not found</div></div>}
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border:`1.5px solid ${open?'var(--gold)':'var(--border)'}`, borderRadius:10, marginBottom:8, overflow:'hidden' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 16px', cursor:'pointer', background:open?'rgba(200,168,75,.04)':'#fff' }}
        onClick={()=>setOpen(p=>!p)}>
        <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)' }}>{q}</div>
        <span style={{ color:'var(--gold)', fontWeight:700, fontSize:16, flexShrink:0, marginLeft:8 }}>{open?'−':'+'}</span>
      </div>
      {open && <div style={{ padding:'0 16px 14px', fontSize:12, color:'var(--muted)', lineHeight:1.8 }}>{a}</div>}
    </div>
  );
}

export default function SurpliqApp() {
  const [user, setUser]       = useState(null);
  const [view, setView]       = useState("landing");
  const [staticPage, setStaticPage] = useState(null);
  const ww = useWindowWidth();
  const mob = ww < 700;
  const sm  = ww < 480;
  const [sellerTab, setSellerTab]   = useState("dashboard");
  const [buyerTab, setBuyerTab]     = useState("browse");
  const [viewingListing, setViewingListing] = useState(null);
  const [enqListingId, setEnqListingId]     = useState(null);
  const [toast, setToast]           = useState(null);

  // ── Notification system state ────────────────────────────────────
  const [notifs,       setNotifs]       = useState([]);
  const [notifOpen,    setNotifOpen]    = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const unreadCount = notifs.filter(n => !n.read).length;

  // Poll for new notifications every 30 seconds when logged in
  useEffect(() => {
    if (!user) return;
    const fetchNotifs = async () => {
      try {
        const all = await DB.list("notifications", [["userId","==",user._id]]);
        setNotifs(all.sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).slice(0,20));
      } catch {}
    };
    fetchNotifs();
    const iv = setInterval(fetchNotifs, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const markAllRead = async () => {
    const unread = notifs.filter(n=>!n.read);
    await Promise.all(unread.map(n=>DB.update("notifications",n._id,{read:true})));
    setNotifs(p=>p.map(n=>({...n,read:true})));
  };

  const markRead = async (id) => {
    await DB.update("notifications", id, {read:true});
    setNotifs(p=>p.map(n=>n._id===id?{...n,read:true}:n));
  };

  const deleteNotif = async (id) => {
    await DB.delete("notifications", id);
    setNotifs(p=>p.filter(n=>n._id!==id));
  };

  // ── Core notification creator ─────────────────────────────────────
  const createNotif = async (userId, type, title, body, link, meta={}) => {
    const notif = {
      userId, type, title, body, link,
      read: false,
      createdAt: new Date().toISOString(),
      ...meta,
    };
    await DB.add("notifications", notif);
    // WhatsApp trigger (non-blocking) — fires but doesn't block UI
    sendWANotif(type, title, body, meta);
    return notif;
  };

  // ── WhatsApp notification layer ───────────────────────────────────
  // In artifact: logs to console (wa.me links can't auto-open)
  // On deployment: replace with Twilio/Meta WhatsApp Business API call
  const sendWANotif = (type, title, body, meta={}) => {
    const msg = `🔔 *Surpliq Alert*

*${title}*
${body}

https://surpliq.com`;
    console.log("[WA Notification]", type, msg);
    // ── DEPLOYMENT: replace below with your WhatsApp API call ────────
    // fetch("https://your-backend.com/api/whatsapp/send", {
    //   method:"POST",
    //   headers:{"Content-Type":"application/json"},
    //   body:JSON.stringify({ to: meta.phone, message: msg })
    // });
    // ─────────────────────────────────────────────────────────────────
  };

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  useEffect(() => {
    seedDemoData();
    const session = Auth.getSession();
    if (session) { setUser(session); setView("app"); }
  }, []);

  const handleAuth = (u) => { setUser(u); setView("app"); };
  const logout = () => { Auth.logout(); setUser(null); setView("landing"); };

  // ── TOPBAR ───────────────────────────────────────────────────────────
  const Topbar = () => (
    <div className="bar" style={{ position:'relative' }}>
      <div className="logo" onClick={()=>view==="app"?null:setView("landing")}>
        <svg width="26" height="26" viewBox="0 0 26 26" style={{display:"block"}}><defs><linearGradient id="slgnav" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#C8A84B"/><stop offset="100%" stopColor="#E8C96A"/></linearGradient></defs><circle cx="13.0" cy="13.0" r="8.6" fill="none" stroke="url(#slgnav)" strokeWidth="2.1"/><circle cx="24.6" cy="13.0" r="3.0" fill="rgba(255,255,255,0.9)"/><circle cx="18.6" cy="13.0" r="3.0" fill="#E8C96A"/></svg><span style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,letterSpacing:-0.5,whiteSpace:"nowrap",lineHeight:1}}><span style={{color:"#C8A84B"}}>Surp</span><span style={{color:"#ffffff"}}>liq</span></span>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        {DEMO_MODE&&<span className="demo-badge">DEMO MODE</span>}
        {user && (
          <>
            <span style={{ fontSize:12, color:"rgba(255,255,255,.5)" }}>{user.name?.split(" ")[0]} · {user.role}</span>
            {/* Notification Bell */}
            <div style={{ position:'relative' }}>
              <button onClick={()=>setNotifOpen(p=>!p)}
                style={{ background:'rgba(255,255,255,.1)', border:'none', borderRadius:8, width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, position:'relative', transition:'background .18s' }}
                onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,.18)'}
                onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,.1)'}>
                🔔
                {unreadCount > 0 && (
                  <span style={{ position:'absolute', top:-4, right:-4, background:'#EF4444', color:'#fff', borderRadius:'50%', minWidth:17, height:17, fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', border:'2px solid var(--navy)', padding:'0 3px' }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {/* Notification dropdown */}
              {notifOpen && (
                <div style={{ position:'absolute', top:42, right:0, width:320, maxHeight:440, background:'#fff', borderRadius:14, boxShadow:'0 8px 40px rgba(0,0,0,.2)', zIndex:200, display:'flex', flexDirection:'column', overflow:'hidden', border:'1.5px solid var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', borderBottom:'1px solid var(--border)', background:'var(--navy)' }}>
                    <div style={{ fontFamily:"var(--fh)", fontWeight:800, fontSize:13, color:'#fff' }}>
                      🔔 Notifications {unreadCount>0&&<span style={{ fontSize:11, fontWeight:500, color:'rgba(255,255,255,.5)' }}>({unreadCount} new)</span>}
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      {unreadCount>0 && <button onClick={markAllRead} style={{ fontSize:10, color:'rgba(200,168,75,.8)', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Mark all read</button>}
                      <button onClick={()=>setNotifOpen(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', cursor:'pointer', fontSize:16, lineHeight:1 }}>×</button>
                    </div>
                  </div>
                  <div style={{ overflowY:'auto', flex:1 }}>
                    {notifs.length === 0 ? (
                      <div style={{ textAlign:'center', padding:'32px 16px', color:'var(--muted)' }}>
                        <div style={{ fontSize:32, marginBottom:8 }}>🔔</div>
                        <div style={{ fontSize:13, fontWeight:600 }}>No notifications yet</div>
                        <div style={{ fontSize:11, marginTop:4 }}>You'll be notified of offers, responses, and deals here</div>
                      </div>
                    ) : notifs.map(n => (
                      <div key={n._id} onClick={()=>{ markRead(n._id); setNotifOpen(false); }}
                        style={{ display:'flex', gap:10, padding:'11px 14px', borderBottom:'1px solid var(--border)', cursor:'pointer', background:n.read?'#fff':'rgba(200,168,75,.05)', transition:'background .15s' }}
                        onMouseOver={e=>e.currentTarget.style.background=n.read?'#f8f9fa':'rgba(200,168,75,.08)'}
                        onMouseOut={e=>e.currentTarget.style.background=n.read?'#fff':'rgba(200,168,75,.05)'}>
                        <div style={{ fontSize:20, flexShrink:0, marginTop:1 }}>
                          {n.type==='new_offer'?'💬':n.type==='offer_accepted'?'✅':n.type==='counter_offer'?'🔄':n.type==='deal_closed'?'🎉':n.type==='listing_match'?'🎯':n.type==='inspection_request'?'📅':'🔔'}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight: n.read?600:800, fontSize:12, color:'var(--navy)', marginBottom:2 }}>{n.title}</div>
                          <div style={{ fontSize:11, color:'var(--muted)', lineHeight:1.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{n.body}</div>
                          <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>
                            {n.createdAt ? new Date(n.createdAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : ''}
                          </div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                          {!n.read && <span style={{ width:7, height:7, borderRadius:'50%', background:'var(--gold)', display:'block' }}/>}
                          <button onClick={e=>{e.stopPropagation();deleteNotif(n._id);}}
                            style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'#cbd5e1', padding:2 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {notifs.length > 0 && (
                    <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', textAlign:'center' }}>
                      <button onClick={()=>{ setNotifs([]); setNotifOpen(false); }}
                        style={{ fontSize:11, color:'var(--muted)', background:'none', border:'none', cursor:'pointer' }}>Clear all notifications</button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button className="btn btn-sm" style={{ background:"rgba(255,255,255,.1)", color:"rgba(255,255,255,.7)", border:"none" }} onClick={logout}>Logout</button>
          </>
        )}
        {!user && <button className="btn btn-gold btn-sm" onClick={()=>setView("auth")}>Login / Register</button>}
      </div>
    </div>
  );

  // ── LANDING ──────────────────────────────────────────────────────────
  if (view === "landing") {
    const D = "linear-gradient(140deg,#070E1A,#0D1E36,#122848)";
    const W = { maxWidth:1100, margin:"0 auto", padding:"0 20px", width:"100%" };
    const SEC = (bg,py=72) => ({ background:bg, padding:`${py}px 20px` });

    const howSteps = [
      { n:"01", ico:"📤", title:"Upload Your Inventory", body:"Export from SAP MB52, Tally, or any ERP. Drop an Excel file — AI maps every column automatically, no template needed." },
      { n:"02", ico:"🤖", title:"AI Classifies & Prices", body:"Claude AI categorises each item (RM / BOP / MRO / Capital), writes buyer-ready descriptions, and benchmarks 3-band pricing from live market data." },
      { n:"03", ico:"🔍", title:"Buyers Discover Your Lot", body:"Your listing goes live to 1,200+ verified GST buyers. AI also proactively searches IndiaMART and TradeIndia to alert matched buyers directly." },
      { n:"04", ico:"🤝", title:"Negotiate & Close", body:"Buyers make offers on-platform. Negotiate in real-time with AI counter-offer suggestions. Deal is recorded, payment terms agreed, and docs generated." },
    ];

    const cats = [
      { ico:"🔩", name:"Raw Materials", sub:"Steel coils, aluminium, copper, plastics, chemicals", col:"#EF4444", bg:"rgba(239,68,68,.08)", border:"rgba(239,68,68,.15)" },
      { ico:"⚙️", name:"Bought-Out Parts", sub:"Bearings, fasteners, castings, forgings, sub-assemblies", col:"#3B82F6", bg:"rgba(59,130,246,.08)", border:"rgba(59,130,246,.15)" },
      { ico:"🛢️", name:"MRO & Consumables", sub:"Lubricants, tooling, safety items, electrical consumables", col:"#22C55E", bg:"rgba(34,197,94,.08)", border:"rgba(34,197,94,.15)" },
      { ico:"⚡", name:"Capital Equipment", sub:"Motors, drives, compressors, pumps, fabrication machinery", col:"#8B5CF6", bg:"rgba(139,92,246,.08)", border:"rgba(139,92,246,.15)" },
      { ico:"📦", name:"Finished Goods", sub:"Project-cancelled goods, discontinued SKUs, export rejects", col:"#F59E0B", bg:"rgba(245,158,11,.08)", border:"rgba(245,158,11,.15)" },
      { ico:"🏗️", name:"Project Surplus", sub:"Construction materials, structural steel, site equipment", col:"#06B6D4", bg:"rgba(6,182,212,.08)", border:"rgba(6,182,212,.15)" },
    ];

    const whyCards = [
      { ico:"🧠", title:"ERP-Native Intake", body:"SAP MB52, Tally, any Excel format. AI maps columns, no template required. Upload and go in minutes." },
      { ico:"📊", title:"AI Price Intelligence", body:"Real-time 3-band pricing: Scrap Floor, Liquidation Range, Resale Max — calibrated to Indian industrial markets." },
      { ico:"🔍", title:"Proactive Buyer Discovery", body:"When you list, AI scours IndiaMART, TradeIndia, and MRAI directories to find and notify matched buyers immediately." },
      { ico:"🤝", title:"On-Platform Negotiation", body:"Full chat-based deal flow with AI counter-offer suggestions, quote cards, and automatic deal records." },
      { ico:"✅", title:"GST-Verified Buyers Only", body:"Every buyer on Surpliq is KYC-verified with GST. No brokers, no time-wasters — only serious industrial buyers." },
      { ico:"📈", title:"CFO-Grade Recovery Reporting", body:"Track your recovery rate vs. book value. Export deal summaries, payment records, and audit-ready reports." },
    ];

    const testimonials = [
      { quote:"We cleared ₹2.3 crore of dead HR steel stock in 11 days. The AI pricing was spot-on — we got 38% over scrap value.", name:"VP Supply Chain", co:"Large Auto Ancillary, Pune", rating:5 },
      { quote:"Finally a platform that understands industrial surplus. Not like ecommerce sites. The buyer discovery feature found us buyers we'd never have reached.", name:"CPO", co:"Capital Goods Manufacturer, Ludhiana", rating:5 },
      { quote:"Our procurement team uses Surpliq to source surplus bearings and motors at 40-60% below market. The GST verification gives us full confidence.", name:"Head – Indirect Procurement", co:"FMCG Manufacturer, Ahmedabad", rating:5 },
    ];

    const liveListings = [
      { ico:"🔩", bg:"rgba(239,68,68,.15)", name:"HR Steel Coil IS2062 — 22.7 MT", loc:"Ludhiana, Punjab", price:"₹37,500/MT", val:"₹9.5L", tag:"4 Enquiries", tagColor:"#4ADE80" },
      { ico:"⚙️", bg:"rgba(59,130,246,.15)", name:"SKF + FAG Bearing Mixed Lot 600 pcs", loc:"Pune, Maharashtra", price:"₹280/pc", val:"₹1.7L", tag:"7 Enquiries", tagColor:"#4ADE80" },
      { ico:"⚡", bg:"rgba(139,92,246,.15)", name:"ABB Motor + Danfoss VFD Bundle", loc:"Ludhiana, Punjab", price:"₹1,10,000", val:"₹2.6L", tag:"2 Offers", tagColor:"#FBBF24" },
      { ico:"🏗️", bg:"rgba(6,182,212,.15)", name:"Aluminium Extrusion 6063-T5 — 3.2 MT", loc:"Ludhiana, Punjab", price:"₹70/kg", val:"₹2.2L", tag:"New", tagColor:"#94A3B8" },
      { ico:"🛢️", bg:"rgba(34,197,94,.15)", name:"Shell Omala S2 G 320 — 24 Drums", loc:"Ahmedabad, Gujarat", price:"₹2,400/drm", val:"₹57,600", tag:"New", tagColor:"#94A3B8" },
    ];

    return (
      <div style={{ fontFamily:"var(--fb)" }}>
        <style>{CSS}</style>
        <style>{`
          @keyframes blink{0%,100%{opacity:1}50%{opacity:.3}}
          @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
          @keyframes ticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
          .ticker-wrap{overflow:hidden;background:rgba(200,168,75,.08);border-top:1px solid rgba(200,168,75,.15);border-bottom:1px solid rgba(200,168,75,.15);padding:10px 0;}
          .ticker-inner{display:flex;width:max-content;animation:ticker 30s linear infinite;}
          .ticker-item{display:inline-flex;align-items:center;gap:8px;padding:0 32px;font-size:12px;font-weight:600;color:rgba(200,168,75,.8);white-space:nowrap;}
          .ticker-dot{width:4px;height:4px;border-radius:50%;background:rgba(200,168,75,.4);}
          .how-line{position:absolute;top:40px;left:calc(50% + 40px);right:calc(-50% + 40px);height:2px;background:linear-gradient(90deg,rgba(200,168,75,.3),rgba(200,168,75,.05));z-index:0;}
          .cat-card:hover{transform:translateY(-4px);box-shadow:0 12px 32px rgba(0,0,0,.08);}
          .why-card:hover{transform:translateY(-3px);}
          .lst-ticker{display:flex;gap:14px;overflow-x:auto;padding-bottom:8px;scrollbar-width:none;}
          .lst-ticker::-webkit-scrollbar{display:none;}
        `}</style>

        {/* ── NAV ── */}
        <nav style={{ background:D, borderBottom:"1px solid rgba(255,255,255,.06)", position:"sticky", top:0, zIndex:300 }}>
          <div style={{ ...W, display:"flex", alignItems:"center", justifyContent:"space-between", height:60, maxWidth:1200 }}>
            <div className="logo"><svg width="26" height="26" viewBox="0 0 26 26" style={{display:"block"}}><defs><linearGradient id="slgnav" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#C8A84B"/><stop offset="100%" stopColor="#E8C96A"/></linearGradient></defs><circle cx="13.0" cy="13.0" r="8.6" fill="none" stroke="url(#slgnav)" strokeWidth="2.1"/><circle cx="24.6" cy="13.0" r="3.0" fill="rgba(255,255,255,0.9)"/><circle cx="18.6" cy="13.0" r="3.0" fill="#E8C96A"/></svg><span style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,letterSpacing:-0.5,whiteSpace:"nowrap",lineHeight:1}}><span style={{color:"#C8A84B"}}>Surp</span><span style={{color:"#ffffff"}}>liq</span></span></div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {DEMO_MODE && <span className="demo-badge">DEMO</span>}
              <button className="btn btn-sm" style={{ background:"rgba(255,255,255,.08)", color:"rgba(255,255,255,.7)", border:"1px solid rgba(255,255,255,.12)" }} onClick={()=>setView("auth")}>Login</button>
              <button className="btn btn-gold btn-sm" onClick={()=>setView("auth")}>Get Started →</button>
            </div>
          </div>
        </nav>

        {/* ── TICKER ── */}
        <div className="ticker-wrap" style={{ background:"#070E1A" }}>
          <div className="ticker-inner">
            {[...Array(2)].map((_,ri)=>(
              <span key={ri} style={{ display:"inline-flex", alignItems:"center" }}>
                {["HR Steel Coil IS2062 · Ludhiana · ₹37,500/MT","SKF Bearings Mixed Lot · Pune · ₹280/pc","ABB 7.5kW Motor · Ludhiana · ₹1.1L","Shell Omala 320 · Ahmedabad · ₹2,400/drm","Copper Cable 6sqmm · Chennai · ₹155/mtr","Danfoss FC302 VFD · Ludhiana · ₹55,000/pc"].map((t,i)=>(
                  <span key={i} className="ticker-item">
                    <span className="ticker-dot"/>LIVE: {t}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>

        {/* ── HERO ── */}
        <div style={{ ...SEC(D,0), paddingTop:mob?40:80, paddingBottom:mob?40:80 }}>
          <div style={{ ...W, maxWidth:1200, display:"grid", gridTemplateColumns:mob?"1fr":"1fr 420px", gap:mob?32:64, alignItems:"center" }}>
            <div>
              {/* Badge */}
              <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"rgba(200,168,75,.1)", border:"1px solid rgba(200,168,75,.25)", color:"var(--gold)", padding:"5px 14px 5px 9px", borderRadius:20, fontSize:12, fontWeight:700, marginBottom:28, letterSpacing:.5 }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:"var(--gold)", display:"inline-block", animation:"blink 1.8s infinite" }}/>
                India\'s B2B Industrial Surplus Marketplace
              </div>

              {/* Headline */}
              <h1 style={{ fontFamily:"var(--fh)", fontSize:"clamp(38px,4.5vw,60px)", fontWeight:800, color:"#fff", lineHeight:1.05, letterSpacing:-2.5, marginBottom:16 }}>
                Turn Surplus Into<br />
                <span style={{ color:"var(--gold)" }}>Working Capital</span>
              </h1>

              {/* SURP | LIQ brand meaning bar */}
              <div style={{ display:"inline-flex", alignItems:"stretch", marginBottom:24, background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.09)", borderRadius:10, overflow:"hidden" }}>
                <div style={{ padding:"9px 16px", borderRight:"1px solid rgba(255,255,255,.08)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontFamily:"var(--fh)", fontSize:13, fontWeight:800, color:"var(--gold)", letterSpacing:2.5 }}>SURP</span>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,.32)", fontWeight:500 }}>Surplus Recovery</span>
                </div>
                <div style={{ padding:"9px 16px", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontFamily:"var(--fh)", fontSize:13, fontWeight:800, color:"rgba(255,255,255,.55)", letterSpacing:2.5 }}>LIQ</span>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,.32)", fontWeight:500 }}>Liquidate Efficiently</span>
                </div>
              </div>

              <p style={{ color:"rgba(255,255,255,.5)", fontSize:16, lineHeight:1.85, marginBottom:36, maxWidth:500 }}>
                Upload any ERP export — SAP, Tally, Excel. AI classifies, prices, and connects you with 1,200+ verified GST buyers across India. RM, BOP, MRO, Capital Equipment and more.
              </p>
              <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:48 }}>
                <button className="btn btn-gold btn-lg" onClick={()=>setView("auth")} style={{ padding:"14px 28px", fontSize:15 }}>
                  📦 List Your Surplus Free
                </button>
                <button style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"14px 24px", borderRadius:10, border:"1px solid rgba(255,255,255,.15)", background:"rgba(255,255,255,.06)", color:"rgba(255,255,255,.8)", fontSize:15, fontWeight:600, cursor:"pointer" }}
                  onClick={()=>{ seedDemoData(); handleAuth({ _id:"demo-buyer-1", email:"buyer@narayan.com", name:"Raj Narayan", role:"buyer", company:"Narayan Steel Traders", city:"Ludhiana", state:"Punjab", kycStatus:"approved" }); }}>
                  🔍 Browse as Buyer
                </button>
              </div>
              <div style={{ display:"flex", gap:0, borderTop:"1px solid rgba(255,255,255,.08)", paddingTop:32, flexWrap:"wrap" }}>
                {[["₹48Cr+","GMV Listed"],["1,200+","Verified Buyers"],["42%","Avg Recovery"],["4 Days","Avg Deal Time"]].map(([n,l],i)=>(
                  <div key={i} style={{ flex:"1 1 40%", paddingRight:mob?0:24, borderRight:(!mob&&i<3)?"1px solid rgba(255,255,255,.07)":"none", paddingLeft:(!mob&&i>0)?24:0, marginBottom:mob?16:0 }}>
                    <div style={{ fontFamily:"var(--fh)", fontSize:24, fontWeight:800, color:"#fff", lineHeight:1 }}>{n}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,.35)", marginTop:4, letterSpacing:.3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Live listings card */}
            <div style={{ background:"rgba(255,255,255,.035)", border:"1px solid rgba(255,255,255,.09)", borderRadius:22, overflow:"hidden", boxShadow:"0 32px 80px rgba(0,0,0,.4)", display:mob?"none":"block" }}>
              <div style={{ background:"rgba(255,255,255,.04)", borderBottom:"1px solid rgba(255,255,255,.07)", padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontFamily:"var(--fh)", fontSize:11, fontWeight:700, color:"rgba(255,255,255,.35)", textTransform:"uppercase", letterSpacing:2 }}>Live Listings</span>
                <span style={{ fontSize:11, color:"#4ADE80", fontWeight:700, display:"flex", alignItems:"center", gap:5 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#4ADE80", display:"inline-block", animation:"blink 1.5s infinite" }}/>Live
                </span>
              </div>
              <div style={{ padding:"12px 14px" }}>
                {liveListings.map((l,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:11, padding:"10px 11px", background:"rgba(255,255,255,.025)", border:"1px solid rgba(255,255,255,.05)", borderRadius:10, marginBottom:7, cursor:"pointer", transition:"background .2s" }}
                    onClick={()=>setView("auth")}>
                    <div style={{ width:34, height:34, borderRadius:8, background:l.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>{l.ico}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.85)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{l.name}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,.3)", marginTop:1 }}>📍 {l.loc} · {l.price}</div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"var(--fh)", fontSize:12, fontWeight:800, color:"var(--gold)" }}>{l.val}</div>
                      <div style={{ fontSize:9, background:"rgba(34,197,94,.12)", color:l.tagColor, padding:"1px 6px", borderRadius:5, marginTop:3, fontWeight:700 }}>{l.tag}</div>
                    </div>
                  </div>
                ))}
                <button className="btn btn-gold btn-full btn-sm" style={{ marginTop:6 }} onClick={()=>setView("auth")}>View All Listings →</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── TRUST BAR ── */}
        <div style={{ background:"#0A1628", borderTop:"1px solid rgba(255,255,255,.05)", borderBottom:"1px solid rgba(255,255,255,.05)", padding:"20px 20px" }}>
          <div style={{ ...W, maxWidth:1200, display:"flex", alignItems:"center", justifyContent:"center", gap:mob?16:48, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,.25)", fontWeight:700, textTransform:"uppercase", letterSpacing:2 }}>Trusted by teams at</span>
            {["Hero Cycles Ltd","HMC Group","Bharat Engineering","Narayan Steel","Punjab Re-Rollers","Gujarat MFG Ltd"].map(c=>(
              <span key={c} style={{ fontSize:13, fontWeight:700, color:"rgba(255,255,255,.35)" }}>{c}</span>
            ))}
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={SEC("#fff")}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ textAlign:"center", marginBottom:56 }}>
              <div className="eye">How It Works</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(28px,3.5vw,40px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5, marginBottom:14 }}>
                From ERP Export to Closed Deal<br />in Under 48 Hours
              </h2>
              <p style={{ color:"var(--muted)", fontSize:16, maxWidth:540, margin:"0 auto", lineHeight:1.7 }}>
                No manual data entry, no broker middlemen. Surpliq's AI does the heavy lifting from classification to buyer matching.
              </p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:mob?"1fr 1fr":"repeat(4,1fr)", gap:mob?24:0, position:"relative" }}>
              {howSteps.map((s,i)=>(
                <div key={i} style={{ textAlign:"center", padding:mob?"0 8px":"0 20px", position:"relative" }}>
                  {i<3&&!mob&&<div style={{ position:"absolute", top:36, left:"calc(50% + 44px)", right:"-calc(50% - 44px)", width:"calc(100% - 88px)", height:2, background:"linear-gradient(90deg,rgba(200,168,75,.4),rgba(200,168,75,.1))", zIndex:0 }}/>}
                  <div style={{ width:mob?52:72, height:mob?52:72, borderRadius:"50%", background:"linear-gradient(135deg,var(--navy),var(--n2))", display:"flex", alignItems:"center", justifyContent:"center", fontSize:mob?20:28, margin:"0 auto 18px", position:"relative", zIndex:1, boxShadow:"0 8px 24px rgba(10,22,40,.15)" }}>
                    {s.ico}
                    <span style={{ position:"absolute", top:-6, right:-6, width:22, height:22, borderRadius:"50%", background:"var(--gold)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"var(--fh)", fontSize:9, fontWeight:800, color:"var(--navy)" }}>{s.n}</span>
                  </div>
                  <h3 style={{ fontFamily:"var(--fh)", fontSize:15, fontWeight:800, color:"var(--navy)", marginBottom:10 }}>{s.title}</h3>
                  <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.7 }}>{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CATEGORIES ── */}
        <div style={SEC("var(--off)")}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div className="eye">Categories</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(26px,3vw,38px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5, marginBottom:12 }}>Every Type of Industrial Surplus</h2>
              <p style={{ color:"var(--muted)", fontSize:15, maxWidth:480, margin:"0 auto" }}>From raw materials to capital equipment — all categories, all industries.</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:sm?"1fr":mob?"1fr 1fr":"repeat(3,1fr)", gap:14 }}>
              {cats.map((c,i)=>(
                <div key={i} className="cat-card" style={{ background:c.bg, border:`1.5px solid ${c.border}`, borderRadius:16, padding:"24px 22px", cursor:"pointer", transition:"all .25s" }} onClick={()=>setView("auth")}>
                  <div style={{ fontSize:36, marginBottom:14 }}>{c.ico}</div>
                  <h3 style={{ fontFamily:"var(--fh)", fontSize:16, fontWeight:800, color:"var(--navy)", marginBottom:7 }}>{c.name}</h3>
                  <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.6 }}>{c.sub}</p>
                  <div style={{ marginTop:14, fontSize:12, fontWeight:700, color:c.col, display:"flex", alignItems:"center", gap:4 }}>Browse listings →</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── WHY RELIQ ── */}
        <div style={SEC("#fff")}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ textAlign:"center", marginBottom:52 }}>
              <div className="eye">Why Surpliq</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(26px,3vw,38px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5, marginBottom:12 }}>Built for Industrial Procurement Teams</h2>
              <p style={{ color:"var(--muted)", fontSize:15, maxWidth:480, margin:"0 auto" }}>Not a generic marketplace. Purpose-built for the unique workflows of Indian manufacturing procurement.</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:sm?"1fr":mob?"1fr 1fr":"repeat(3,1fr)", gap:14 }}>
              {whyCards.map((c,i)=>(
                <div key={i} className="why-card" style={{ background:"var(--white)", border:"1.5px solid var(--border)", borderRadius:16, padding:"24px", transition:"all .22s" }}>
                  <div style={{ width:48, height:48, background:"linear-gradient(135deg,#EEF2FF,#E0E7FF)", borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, marginBottom:16 }}>{c.ico}</div>
                  <h3 style={{ fontFamily:"var(--fh)", fontSize:15, fontWeight:800, color:"var(--navy)", marginBottom:9 }}>{c.title}</h3>
                  <p style={{ fontSize:13, color:"var(--muted)", lineHeight:1.7 }}>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── AI SPOTLIGHT ── */}
        <div style={{ ...SEC("linear-gradient(135deg,#0A1628,#1B3A5C)"), padding:"72px 20px" }}>
          <div style={{ ...W, maxWidth:1200, display:"grid", gridTemplateColumns:mob?"1fr":"1fr 1fr", gap:mob?32:64, alignItems:"center" }}>
            <div>
              <div style={{ display:"inline-block", background:"rgba(200,168,75,.15)", border:"1px solid rgba(200,168,75,.3)", color:"var(--gold)", fontSize:10, fontWeight:700, letterSpacing:2, padding:"3px 10px", borderRadius:6, textTransform:"uppercase", marginBottom:20 }}>AI Feature</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(26px,3vw,38px)", fontWeight:800, color:"#fff", letterSpacing:-1.5, marginBottom:18, lineHeight:1.15 }}>
                AI Buyer Discovery —<br />We Find Buyers For You
              </h2>
              <p style={{ color:"rgba(255,255,255,.55)", fontSize:15, lineHeight:1.8, marginBottom:28 }}>
                When you list a surplus lot, our AI immediately scans IndiaMART, TradeIndia, MRAI directories, and our own verified buyer database. It scores every potential buyer by category match, proximity, and size — then sends alerts on your behalf.
              </p>
              {["Zero manual outreach","Match scores with source citations","One-click notify to all discovered buyers","Auto-prioritises registered Surpliq buyers"].map((f,i)=>(
                <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 }}>
                  <span style={{ width:20, height:20, borderRadius:"50%", background:"rgba(200,168,75,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"var(--gold)", flexShrink:0, marginTop:1 }}>✓</span>
                  <span style={{ fontSize:14, color:"rgba(255,255,255,.65)" }}>{f}</span>
                </div>
              ))}
            </div>
            <div style={{ background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.08)", borderRadius:18, padding:"22px 20px", display:mob?"none":"block" }}>
              <span style={{ background:"rgba(200,168,75,.15)", border:"1px solid rgba(200,168,75,.25)", color:"var(--gold)", fontSize:10, fontWeight:700, letterSpacing:1.5, padding:"2px 9px", borderRadius:5, textTransform:"uppercase", display:"inline-block", marginBottom:14 }}>AI SCANNING LIVE</span>
              {["🔍 Searching IndiaMART for 'HR Steel buyers Punjab'...","🌐 Scanning TradeIndia — 847 results filtered...","📋 Checking MRAI Member Directory...","🧠 Scoring by proximity + category match...","✅ 8 high-relevance buyers identified"].map((l,i)=>(
                <div key={i} style={{ fontSize:12, color:"rgba(255,255,255,.55)", padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,.05)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:"rgba(200,168,75,.5)", flexShrink:0 }}/>{l}
                </div>
              ))}
              <div style={{ marginTop:16 }}>
                {[{n:"Narayan Steel Traders",sc:96,loc:"Ludhiana",src:"Surpliq Network",reg:true},{n:"Punjab Re-Rollers Ltd",sc:91,loc:"Mandi Gobindgarh",src:"MRAI Directory",reg:false},{n:"NCR Steel Distributors",sc:78,loc:"Delhi",src:"IndiaMART",reg:false}].map((b,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:11, padding:"9px 11px", background:"rgba(255,255,255,.04)", border:"1px solid rgba(255,255,255,.07)", borderRadius:10, marginBottom:7 }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", background:b.sc>89?"rgba(22,163,74,.25)":"rgba(200,168,75,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"var(--fh)", fontSize:11, fontWeight:800, color:b.sc>89?"#86EFAC":"var(--gold)", flexShrink:0 }}>{b.sc}%</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.8)" }}>{b.name} {b.reg?"🟢":""}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,.35)" }}>{b.loc} · {b.src}</div>
                    </div>
                    <button style={{ padding:"4px 10px", border:"1px solid rgba(200,168,75,.35)", borderRadius:6, background:"rgba(200,168,75,.1)", color:"var(--gold)", fontSize:10, fontWeight:700, cursor:"pointer" }}>📲 Notify</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── TESTIMONIALS ── */}
        <div style={SEC("var(--off)")}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div className="eye">Testimonials</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(26px,3vw,38px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5 }}>What Our Customers Say</h2>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:mob?"1fr":"repeat(3,1fr)", gap:16 }}>
              {testimonials.map((t,i)=>(
                <div key={i} style={{ background:"#fff", border:"1.5px solid var(--border)", borderRadius:18, padding:"26px 24px", display:"flex", flexDirection:"column", gap:16 }}>
                  <div style={{ display:"flex", gap:2 }}>{[...Array(t.rating)].map((_,j)=><span key={j} style={{ color:"var(--gold)", fontSize:15 }}>★</span>)}</div>
                  <p style={{ fontSize:14, color:"var(--navy)", lineHeight:1.75, fontStyle:"italic", flex:1 }}>"{t.quote}"</p>
                  <div style={{ borderTop:"1px solid var(--border)", paddingTop:14 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"var(--navy)" }}>{t.name}</div>
                    <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{t.co}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── PRICING ── */}
        <div style={SEC("#fff")}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ textAlign:"center", marginBottom:48 }}>
              <div className="eye">Pricing</div>
              <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(26px,3vw,38px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5, marginBottom:12 }}>Simple, Performance-Based Pricing</h2>
              <p style={{ color:"var(--muted)", fontSize:15 }}>Start free. Pay only when you sell.</p>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:mob?"1fr":"repeat(3,1fr)", gap:16, maxWidth:860, margin:"0 auto" }}>
              {[
                { name:"Starter", price:"Free", sub:"First 3 listings", features:["3 listings free","AI classification","Price intelligence","Buyer notifications","Platform chat"], cta:"Get Started", highlight:false },
                { name:"Growth", price:"₹199", sub:"per listing, then 3% on deals", features:["Unlimited listings","All Starter features","Featured listing boost","AI buyer discovery","Priority support"], cta:"Start Listing", highlight:true },
                { name:"Enterprise", price:"Custom", sub:"Managed liquidation service", features:["Dedicated relationship mgr","8–12% commission on deals","Full lot management","CFO recovery reports","Audit-ready documentation"], cta:"Contact Us", highlight:false },
              ].map((p,i)=>(
                <div key={i} style={{ background:p.highlight?"var(--navy)":"#fff", border:`2px solid ${p.highlight?"var(--gold)":"var(--border)"}`, borderRadius:20, padding:"28px 24px", display:"flex", flexDirection:"column", position:"relative" }}>
                  {p.highlight&&<div style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)", background:"var(--gold)", color:"var(--navy)", fontSize:10, fontWeight:800, padding:"3px 14px", borderRadius:20, textTransform:"uppercase", letterSpacing:1, whiteSpace:"nowrap" }}>Most Popular</div>}
                  <div style={{ fontFamily:"var(--fh)", fontSize:13, fontWeight:800, color:p.highlight?"rgba(255,255,255,.5)":"var(--muted)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>{p.name}</div>
                  <div style={{ fontFamily:"var(--fh)", fontSize:32, fontWeight:800, color:p.highlight?"#fff":"var(--navy)", marginBottom:4, lineHeight:1 }}>{p.price}</div>
                  <div style={{ fontSize:12, color:p.highlight?"rgba(255,255,255,.4)":"var(--muted)", marginBottom:22 }}>{p.sub}</div>
                  <div style={{ flex:1 }}>
                    {p.features.map((f,j)=>(
                      <div key={j} style={{ display:"flex", gap:9, alignItems:"flex-start", marginBottom:10, fontSize:13, color:p.highlight?"rgba(255,255,255,.7)":"var(--muted)" }}>
                        <span style={{ color:p.highlight?"var(--gold)":"var(--green)", flexShrink:0, marginTop:1 }}>✓</span>{f}
                      </div>
                    ))}
                  </div>
                  <button className={`btn btn-full ${p.highlight?"btn-gold":"btn-out"}`} style={{ marginTop:20 }} onClick={()=>setView("auth")}>{p.cta}</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA BANNER ── */}
        <div style={{ background:"linear-gradient(135deg,var(--gold),#E8C96A)", padding:mob?"40px 16px":"64px 20px" }}>
          <div style={{ ...W, maxWidth:800, textAlign:"center" }}>
            <h2 style={{ fontFamily:"var(--fh)", fontSize:"clamp(28px,3.5vw,44px)", fontWeight:800, color:"var(--navy)", letterSpacing:-1.5, marginBottom:16 }}>
              Ready to Liquidate Your Surplus?
            </h2>
            <p style={{ fontSize:16, color:"rgba(10,22,40,.65)", lineHeight:1.7, marginBottom:32, maxWidth:480, margin:"0 auto 32px" }}>
              Join 400+ Indian manufacturers who've recovered crores from dead inventory using Surpliq. First 3 listings are completely free.
            </p>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <button className="btn btn-navy btn-lg" onClick={()=>setView("auth")} style={{ padding:"14px 32px", fontSize:16 }}>📦 Start Listing Free →</button>
              <button style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"14px 24px", borderRadius:10, border:"2px solid rgba(10,22,40,.2)", background:"transparent", color:"var(--navy)", fontSize:15, fontWeight:700, cursor:"pointer" }} onClick={()=>{ seedDemoData(); handleAuth({ _id:"demo-buyer-1", email:"buyer@narayan.com", name:"Raj Narayan", role:"buyer", company:"Narayan Steel Traders", city:"Ludhiana", state:"Punjab", kycStatus:"approved" }); }}>
                🔍 Browse Buyer
              </button>
            </div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer style={{ background:"#040D1A", padding:mob?"36px 16px 24px":"52px 20px 28px" }}>
          <div style={{ ...W, maxWidth:1200 }}>
            <div style={{ display:"grid", gridTemplateColumns:mob?"1fr 1fr":"2fr 1fr 1fr 1fr", gap:mob?24:48, paddingBottom:40, borderBottom:"1px solid rgba(255,255,255,.06)", marginBottom:28 }}>
              <div>
                <div className="logo" style={{ marginBottom:14, fontSize:20 }}><svg width="28" height="28" viewBox="0 0 28 28" style={{display:"block"}}><defs><linearGradient id="slgfoot" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#C8A84B"/><stop offset="100%" stopColor="#E8C96A"/></linearGradient></defs><circle cx="14.0" cy="14.0" r="9.2" fill="none" stroke="url(#slgfoot)" strokeWidth="2.2"/><circle cx="26.5" cy="14.0" r="3.2" fill="rgba(255,255,255,0.9)"/><circle cx="20.0" cy="14.0" r="3.2" fill="#E8C96A"/></svg><span style={{fontFamily:"'Plus Jakarta Sans',sans-serif",fontWeight:800,letterSpacing:-0.5,whiteSpace:"nowrap",lineHeight:1}}><span style={{color:"#C8A84B"}}>Surp</span><span style={{color:"#ffffff"}}>liq</span></span></div>
                <p style={{ fontSize:13, color:"rgba(255,255,255,.35)", lineHeight:1.8, maxWidth:mob?240:280 }}>India's AI-powered B2B marketplace for industrial surplus liquidation. Turn dead stock into working capital.</p>
                <div style={{ display:"flex", gap:10, marginTop:18 }}>
                  {["LinkedIn","Twitter","Email"].map(s=>(
                    <div key={s} style={{ width:32, height:32, borderRadius:8, background:"rgba(255,255,255,.06)", border:"1px solid rgba(255,255,255,.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, cursor:"pointer", color:"rgba(255,255,255,.4)" }}>{s[0]}</div>
                  ))}
                </div>
              </div>
              {[
                { h:"Platform", links:["Browse Listings","Sell Surplus","AI Valuation","Buyer Discovery","Pricing"] },
                { h:"Categories", links:["Raw Materials","Bought-Out Parts","MRO & Consumables","Capital Equipment","Finished Goods"] },
                { h:"Company",   links:["About Us","Contact Us","Privacy Policy","Terms of Service","FAQ"] },
              ].map((col,i)=>(
                <div key={i}>
                  <div style={{ fontFamily:"var(--fh)", fontSize:12, fontWeight:800, color:"rgba(255,255,255,.3)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:16 }}>{col.h}</div>
                  {col.links.map(l=><div key={l} style={{ fontSize:13, color:"rgba(255,255,255,.35)", marginBottom:10, cursor:"pointer", transition:"color .2s" }} onMouseOver={e=>e.target.style.color="rgba(255,255,255,.7)"} onMouseOut={e=>e.target.style.color="rgba(255,255,255,.35)"} onClick={()=>{const pg={"About Us":"about","Contact Us":"contact","Privacy Policy":"privacy","Terms of Service":"terms","FAQ":"faq","Browse Listings":null,"Sell Surplus":null,"AI Valuation":null,"Buyer Discovery":null,"Pricing":null,"Raw Materials":null,"Bought-Out Parts":null,"MRO & Consumables":null,"Capital Equipment":null,"Finished Goods":null}[l];if(pg){setStaticPage(pg);setView("static");}else{setView("auth");}}}>{l}</div>)}
                </div>
              ))}
            </div>
            <div style={{ display:"flex", justifyContent:mob?"center":"space-between", alignItems:"center", flexWrap:"wrap", gap:10, textAlign:mob?"center":"left" }}>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.2)" }}>© 2025 Surpliq Technologies Pvt. Ltd. · GST-Verified Platform · India</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,.2)", marginTop:6 }}>📧 <a href="mailto:support@surpliq.com" style={{ color:"rgba(200,168,75,0.6)", textDecoration:"none" }}>support@surpliq.com</a></div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // ── STATIC PAGES ──────────────────────────────────────────────────────
  if (view === "static" && staticPage) return (
    <div><style>{CSS}</style><Topbar /><StaticPage page={staticPage} onBack={()=>setView("landing")} onPageChange={(p)=>setStaticPage(p)} /></div>
  );

  // ── AUTH ──────────────────────────────────────────────────────────────
  if (view === "auth") return (
    <div>
      <style>{CSS}</style>
      <AuthPage onAuth={handleAuth} onBack={()=>setView("landing")} />
    </div>
  );

  // ── APP ───────────────────────────────────────────────────────────────
  if (view === "app" && user) {
    if (user.role === "admin") return (
      <div><style>{CSS}</style><Topbar /><AdminPanel user={user} /></div>
    );

    if (user.role === "seller") {
      const isPro = user.plan === 'pro' || user.isPro === true;
      const SELLER_TABS = [
        {k:"dashboard",ico:"📊",lbl:"Dashboard"},
        {k:"listings",ico:"📦",lbl:"My Listings"},
        {k:"create",ico:"➕",lbl:"New Listing"},
        {k:"enquiries",ico:"💬",lbl:"Enquiries",badge:0},
        {k:"discovery",ico:"🎯",lbl:"Buyer AI",pro:true},
      ];
      return (
        <div><style>{CSS}</style>
          {toast&&<Toast msg={toast.msg} type={toast.type}/>}
          <Topbar />
          <div className="layout">
            <div className="sidebar">
              <div style={{ padding:"12px 10px 8px", borderBottom:"1px solid var(--border)", marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:13 }}>{user.name}</div>
                <div style={{ fontSize:11, color:"var(--muted)" }}>{user.company}</div>
                <span className={`pill ${user.kycStatus==="approved"?"pill-green":"pill-amber"}`} style={{ marginTop:5, display:"inline-block" }}>{user.kycStatus==="approved"?"✅ Verified":"⏳ KYC Pending"}</span>
              </div>
              <div className="sb-sec">Menu</div>
              {SELLER_TABS.map(t=>(
                <div key={t.k} className={`sb-item ${sellerTab===t.k?"act":""}`}
                  onClick={()=>{ setSellerTab(t.k); setEnqListingId(null); }}>
                  <span>{t.ico}</span>
                  <span style={{ flex:1 }}>{t.lbl}</span>
                  {t.badge>0&&<span className="sb-badge sbb-r">{t.badge}</span>}
                  {t.pro && !isPro && <span style={{ fontSize:9,fontWeight:700,background:'rgba(200,168,75,.2)',color:'#C8A84B',padding:'1px 5px',borderRadius:6,letterSpacing:.3 }}>PRO</span>}
                  {t.pro && isPro  && <span style={{ fontSize:9,fontWeight:700,background:'rgba(34,197,94,.15)',color:'var(--green)',padding:'1px 5px',borderRadius:6,letterSpacing:.3 }}>✓</span>}
                </div>
              ))}
              {/* Pro plan status */}
              <div style={{ margin:'12px 8px 0',padding:'10px 10px',background: isPro?'rgba(200,168,75,.08)':'rgba(10,22,40,.04)',borderRadius:10,border:`1px solid ${isPro?'rgba(200,168,75,.25)':'var(--border)'}` }}>
                {isPro ? (
                  <div style={{ fontSize:11,color:'var(--gold)',fontWeight:700 }}>⭐ Pro Plan Active</div>
                ) : (
                  <>
                    <div style={{ fontSize:11,fontWeight:700,color:'var(--navy)',marginBottom:3 }}>Free Plan</div>
                    <div style={{ fontSize:10,color:'var(--muted)',marginBottom:6 }}>Upgrade to unlock Buyer AI + more</div>
                    <button className="btn btn-gold btn-sm" style={{ width:'100%',fontSize:10,padding:'5px 8px' }}
                      onClick={()=>setSellerTab('discovery')}>
                      Upgrade ₹499/mo →
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="main">
              {sellerTab==="dashboard"&&<SellerDash user={user} onTab={setSellerTab} tab={sellerTab}/>}
              {sellerTab==="listings"&&<SellerListings user={user} onEnquiry={(id)=>{setEnqListingId(id);setSellerTab("enquiries");}}/>}
              {sellerTab==="create"&&<CreateListing user={user} onSaved={()=>{showToast("Listing published! Live to buyers.");setSellerTab("listings");}} onCancel={()=>setSellerTab("listings")}/>}
              {sellerTab==="discovery"&&<SellerBuyerDiscovery user={user} isPro={isPro} onUpgraded={()=>{ /* handled inline */ }}/>}
              {sellerTab==="enquiries"&&<SellerEnquiries user={user} listingId={enqListingId}
                onDealClosed={async (enq)=>{
                  await createNotif(enq.buyerId,'deal_closed','🎉 Deal Closed!',
                    `Your deal for "${enq.listingTitle}" has been accepted. Contact details shared.`,
                    '/enquiries',{sellerCompany:enq.sellerCompany});
                  showToast('Deal closed! Buyer notified ✅');
                }}
                onSellerResponded={async (enq,msg)=>{
                  await createNotif(enq.buyerId,'counter_offer','💬 Seller Responded',
                    `${enq.sellerCompany} responded to your offer on "${enq.listingTitle}"`,
                    '/enquiries');
                  showToast('Buyer notified of your response');
                }}
              />}
            </div>
          </div>
        </div>
      );
    }

    if (user.role === "buyer") {
      const BUYER_TABS = [
        {k:"browse",ico:"🔍",lbl:"Browse"},
        {k:"enquiries",ico:"💬",lbl:"My Enquiries"},
        {k:"watchlist",ico:"❤️",lbl:"Watchlist"},
        {k:"alerts",ico:"🔔",lbl:"Alerts"},
      ];
      return (
        <div><style>{CSS}</style>
          {toast&&<Toast msg={toast.msg} type={toast.type}/>}
          <Topbar />
          <div className="layout">
            <div className="sidebar">
              <div style={{ padding:"12px 10px 8px", borderBottom:"1px solid var(--border)", marginBottom:8 }}>
                <div style={{ fontWeight:700, fontSize:13 }}>{user.name}</div>
                <div style={{ fontSize:11, color:"var(--muted)" }}>{user.company}</div>
                <span className={`pill ${user.kycStatus==="approved"?"pill-green":"pill-amber"}`} style={{ marginTop:5, display:"inline-block" }}>{user.kycStatus==="approved"?"✅ Verified":"⏳ Pending"}</span>
              </div>
              <div className="sb-sec">Menu</div>
              {BUYER_TABS.map(t=>(
                <div key={t.k} className={`sb-item ${buyerTab===t.k&&!viewingListing?"act":""}`} onClick={()=>{setBuyerTab(t.k);setViewingListing(null);}}>
                  <span>{t.ico}</span><span>{t.lbl}</span>
                </div>
              ))}
            </div>
            <div className="main">
              {(!viewingListing&&buyerTab==="browse")&&<BuyerBrowse user={user} onViewListing={(id)=>setViewingListing(id)}/>}
              {viewingListing&&<ListingDetail user={user} listingId={viewingListing} onBack={()=>setViewingListing(null)} onOfferSent={async(data)=>{
                  showToast("Offer sent! Seller will respond soon.");
                  if(data?.sellerId){
                    await createNotif(data.sellerId,'new_offer','💬 New Offer Received',
                      `${data.buyerCompany} made an offer of ₹${data.offerPrice} on "${data.listingTitle}"`,
                      '/enquiries',{buyerCompany:data.buyerCompany});
                  }
                }}/>}
              {(!viewingListing&&buyerTab==="enquiries")&&<BuyerEnquiries user={user}/>}
              {(!viewingListing&&buyerTab==="watchlist")&&<BuyerWatchlist user={user} onViewListing={(id)=>setViewingListing(id)}/>}
              {(!viewingListing&&buyerTab==="alerts")&&(
                <div className="fu">
                  <div className="eye">Price Alerts</div>
                  <div className="h1" style={{ marginBottom:18 }}>Saved Searches</div>
                  {[{q:"MS HR Steel Coil IS2062",f:"Punjab · >10 MT",hits:2},{q:"SKF Bearings",f:"Any location",hits:1},{q:"ABB / Siemens Motor",f:"North India · 5-50kW",hits:0}].map((a,i)=>(
                    <div className="card" key={i} style={{ marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div><div className="h3">{a.q}</div><div style={{ fontSize:12, color:"var(--muted)" }}>{a.f}</div>{a.hits>0&&<div style={{ fontSize:12, color:"var(--green)", fontWeight:600, marginTop:3 }}>🔔 {a.hits} new {a.hits===1?"match":"matches"}</div>}</div>
                      <div style={{ display:"flex", gap:8 }}>
                        <span className="pill pill-green">Active</span>
                        <button className="btn btn-out btn-sm">Pause</button>
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-gold btn-sm" style={{ marginTop:8 }}>+ New Alert</button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  }

  return <div><style>{CSS}</style><div style={{ padding:40, textAlign:"center" }}>Loading Surpliq...</div></div>;
}
